/**
 * Code Generator - Converts visual flow nodes to target language code
 * Supports: C, C++, Java, JavaScript, Python, Pseudocode
 */

// ADO #287 Phase 1 — Output template parser + per-placeholder format
// spec ({var:.2f}). Single source of truth for both the validator and
// the C codegen branch in buildOutputStatement.
import {
  parseOutputTemplate,
  buildCFormatFromSpec,
  hasAnyExplicitSpec,
} from './outputTemplate.js';
// ADO #307 — Java curated built-in classes catalog. The package map is
// the single source of truth for auto-import: any class-instance
// resource whose class name is in here triggers the right `import …;`
// line at the top of the generated Java file.
import { resolveJavaImportForClass } from '@/config/javaBuiltinClasses';
// ADO #313 — Java built-in exception registry for the throws-clause auto-import path.
import { importFqcnForBuiltinException } from '@/config/javaBuiltinExceptions';
// ADO #400 — synthetic argc/argv resources so codegen resolves their types
// (e.g. argv[i] → %s) when the entry flow opts into command-line args.
import { withCommandLineArgs } from './commandLineArgs';
import { ipoComment } from './ipo';

// ============================================================================
// ADO #248 — Unit-Emission Contract (Phase 1: typedefs + stubs)
// ----------------------------------------------------------------------------
// Typedefs below formalise the per-unit emission boundary that will replace
// the monolithic generateProgramCode walker in phases 2-5. The runtime stubs
// live at the bottom of this file so the scaffolding is visible in one place
// without disturbing the existing 4000-line body in between.
// ============================================================================

/**
 * @typedef {Object} UnitFragment
 * @property {string}    source          Emitted source for this unit.
 * @property {string[]=} headerIncludes  C/C++ `#include` names this unit needs.
 * @property {string[]=} imports         Java/Python import statements this unit needs.
 * @property {string[]=} forwardDecls    Prototypes / forward decls this unit would need
 *                                        if assembled into a context that requires them.
 *                                        Assembler decides whether to emit (scopeMode-driven).
 */

/**
 * @typedef {Object} UnitEmitContext
 * @property {string} language         'c' | 'cpp' | 'java' | 'python' | 'javascript' | 'pseudocode'.
 * @property {string} fileClassType    'class' | 'record' | 'interface' | 'enum' | 'header' | 'freeFunctions'.
 * @property {string} sessionName      The enclosing session's `name` (e.g. 'Box', 'hello').
 * @property {Object} contractSnapshot Read-only snapshot of the session's Contract Flow
 *                                      (fields, function_decl nodes). emitUnit must NOT read
 *                                      sibling-unit bodies — that coupling is what ADO #248 removes.
 */

/**
 * @typedef {Object} FileSource
 * @property {string} filename
 * @property {string} source
 */


// Module-level: function name → return type map (populated by program generators)
// Used by buildOutputStatement to infer format specifiers for function call expressions
let _functionReturnTypes = {};

// ADO #308 — Stash the enclosing flow's flowType while codegen walks its
// nodes. The subflow case reads this to decide whether to emit `this.method()`
// (instance / constructor context) or bare `method()` (static / main / non-
// Java context) for `receiverKind === 'none'` calls. Set by each
// top-level generateNodeCode caller before iterating that flow's nodes.
let _currentFlowType = null;
// ADO #368 follow-up — the enclosing flow's name and declared return type, so a
// Return node can emit `RETURN:<fn>:<value>`. debugTraceParser has always
// expected that shape ("Subflow return: RETURN:fnName:var=value") and codegen
// has always emitted a bare `RETURN::` -- an empty payload to a parser written
// for a full one. The memory model therefore showed the callee's parameter as
// `??` and the returned value as nothing, so a student stepping a subflow call
// could see THAT it happened and never what went in or came back.
let _currentFlowName = null;
let _currentReturnType = null;
// ADO #441 — true while generating a Python instance method / constructor body.
// Python needs explicit `self.` on every class-attribute reference (Java gets it
// implicitly via `this`). Set in generateFunctionCode per flow; _pyMethodParams
// holds the method's parameter names (params SHADOW attributes of the same name).
let _pyInClassMethod = false;
let _pyMethodParams = new Set();

// Prefix `self.` when we KNOW the reference is the class attribute — i.e. the
// student picked the classField resource (by id), not a param/local of the same
// name. This is the precise path for assignment target/source: in
// `self.name = name`, the TARGET picked the field (→ self.name) while the SOURCE
// picked the parameter (→ bare name).
function pySelfRefByResource(ref, resource) {
  if (!_pyInClassMethod || ref == null) return ref;
  const s = String(ref);
  if (s.startsWith('self.')) return ref;
  return (resource && resource.location === 'classField') ? `self.${s}` : ref;
}

// Name-based path for sites with no resource id (e.g. an output template `{name}`):
// prefix when the base name is a class attribute AND is NOT shadowed by one of
// the method's parameters. In `speak(self)` (no param `name`), `{name}` → the
// attribute (`{self.name}`); inside `__init__(self, name)`, `{name}` stays the
// parameter.
function pySelfRef(ref, resources) {
  if (!_pyInClassMethod || ref == null) return ref;
  const s = String(ref);
  if (s.startsWith('self.')) return ref;
  const base = s.split(/[.[(]/)[0].trim();
  if (!base || base === 'self' || _pyMethodParams.has(base)) return ref;
  const r = (resources || []).find((x) => x && x.name === base);
  if (r && r.location === 'classField') return `self.${s}`;
  return ref;
}
// ADO #392 — all flows in the program being generated, so a subflow call can
// order its arguments by the TARGET flow's parameter order (not by the
// inputMappings object's key order, which need not match).
let _allFlowsForCodegen = [];

// C format specifiers based on type
const C_FORMAT_SPECIFIERS = {
  'int': '%d',
  'integer': '%d',
  'float': '%f',
  'double': '%lf',
  'string': '%s',
  'char': '%c',
  'char*': '%s',
  'char[]': '%s',  // char array (string)
  'boolean': '%d',
  'bool': '%d',
  'long': '%ld',
  'short': '%hd',
  // ADO #361 — modifiers + C99 fixed-width (LP64 / Judge0 gcc). Smaller-than-int
  // types are promoted to int in printf varargs, so %d/%u are correct there.
  'unsigned': '%u',
  'unsigned int': '%u',
  'unsigned short': '%u',
  'unsigned long': '%lu',
  'long long': '%lld',
  'unsigned long long': '%llu',
  'long double': '%Lf',
  'signed char': '%d',
  'unsigned char': '%u',
  'int8_t': '%d',
  'int16_t': '%d',
  'int32_t': '%d',
  'int64_t': '%ld',
  'uint8_t': '%u',
  'uint16_t': '%u',
  'uint32_t': '%u',
  'uint64_t': '%lu',
  'size_t': '%zu',
};

// ADO #361 — C type spellings (modifiers + C99 fixed-width) that mapToCType
// should emit verbatim rather than collapsing to 'int'.
const C_PASSTHROUGH_TYPES = new Set([
  'short', 'long', 'unsigned', 'unsigned int', 'unsigned short', 'unsigned long',
  'long long', 'unsigned long long', 'long double', 'signed char', 'unsigned char',
  'int8_t', 'int16_t', 'int32_t', 'int64_t',
  'uint8_t', 'uint16_t', 'uint32_t', 'uint64_t', 'size_t',
]);

// Get C format specifier for a type
function getCFormatSpecifier(type) {
  const t = String(type ?? '').trim().toLowerCase();
  // A POINTER MUST NOT FALL THROUGH TO `%d`. It is not a cosmetic choice: passing
  // a pointer where printf expects an int is undefined behaviour. On x86-64 it
  // happens to truncate to something plausible — Ram's Debug panel showed a file
  // stream as `TRUE:1431671392 != NULL` — but nothing guarantees that, and it
  // reads as a random number to a learner who met addresses as `0x…` in Pointers.
  //
  // `%p` also needs its argument cast to `void *`; see `cFormatArg` below, which
  // every caller uses so the cast cannot be forgotten at one of them.
  // ORDER MATTERS. The explicit map wins first: `char*` is a STRING to printf and
  // must stay `%s`, even though it ends in a star. A first pass checked the star
  // before the map and turned `printf("%s\n", argv[0])` into `%p` — caught by
  // ADO #400's own test, which is the only reason it did not ship.
  const mapped = C_FORMAT_SPECIFIERS[t];
  if (mapped) return mapped;
  if (/\*\s*$/.test(t) || t === 'pointer') return '%p';
  return '%d';
}

// The argument to pair with getCFormatSpecifier's result. Only `%p` needs
// anything, and it needs it every time.
function cFormatArg(expr, spec) {
  return spec === '%p' ? `(void *)(${expr})` : expr;
}

// ADO #309 — Java format specifiers. java.util.Formatter accepts a different
// alphabet from C's printf: no `%lf` / `%ld` (those throw at runtime), and
// there's a real `%b` for boolean instead of C's int-coerced 0/1.
// Default fallback is `%s` (safe — Formatter accepts any Object via toString),
// not `%d` — `%d` crashes on anything non-integer at runtime.
const JAVA_FORMAT_SPECIFIERS = {
  'int': '%d',
  'integer': '%d',
  'long': '%d',
  'short': '%d',
  'byte': '%d',
  'float': '%f',
  'double': '%f',
  'boolean': '%b',
  'bool': '%b',
  'char': '%c',
  'character': '%c',
  'string': '%s',
};
function getJavaFormatSpecifier(type) {
  return JAVA_FORMAT_SPECIFIERS[type?.toLowerCase()] || '%s';
}

// Get C scanf format specifier (different for double)
function getCScanfSpecifier(type) {
  const specifiers = {
    'int': '%d',
    'integer': '%d',
    'float': '%f',
    'double': '%lf',
    'string': '%s',
    'char': '%c',
    'char[]': '%s',  // char array (string)
    'long': '%ld',
    'short': '%hd',
    // ADO #361 — scanf needs the EXACT-width specifier (no promotion).
    'unsigned': '%u',
    'unsigned int': '%u',
    'unsigned short': '%hu',
    'unsigned long': '%lu',
    'long long': '%lld',
    'unsigned long long': '%llu',
    'long double': '%Lf',
    'signed char': '%hhd',
    'unsigned char': '%hhu',
    'int8_t': '%hhd',
    'int16_t': '%hd',
    'int32_t': '%d',
    'int64_t': '%ld',
    'uint8_t': '%hhu',
    'uint16_t': '%hu',
    'uint32_t': '%u',
    'uint64_t': '%lu',
    'size_t': '%zu',
  };
  return specifiers[type?.toLowerCase()] || '%d';
}

// Language templates and syntax
export const LANGUAGE_CONFIG = {
  c: {
    name: 'C',
    extension: 'c',
    fileHeader: '', // Headers are now generated dynamically based on usage
    // `(void)`: in C before C23 an empty list means "unknown arguments".
    mainStart: 'int main(void) {\n',
    mainEnd: '    return 0;\n}\n',
    // Used when the body already ended in a return — see generateMainCode.
    mainEndAfterReturn: '}\n',
    indent: '    ',
    print: (text, endWithNewline = true) => `printf("${text}${endWithNewline ? '\\n' : ''}");`,
    printVar: (varName, type, endWithNewline = true) => `printf("${getCFormatSpecifier(type)}${endWithNewline ? '\\n' : ''}", ${varName});`,
    printString: (varName, endWithNewline = true) => `printf("%s${endWithNewline ? '\\n' : ''}", ${varName});`,
    input: (varName, type, isDeclared = false) => {
      // char arrays use %s (no & needed, array decays to pointer)
      const lt = (type || '').toLowerCase();
      if (lt === 'char[]' || lt === 'char*' || lt === 'string') {
        return `scanf("%s", ${varName});`;
      }
      // Input bugs (epic #581) — a bool is 1 byte, so scanf("%d", &flag)
      // wrote an int's 4 bytes into it: undefined behaviour. Read into an int
      // in its own block and assign. One line, so node-to-line highlighting
      // and the line map are unchanged. The temp is renamed if the target
      // expression already uses that identifier (e.g. `flags[in_value]`).
      if (lt === 'bool' || lt === 'boolean') {
        let tmp = 'in_value';
        while (new RegExp(`\\b${tmp}\\b`).test(varName)) tmp += '_';
        return `{ int ${tmp}; scanf("%d", &${tmp}); ${varName} = ${tmp}; }`;
      }
      // Input bugs (epic #581) — `%c` does not skip whitespace, so a char read
      // after any other read took the leftover newline instead of the letter.
      // The leading space skips it. (Whole-word char[] reads keep %s, above.)
      if (lt === 'char') {
        return `scanf(" %c", &${varName});`;
      }
      const specifier = getCScanfSpecifier(type);
      // All other types need & for scanf
      return `scanf("${specifier}", &${varName});`;
    },
    varDeclare: (name, type, value) => {
      const cType = mapToCType(type);
      return value !== undefined && value !== ''
        ? `${cType} ${name} = ${value};`
        : `${cType} ${name};`;
    },
    assignment: (name, value) => `${name} = ${value};`,
    ifStart: (condition) => `if (${condition}) {`,
    elseIfStart: (condition) => `} else if (${condition}) {`,
    elseStart: '} else {',
    ifEnd: '}',
    whileStart: (condition) => `while (${condition}) {`,
    whileEnd: '}',
    doWhileStart: 'do {',
    doWhileEnd: (condition) => `} while (${condition});`,
    forStart: (init, condition, increment) => `for (${init}; ${condition}; ${increment}) {`,
    forEnd: '}',
    comment: (text) => `// ${text}`,
    blockComment: (text) => `/* ${text} */`,
  },

  cpp: {
    name: 'C++',
    extension: 'cpp',
    fileHeader: '', // Headers are generated dynamically based on usage
    mainStart: 'int main() {\n',
    mainEnd: '    return 0;\n}\n',
    // Used when the body already ended in a return — see generateMainCode.
    mainEndAfterReturn: '}\n',
    indent: '    ',
    print: (text, endWithNewline = true) => `cout << "${text}"${endWithNewline ? ' << endl' : ''};`,
    printVar: (varName, type, endWithNewline = true) => `cout << ${varName}${endWithNewline ? ' << endl' : ''};`,
    printString: (varName, endWithNewline = true) => `cout << ${varName}${endWithNewline ? ' << endl' : ''};`,
    input: (varName, type, isDeclared = false) => {
      if (type === 'string' || type === 'char*' || type === 'char[]') {
        return `getline(cin, ${varName});`;
      }
      return `cin >> ${varName};`;
    },
    varDeclare: (name, type, value) => {
      const cppType = mapToCppType(type);
      return value !== undefined && value !== ''
        ? `${cppType} ${name} = ${value};`
        : `${cppType} ${name};`;
    },
    assignment: (name, value) => `${name} = ${value};`,
    ifStart: (condition) => `if (${condition}) {`,
    elseIfStart: (condition) => `} else if (${condition}) {`,
    elseStart: '} else {',
    ifEnd: '}',
    whileStart: (condition) => `while (${condition}) {`,
    whileEnd: '}',
    doWhileStart: 'do {',
    doWhileEnd: (condition) => `} while (${condition});`,
    forStart: (init, condition, increment) => `for (${init}; ${condition}; ${increment}) {`,
    forEnd: '}',
    comment: (text) => `// ${text}`,
    blockComment: (text) => `/* ${text} */`,
  },

  java: {
    name: 'Java',
    extension: 'java',
    // fileHeader: `public class Main` is the fallback. Callers should prefer
    // javaFileHeader(className, fileClassType, recordComponents) below so the
    // class name matches the .java file and record syntax is used when needed.
    fileHeader: 'import java.util.Scanner;\n\npublic class Main {\n',
    /**
     * ADO #231 — emit the Java file header (imports + class/record declaration).
     * Branches on fileClassType:
     *   'class'  → `public class <ClassName> {`
     *   'record' → `public record <ClassName>(Type c1, Type c2, ...) {`
     *
     * ADO #307 — extraImports (Set<string> of FQCNs) lets the caller inject
     * imports collected from class-instance resources (`java.util.ArrayList`,
     * `java.util.HashMap`, etc.). Always deduped against the hardcoded
     * `java.util.Scanner` line. Sorted alphabetically for stable output.
     *
     * @param {string}      className         file name minus `.java`
     * @param {string}      [fileClassType]   'class' | 'record' (default 'class')
     * @param {Array}       [recordComponents] [{ name, type }] — only used for records
     * @param {Set<string>} [extraImports]    additional FQCNs to import
     */
    javaFileHeader: (className, fileClassType, recordComponents, extraImports) => {
      const name = className || 'Main';
      const importLines = ['import java.util.Scanner;'];
      if (extraImports && extraImports.size > 0) {
        Array.from(extraImports)
          .filter((fqcn) => fqcn && fqcn !== 'java.util.Scanner')
          .sort()
          .forEach((fqcn) => importLines.push(`import ${fqcn};`));
      }
      const header = importLines.join('\n') + '\n\n';
      if (fileClassType === 'record') {
        const comps = (recordComponents || [])
          .map(c => `${mapToJavaType(c.type)} ${c.name}`)
          .join(', ');
        return `${header}public record ${name}(${comps}) {\n`;
      }
      return `${header}public class ${name} {\n`;
    },
    mainStart: '    public static void main(String[] args) {\n        Scanner scanner = new Scanner(System.in);\n',
    mainEnd: '        System.out.flush();\n        scanner.close();\n    }\n',  // flush stdout before exit (printf doesn't auto-flush)
    classEnd: '}\n',  // Separate class closing
    indent: '        ',
    methodIndent: '    ',  // Indent for method definitions inside class
    print: (text, endWithNewline = true) => `System.out.${endWithNewline ? 'println' : 'print'}("${text}");`,
    printVar: (varName, type, endWithNewline = true) => `System.out.${endWithNewline ? 'println' : 'print'}(${varName});`,
    printString: (varName, endWithNewline = true) => `System.out.${endWithNewline ? 'println' : 'print'}(${varName});`,
    input: (varName, type, isDeclared = false) => {
      // ADO #219 — char needs a chained call, not the normal scanner.method() template
      const lt = (type || '').toLowerCase();
      if (lt === 'char') {
        if (isDeclared) return `${varName} = scanner.next().charAt(0);`;
        return `char ${varName} = scanner.next().charAt(0);`;
      }
      const scannerMethod = getJavaScannerMethod(type);
      // If variable is already declared, just assign. Otherwise declare and assign.
      if (isDeclared) {
        return `${varName} = scanner.${scannerMethod}();`;
      } else {
        const javaType = mapToJavaType(type);
        return `${javaType} ${varName} = scanner.${scannerMethod}();`;
      }
    },
    varDeclare: (name, type, value) => {
      const javaType = mapToJavaType(type);
      return value !== undefined && value !== ''
        ? `${javaType} ${name} = ${value};`
        : `${javaType} ${name};`;
    },
    assignment: (name, value) => `${name} = ${value};`,
    ifStart: (condition) => `if (${condition}) {`,
    elseIfStart: (condition) => `} else if (${condition}) {`,
    elseStart: '} else {',
    ifEnd: '}',
    whileStart: (condition) => `while (${condition}) {`,
    whileEnd: '}',
    doWhileStart: 'do {',
    doWhileEnd: (condition) => `} while (${condition});`,
    forStart: (init, condition, increment) => `for (${init}; ${condition}; ${increment}) {`,
    forEnd: '}',
    comment: (text) => `// ${text}`,
    blockComment: (text) => `/* ${text} */`,
  },
  
  javascript: {
    name: 'JavaScript',
    extension: 'js',
    fileHeader: `// Generated JavaScript Code
const readline = require('readline');

// Setup stdin reading
const inputLines = [];
let inputIndex = 0;

function readLine() {
    return inputLines[inputIndex++] || '';
}

function readInt() {
    return parseInt(readLine());
}

function readFloat() {
    return parseFloat(readLine());
}

// Read all input first, then run main
const rl = readline.createInterface({ input: process.stdin });
rl.on('line', (line) => inputLines.push(line));
rl.on('close', () => main());

`,
    mainStart: 'function main() {\n',
    mainEnd: '}\n',
    indent: '    ',
    print: (text, endWithNewline = true) => endWithNewline ? `console.log("${text}");` : `process.stdout.write("${text}");`,
    printVar: (varName, type, endWithNewline = true) => endWithNewline ? `console.log(${varName});` : `process.stdout.write(String(${varName}));`,
    printString: (varName, endWithNewline = true) => endWithNewline ? `console.log(${varName});` : `process.stdout.write(${varName});`,
    input: (varName, type, isDeclared = false) => {
      const readFunc = type === 'int' || type === 'integer' ? 'readInt()' : 
                       type === 'float' || type === 'double' ? 'readFloat()' : 'readLine()';
      if (isDeclared) {
        return `${varName} = ${readFunc};`;
      } else {
        return `let ${varName} = ${readFunc};`;
      }
    },
    varDeclare: (name, type, value) => {
      return value !== undefined && value !== ''
        ? `let ${name} = ${value};`
        : `let ${name};`;
    },
    assignment: (name, value) => `${name} = ${value};`,
    ifStart: (condition) => `if (${condition}) {`,
    elseIfStart: (condition) => `} else if (${condition}) {`,
    elseStart: '} else {',
    ifEnd: '}',
    whileStart: (condition) => `while (${condition}) {`,
    whileEnd: '}',
    doWhileStart: 'do {',
    doWhileEnd: (condition) => `} while (${condition});`,
    forStart: (init, condition, increment) => `for (${init}; ${condition}; ${increment}) {`,
    forEnd: '}',
    comment: (text) => `// ${text}`,
    blockComment: (text) => `/* ${text} */`,
  },
  
  python: {
    name: 'Python',
    extension: 'py',
    fileHeader: '# Generated Python Code\n\n',
    mainStart: 'def main():\n',
    mainEnd: '\n\nif __name__ == "__main__":\n    main()\n',
    indent: '    ',
    print: (text, endWithNewline = true) => `print("${text}"${endWithNewline ? '' : ', end=""'})`,
    printVar: (varName, type, endWithNewline = true) => `print(${varName}${endWithNewline ? '' : ', end=""'})`,
    printString: (varName, endWithNewline = true) => `print(${varName}${endWithNewline ? '' : ', end=""'})`,
    input: (varName, type, isDeclared = false) => {
      // Python doesn't need declaration, so always just assign
      const converter = type === 'int' || type === 'integer' ? 'int' : 
                        type === 'float' || type === 'double' ? 'float' : '';
      return converter 
        ? `${varName} = ${converter}(input("Enter ${varName}: "))`
        : `${varName} = input("Enter ${varName}: ")`;
    },
    varDeclare: (name, type, value) => {
      return value !== undefined && value !== ''
        ? `${name} = ${value}`
        : `${name} = None`;
    },
    assignment: (name, value) => `${name} = ${value}`,
    ifStart: (condition) => `if ${condition}:`,
    elseIfStart: (condition) => `elif ${condition}:`,
    elseStart: 'else:',
    ifEnd: '', // Python uses indentation
    whileStart: (condition) => `while ${condition}:`,
    whileEnd: '',
    doWhileStart: 'while True:',
    doWhileEnd: (condition) => `    if not (${condition}):\n        break`,
    forStart: (init, condition, increment) => `# FOR loop: ${init}; ${condition}; ${increment}`,
    forEnd: '',
    comment: (text) => `# ${text}`,
    blockComment: (text) => `"""${text}"""`,
  },
  
  pseudocode: {
    name: 'Pseudocode',
    extension: 'txt',
    fileHeader: '// Pseudocode\n\n',
    mainStart: 'BEGIN PROGRAM\n',
    mainEnd: 'END PROGRAM\n',
    indent: '    ',
    print: (text) => `DISPLAY "${text}"`,
    printVar: (varName, type) => `DISPLAY ${varName}`,
    printString: (varName) => `DISPLAY ${varName}`,
    input: (varName, type) => `READ ${varName}`,
    varDeclare: (name, type, value) => {
      return value !== undefined && value !== ''
        ? `DECLARE ${name} AS ${type || 'variable'} = ${value}`
        : `DECLARE ${name} AS ${type || 'variable'}`;
    },
    assignment: (name, value) => `SET ${name} = ${value}`,
    ifStart: (condition) => `IF ${condition} THEN`,
    elseIfStart: (condition) => `ELSE IF ${condition} THEN`,
    elseStart: 'ELSE',
    ifEnd: 'END IF',
    whileStart: (condition) => `WHILE ${condition} DO`,
    whileEnd: 'END WHILE',
    doWhileStart: 'DO',
    doWhileEnd: (condition) => `WHILE ${condition}`,
    forStart: (init, condition, increment) => `FOR ${init} TO ${condition} STEP ${increment} DO`,
    forEnd: 'END FOR',
    comment: (text) => `// ${text}`,
    blockComment: (text) => `/* ${text} */`,
  }
};

// C Header requirements mapping - which headers are needed for which functions/features
const C_HEADER_REQUIREMENTS = {
  '<stdio.h>': {
    functions: ['printf', 'scanf', 'fprintf', 'fscanf', 'sprintf', 'sscanf', 'fopen', 'fclose', 
                'fread', 'fwrite', 'fgets', 'fputs', 'getchar', 'putchar', 'gets', 'puts',
                'fseek', 'ftell', 'rewind', 'feof', 'ferror', 'clearerr', 'remove', 'rename', 'perror',
                'snprintf', 'vprintf', 'vfprintf', 'vsprintf', 'vsnprintf', 'fgetc', 'fputc', 'getc', 'putc',
                'ungetc', 'fflush', 'freopen', 'tmpfile', 'setvbuf'],
    features: ['input', 'output'],  // Flow builder features that need this header
  },
  '<stdlib.h>': {
    functions: ['malloc', 'calloc', 'realloc', 'free', 'atoi', 'atol', 'atof', 'strtol', 'strtoul', 
                'strtod', 'rand', 'srand', 'abs', 'labs', 'div', 'ldiv', 'exit', 'abort', 
                'atexit', 'system', 'getenv', 'qsort', 'bsearch', 'max', 'min'],
    features: [],
  },
  '<string.h>': {
    functions: ['strlen', 'strcpy', 'strncpy', 'strcat', 'strncat', 'strcmp', 'strncmp',
                'strchr', 'strrchr', 'strstr', 'strtok', 'strcspn', 'strspn', 'memcpy', 'memmove', 'memset', 'memcmp', 'memchr'],
    features: [],
  },
  '<math.h>': {
    functions: ['sin', 'cos', 'tan', 'asin', 'acos', 'atan', 'atan2', 'sinh', 'cosh', 'tanh',
                'exp', 'log', 'log10', 'log2', 'pow', 'sqrt', 'cbrt', 'ceil', 'floor', 'round',
                'trunc', 'fabs', 'fmod', 'fmax', 'fmin', 'hypot', 'isnan', 'isinf'],
    features: [],
  },
  '<ctype.h>': {
    functions: ['isalpha', 'isdigit', 'isalnum', 'isspace', 'isupper', 'islower', 'ispunct',
                'isprint', 'iscntrl', 'isxdigit', 'toupper', 'tolower'],
    features: [],
  },
  '<time.h>': {
    functions: ['time', 'clock', 'difftime', 'mktime', 'strftime', 'localtime', 'gmtime', 'asctime', 'ctime'],
    features: [],
  },
  '<stdbool.h>': {
    functions: [],
    features: ['boolean'],  // Include when boolean type is used
  },
  // ADO #361 — pulled in when a C99 fixed-width / size type appears in the code.
  '<stdint.h>': {
    functions: [],
    features: [],
    typeRegex: /\bu?int(8|16|32|64)_t\b/,
  },
  '<stddef.h>': {
    functions: [],
    features: [],
    typeRegex: /\bsize_t\b/,
  },
  // ADO #449 — pulled in for variadic functions: the va_* macros live here, and
  // a variadic function's `va_list` declaration + va_start/va_arg/va_end calls
  // all imply it. Detect by the va_* call names and the `va_list` type.
  '<stdarg.h>': {
    functions: ['va_start', 'va_arg', 'va_end', 'va_copy'],
    features: [],
    typeRegex: /\bva_list\b/,
  },
};

/** The header a C library function lives in, as its name: cHeaderOf('sqrt') → 'math.h'; null if none. */
export function cHeaderOf(fn) {
  const name = String(fn || '');
  const hit = Object.entries(C_HEADER_REQUIREMENTS).find(([, r]) => (r.functions || []).includes(name));
  return hit ? hit[0].replace(/^<|>$/g, '') : null;
}

/**
 * Recursively collect all nodes from a flow (including nested nodes in Decision/Loop branches)
 */
function collectAllNodes(nodes) {
  const result = [];
  
  for (const node of nodes) {
    result.push(node);
    
    // Collect nodes from Decision / Switch branches (both use `outcomes`).
    if ((node.type === 'decision' || node.type === 'switch') && node.outcomes) {
      for (const outcome of node.outcomes) {
        if (outcome.nodes && outcome.nodes.length > 0) {
          result.push(...collectAllNodes(outcome.nodes));
        }
      }
    }
    
    // Collect nodes from Loop branches
    if (node.type === 'loop') {
      if (node.loopBody && node.loopBody.length > 0) {
        result.push(...collectAllNodes(node.loopBody));
      }
      if (node.afterLast && node.afterLast.length > 0) {
        result.push(...collectAllNodes(node.afterLast));
      }
    }

    // Collect nodes from Try-Catch branches
    if (node.type === 'try_catch' && node.branches) {
      for (const branch of node.branches) {
        if (branch.nodes && branch.nodes.length > 0) {
          result.push(...collectAllNodes(branch.nodes));
        }
      }
    }
  }

  return result;
}

// ─── Shared helpers (used by decision + loop node handlers) ──────────────────

/**
 * Maps logical operator names to code symbols.
 * Defined once here instead of being re-declared inside each node handler.
 */
// ADO #582 — what a decision outcome / loop with NO condition emits. Never an
// invented condition (`true`, `x > 0`, `i < 10`): a marker the student can read
// in the code view. It does not compile, and it never reaches a compiler —
// validation flags the node and Run / Debug / tests are blocked until it has one.
export const missingConditionPlaceholder = (langName) => (
  ['Python', 'Pseudocode'].includes(langName) ? '<no condition yet>' : '/* no condition yet */'
);

const OPERATOR_MAP = {
  'equals': '==',
  'notEquals': '!=',
  'greaterThan': '>',
  'greaterOrEqual': '>=',
  'lessThan': '<',
  'lessOrEqual': '<=',
  'contains': '.includes',
  'startsWith': '.startsWith',
  'isNull': '== null',
  'isNotNull': '!= null',
  'instanceof': 'instanceof', // ADO #221 — Java only
};

// A guided For condition, expressed as Python `range(...)` arguments — or null
// when it cannot be, in which case the caller emits a `while` instead.
//
// Python's `for` iterates a sequence. `range` can say "counter from A, stopping
// before B, in steps of C" and nothing else, so only a single condition of the
// shape `counter <op> bound` maps onto it. `i != 7`, two conditions joined by
// AND, or a condition on some other variable do not, and pretending otherwise
// would make the same flow print different things in Python and in C — which is
// the one promise this course cannot break.
//
// range's stop is EXCLUSIVE and the builder's condition is written by the
// student, so `<=` and `>=` need the bound moved by one. A numeric bound is
// folded there and then (`range(1, 16)`); a named one is emitted as `n + 1`,
// which reads correctly and stays correct when n changes.
function pythonRangeArgs(node, counter, start, step, resources) {
  const conds = Array.isArray(node.conditions) ? node.conditions.filter(
    (c) => c && ((c.resource && c.resource.trim()) || (c.resourceFullPath && c.resourceFullPath.trim()))
  ) : [];
  if (node.rawExpression && node.rawExpression.trim() !== '') return null;

  // ADO #582 — no condition, no range. (A legacy `forConfig.end` is not read:
  // the editor seeded it as '10' and no longer shows it, so honouring it ran a
  // hidden `i < 10` the student never wrote.)
  if (conds.length === 0) return null;

  if (conds.length !== 1) return null;

  const c = conds[0];
  const name = String(c.resourceFullPath || c.resource).trim();
  if (name !== String(counter).trim()) return null;          // must count the counter

  const bound = String(c.value == null ? '' : c.value).trim();
  if (bound === '') return null;
  if (needsStringQuotes(bound, resources)) return null;       // range is numbers only

  const stepNum = Number(String(step).trim());
  if (!Number.isFinite(stepNum) || stepNum === 0) return null;
  const goingDown = stepNum < 0;

  // Which way the condition points has to agree with which way the step moves,
  // or the loop is either infinite or dead — neither is expressible as a range.
  const shift = { lessThan: 0, lessOrEqual: 1, greaterThan: 0, greaterOrEqual: -1 }[c.operator];
  if (shift === undefined) return null;
  const pointsDown = c.operator === 'greaterThan' || c.operator === 'greaterOrEqual';
  if (pointsDown !== goingDown) return null;

  let stop = bound;
  if (shift !== 0) {
    const n = Number(bound);
    stop = Number.isFinite(n) ? String(n + shift) : `${bound} ${shift > 0 ? '+' : '-'} 1`;
  }

  const startTrim = String(start).trim();
  if (stepNum === 1 && (startTrim === '0' || startTrim === '')) return `${stop}`;
  if (stepNum === 1) return `${startTrim}, ${stop}`;
  return `${startTrim}, ${stop}, ${String(step).trim()}`;
}

// ADO #444 — Python null checks use `is None` / `is not None`, never `== null`
// (invalid Python) or even `== None` (valid but PEP8-discouraged). Covers the
// isNull/isNotNull operators AND an equals/notEquals against a None/null value.
// Returns the full condition string for Python, or null to fall through.
function pyNullCondition(resourceName, operator, value) {
  if (operator === 'isNull') return `${resourceName} is None`;
  if (operator === 'isNotNull') return `${resourceName} is not None`;
  const v = String(value == null ? '' : value).trim();
  if ((operator === 'equals' || operator === 'notEquals') && (v === 'None' || v === 'null')) {
    return `${resourceName} ${operator === 'equals' ? 'is' : 'is not'} None`;
  }
  return null;
}

/**
 * Returns true if a condition value is a string literal that needs quoting.
 * Accepts resources array so it can distinguish variable references from literals.
 */
export function needsStringQuotes(val, resources = []) {
  if (!val || val === '') return false;
  if ((val.startsWith('"') && val.endsWith('"')) ||
      (val.startsWith("'") && val.endsWith("'"))) return false;
  if (!isNaN(val) && !isNaN(parseFloat(val))) return false;
  if (val === 'true' || val === 'false' || val === 'True' || val === 'False') return false;
  // Null constants across tracks. NULL/nullptr (C/C++) were missing, so a
  // pointer check like `newNode == NULL` was quoted to "NULL" and emitted as
  // strcmp(newNode, "NULL") — a type error on pointers. ADO #354.
  if (val === 'null' || val === 'None' || val === 'nil' || val === 'NULL' || val === 'nullptr') return false;
  // Standard-library integer constants. Same fault as NULL above, one header
  // over: `ch == EOF` was quoted into strcmp(ch, "EOF") — a type error against
  // an int — and `fseek(fp, n, SEEK_SET)` compared the same way. These are the
  // whole vocabulary a File I/O flow needs, and none of them is ever a string.
  // Found probing ps-m11 before authoring it.
  if (val === 'EOF' || val === 'SEEK_SET' || val === 'SEEK_CUR' || val === 'SEEK_END') return false;
  if (/^[a-zA-Z_][a-zA-Z0-9_]*(\.[a-zA-Z_][a-zA-Z0-9_]*)*$/.test(val)) {
    const isResourceRef = resources.some(r => r.name === val || val.startsWith(r.name + '.'));
    if (isResourceRef) return false;
    // ADO #409 — enum constants (e.g. TUESDAY) are bare integer identifiers, not
    // strings. They live inside an enum_type's enumMembers (not as their own
    // resources), so check there too — otherwise `today == TUESDAY` would be
    // quoted into `strcmp(today, "TUESDAY")`, a type error against an enum.
    const isEnumMember = resources.some(r => {
      const rt = (r.resourceType || r.type || '').toLowerCase();
      return rt === 'enum_type'
        && Array.isArray(r.enumMembers)
        && r.enumMembers.some(m => (m.name || '').trim() === val);
    });
    if (isEnumMember) return false;
  }
  // An INDEXED reference is a reference too: `word[j]`, `marks[row][col]`.
  //
  // The identifier test above requires a bare name, so anything with brackets
  // fell straight through to "this is a string literal" and was quoted. That
  // turned `word[i] != word[j]` into strcmp(word[i], "word[j]") — comparing a
  // character against the literal text `word[j]`, which does not compile in C
  // and is nonsense everywhere else.
  //
  // It is why comparing two characters of a piece of text was impossible, and
  // it is the same fault that quoted an unresolvable name into
  // strcmp(col, "row") on a two-dimensional walk. Strip the indexes and ask
  // whether the base is a resource, which is the question the bare-name branch
  // was already asking.
  if (/^[a-zA-Z_][a-zA-Z0-9_]*(\[[^\]]*\])+$/.test(val)) {
    const base = val.replace(/\[[^\]]*\]/g, '');
    if (resources.some((r) => r.name === base)) return false;
  }
  // A MEMBER OF AN ARRAY ELEMENT is a reference too: `table[j].total`,
  // `table[j + 1].attendance`.
  //
  // This is the third time round the same fault. The dotted branch above wants a
  // bare name with dots (`p.target`); the indexed branch wants a name with
  // brackets (`word[j]`). A path with BOTH matches neither, so it fell through to
  // "this is a string literal" and was quoted — turning
  //
  //     table[j].total > table[j + 1].total
  //
  // into strcmp(table[j].total, "table[j + 1].total"): a string compare on two
  // ints, against the literal text of the right-hand side. It does not compile.
  //
  // That comparison is every sort there is — ORDER BY over a table of records —
  // so while nothing shipped hits it (no item compared two record members until
  // now), it made sorting a table of structs unauthorable.
  //
  // Strip the subscripts, take the base before the first dot, and ask the same
  // question the other two branches ask. Index EXPRESSIONS (`j + 1`) come out
  // with the brackets, so they need no special handling.
  if (/^[a-zA-Z_][a-zA-Z0-9_]*(\[[^\]]*\]|\.[a-zA-Z_][a-zA-Z0-9_]*)+$/.test(val)) {
    const base = val.replace(/\[[^\]]*\]/g, '').split('.')[0];
    if (resources.some((r) => r.name === base)) return false;
  }
  return true;
}

/**
 * Builds a C-style strcmp() expression for string comparisons.
 * Returns null when langName is not 'C' or the operator has no strcmp equivalent.
 */
function buildStrcmpCondition(langName, resourceName, operator, value) {
  if (langName !== 'C') return null;
  const strcmpOps = {
    'equals':         `strcmp(${resourceName}, ${value}) == 0`,
    'notEquals':      `strcmp(${resourceName}, ${value}) != 0`,
    'greaterThan':    `strcmp(${resourceName}, ${value}) > 0`,
    'lessThan':       `strcmp(${resourceName}, ${value}) < 0`,
    'greaterOrEqual': `strcmp(${resourceName}, ${value}) >= 0`,
    'lessOrEqual':    `strcmp(${resourceName}, ${value}) <= 0`,
  };
  return strcmpOps[operator] || null;
}

/**
 * ADO #356 — In a RAW (free-text) C/C++ condition, `cmd == "SELECT"` compiles as
 * a pointer comparison (a silent bug), unlike the guided builder which emits
 * strcmp(). Rewrite `<lhs> == "literal"` / `!= "literal"` to the strcmp() form so
 * raw conditions comparing against a string literal behave as expected. Only
 * triggers when the RHS is a string literal (the unambiguous string-compare
 * case); numeric/char comparisons are untouched. <string.h> is auto-included by
 * generateCHeader once `strcmp(` appears.
 */
function convertRawStringEquality(expr, langName) {
  if ((langName !== 'C' && langName !== 'C++') || typeof expr !== 'string' || expr.indexOf('"') === -1) {
    return expr;
  }
  // lhs = identifier with optional .member / ->member / [index] chains.
  const re = /([A-Za-z_]\w*(?:\s*(?:\.\w+|->\w+|\[[^\]]*\]))*)\s*(==|!=)\s*("(?:[^"\\]|\\.)*")/g;
  return expr.replace(re, (_m, lhs, op, lit) => `strcmp(${lhs.trim()}, ${lit}) ${op} 0`);
}

/**
 * One guided condition row as the language writes it — the text a Decision
 * outcome's or a Loop's `conditions[]` row generates. ONE function for both, so
 * the Decision and Repeat v2 editors can show a guided row as exactly the
 * condition codegen emits for it (guidedConditionC below).
 *
 * `rawStrings` (C only): a text equality is written `name == "admin"` instead of
 * strcmp(name, "admin") == 0 — but only when the raw-condition path
 * (convertRawStringEquality) turns it back into that same strcmp, so the
 * generated C does not change when an editor stores it as a raw condition.
 */
function guidedConditionPart(cond, langName, resources, resolveFormulas, { rawStrings = false } = {}) {
  const resourceName = resolveFormulas(cond.resourceFullPath || cond.resource);
  let value = resolveFormulas(cond.value || '0');
  // ADO #221 — instanceof: value is a type name, don't quote as a string
  const isInstanceOf = cond.operator === 'instanceof';
  const isStringComparison = !isInstanceOf && needsStringQuotes(value, resources);
  if (isStringComparison) value = `"${value}"`;
  // ADO #444 — Python `is None` for null checks (incl. `== None`).
  if (langName === 'Python') {
    const pyNull = pyNullCondition(resourceName, cond.operator, value);
    if (pyNull) return pyNull;
  }
  const op = OPERATOR_MAP[cond.operator] || cond.operator || '==';
  if (cond.operator === 'isNull' || cond.operator === 'isNotNull') {
    return `${resourceName} ${op}`;
  }
  if (isStringComparison) {
    const strcmpExpr = buildStrcmpCondition(langName, resourceName, cond.operator, value);
    if (strcmpExpr) {
      if (rawStrings && (cond.operator === 'equals' || cond.operator === 'notEquals')) {
        const plain = `${resourceName} ${op} ${value}`;
        if (convertRawStringEquality(plain, langName) === strcmpExpr) return plain;
      }
      return strcmpExpr;
    }
    // ADO #217 — Java: .equals() for String comparisons
    const resObj = resources.find(r => r.name === (cond.resourceFullPath || cond.resource));
    const resType = resObj?.dataType || '';
    const javaEqualsExpr = buildJavaEqualsCondition(langName, resourceName, cond.operator, value, resType);
    if (javaEqualsExpr) return javaEqualsExpr;
  }
  return `${resourceName} ${op} ${value}`;
}

/**
 * The C condition a holder's guided rows generate — a Decision outcome
 * (`kind: 'decision'`) or a Loop node (`kind: 'loop'`) — joined exactly as the
 * code generator joins them, or '' when it has no usable row. Formula names
 * are left as written: codegen resolves them in a raw condition too. Text
 * equalities read `name == "admin"` (see guidedConditionPart `rawStrings`).
 *
 * Decision and Repeat v2 (Ram, 2026-10-23) show a guided path with this and,
 * when the student edits it, store the edited text as `rawExpression`.
 */
export function guidedConditionC(holder, resources = [], { kind = 'decision' } = {}) {
  const conds = Array.isArray(holder && holder.conditions) ? holder.conditions : [];
  const valid = conds.filter((c) => c && ((c.resource && c.resource.trim() !== '') || (c.resourceFullPath && c.resourceFullPath.trim() !== '')));
  if (!valid.length) return '';
  const same = (x) => x;
  const parts = valid.map((c) => guidedConditionPart(c, 'C', resources || [], same, { rawStrings: true }));
  if (kind === 'loop') {
    if (holder.conditionLogic === 'CUSTOM' && holder.customLogicExpression) {
      return holder.customLogicExpression
        .replace(/(\d+)/g, (match) => parts[parseInt(match, 10) - 1] || 'true')
        .replace(/\bAND\b/gi, '&&')
        .replace(/\bOR\b/gi, '||');
    }
    return parts.join(holder.conditionLogic === 'OR' ? ' || ' : ' && ');
  }
  if (holder.conditionLogic === 'CUSTOM' && holder.customLogicExpression && holder.customLogicExpression.trim()) {
    // eslint-disable-next-line global-require
    const { substituteCustomLogic } = require('@/components/visual-flow-builder/customConditionLogic');
    return substituteCustomLogic(holder.customLogicExpression, parts, 'c');
  }
  return parts.join(holder.conditionLogic === 'OR' ? ' || ' : ' && ');
}

/**
 * ADO #217 — Java String comparison using .equals() instead of ==.
 * Mirrors buildStrcmpCondition for C's strcmp().
 * Returns null for non-Java or non-String types so the caller falls
 * through to the default == / != operators.
 */
function buildJavaEqualsCondition(langName, resourceName, operator, value, resourceType) {
  if (langName !== 'Java') return null;
  // Only apply to String types
  const rt = (resourceType || '').toLowerCase();
  if (rt !== 'string' && rt !== 'char[]') return null;

  if (operator === 'equals') {
    return `${resourceName}.equals(${value})`;
  }
  if (operator === 'notEquals') {
    return `!${resourceName}.equals(${value})`;
  }
  // Other operators (>, <, etc.) don't apply to .equals() — use compareTo if needed
  if (operator === 'greaterThan' || operator === 'lessThan' ||
      operator === 'greaterOrEqual' || operator === 'lessOrEqual') {
    const cmpOps = {
      'greaterThan': '> 0',
      'lessThan': '< 0',
      'greaterOrEqual': '>= 0',
      'lessOrEqual': '<= 0',
    };
    return `${resourceName}.compareTo(${value}) ${cmpOps[operator]}`;
  }
  return null;
}

/**
 * Emits one indented declaration, correctly handling multi-line declarations (e.g. structs).
 * Returns the formatted code string with a trailing newline.
 */
function indentDeclaration(declaration, indent) {
  if (!declaration || !declaration.trim()) return '';
  if (declaration.includes('\n')) {
    let out = '';
    declaration.split('\n').forEach(line => { if (line.trim()) out += `${indent}${line}\n`; });
    out += `${indent};\n`;
    return out;
  }
  return `${indent}${declaration}\n`;
}

/**
 * Builds the output statement for a templated string containing {placeholders}.
 * Centralises the 4-way language branch that was previously duplicated inline.
 */
/**
 * Infer the type of an expression by checking for function / method calls
 * against the module-level _functionReturnTypes map.
 *   "circle_area(radius)"   → looks up "circle_area"  → 'float'
 *   "myBox.volume()"        → looks up "volume"       → 'double'   (ADO #309)
 *   "this.greet()"          → looks up "greet"        → 'String'   (ADO #309)
 *
 * Method-name keying is shared with free functions since Phase 2 projects
 * are small enough that collisions are unlikely. A future enhancement
 * keyed on `ClassName.methodName` will land alongside ADO #308's Subflow
 * receiver work — both will read the same map.
 */
function inferExpressionType(expr, getVarType) {
  // Match either `methodName(` at the start, OR `receiver.methodName(` —
  // ADO #309: previously the regex only matched receiver-less calls, which
  // is why `{myBox.volume()}` in an Output template defaulted to `%d` and
  // crashed when volume() returned a double.
  const callMatch = expr.match(/(?:^|\.)(\w+)\s*\(/);
  if (callMatch && _functionReturnTypes[callMatch[1]]) {
    return _functionReturnTypes[callMatch[1]];
  }

  // READ THE OPERANDS BEFORE GIVING UP. Without this the fallback below
  // answered `int` for EVERY arithmetic expression, so `{side * side}` on a
  // float side emitted `%d` against a double — undefined behaviour, printing
  // a different garbage number each run. It only ever bit float EXPRESSIONS:
  // `{price}` is a bare variable and takes its declared type, and
  // `{price * qty:.2f}` has a spec that overrides the inference. Every earlier
  // fix here (ADO #309 method calls, ADO #400 array elements) was the same
  // shape — a placeholder form reaching this `int` fallback — and each was
  // found by content rather than by a test.
  //
  // C's usual arithmetic conversions: if any operand is floating, the result
  // is floating. A float literal in the expression counts for the same reason.
  if (typeof getVarType === 'function') {
    // Drop function NAMES before scanning — in `sqrt(area)` only `area` is an
    // operand. The return type of a known call was already handled above.
    const operands = expr.replace(/[A-Za-z_]\w*\s*\(/g, '(');
    for (const id of operands.match(/[A-Za-z_]\w*(?:\[[^\]]*\])?/g) || []) {
      if (isFloatingCType(getVarType(id.replace(/\[[^\]]*\]/g, '')))) return 'double';
    }
  }
  if (/(?:^|[^\w.])\d+\.\d+/.test(expr)) return 'double';

  return 'int';
}

/**
 * Is this declared type one that printf must receive as a double?
 *
 * Deliberately keyed on the type NAME rather than on the %-specifier, because
 * the specifier table maps `float` to `%f` and `double` to `%lf` — different
 * strings for the same varargs promotion, which is exactly the sort of detail
 * a caller gets wrong.
 */
function isFloatingCType(type) {
  const t = String(type || '').trim().toLowerCase();
  return t === 'float' || t === 'double' || t === 'long double';
}

function buildOutputStatement(lang, escapedTemplate, resources, indent, getVarType, resolveFormulaExpr, endWithNewline = true) {
  if (lang.name === 'C') {
    // ADO #287 Phase 1 — use the structured parser so explicit format
    // specs ({var:.2f}) and brace escapes ({{ / }}) are handled. Bare
    // {var} keeps the prior inference behaviour for backwards compat.
    const segments = parseOutputTemplate(escapedTemplate);
    let formatString = '';
    const expressions = [];

    for (const seg of segments) {
      if (seg.type === 'literal') {
        // Literal segments may contain `%` from the template — escape
        // them so printf doesn't treat them as format specifiers. The
        // double-brace escapes are already resolved into raw `{` / `}`
        // literals by the parser; printf is happy with those.
        formatString += seg.value.replace(/%/g, '%%');
        continue;
      }
      // Placeholder. Validator should have caught seg.error upstream;
      // codegen is best-effort if it slips through.
      const name = seg.name;
      const spec = seg.spec;

      // Resolve expression / variable / member.
      let expr;
      let dataType;

      // THROUGH A POINTER — `*p` and `p->member`.
      //
      // Neither matched any branch below, so both fell to inferExpressionType,
      // which has no view of what a pointer points AT. A char shown through a
      // pointer therefore printed its code while the same char shown by name
      // printed the letter:
      //
      //     by name A through pointer 65
      //
      // Same value, two routes, two answers — and the second reads as the
      // learner's mistake rather than the tool's.
      const _deref = /^\s*\*\s*([A-Za-z_]\w*)\s*$/.exec(name);
      const _arrow = /^\s*([A-Za-z_]\w*)\s*->\s*(.+)$/.exec(name);
      const _ptrRes = (_deref || _arrow)
        ? resources.find((r) => r.name === (_deref ? _deref[1] : _arrow[1]))
        : null;
      if (_ptrRes) {
        const pointee = _ptrRes.pointsToType
          || String(_ptrRes.dataType || '').replace(/\s*\*\s*$/, '');
        expr = name;
        if (_deref) {
          dataType = pointee || 'int';
        } else {
          // Resolve the member against the struct the pointer points at, by
          // finding an instance of that type — getMemberType already knows how
          // to read a member, it just needs something to read it from.
          const inst = resources.find(
            (r) => isStructInstance(r)
              && (r.structTypeRef === String(pointee || '').replace(/^struct\s+/, '')
                  || r.dataType === pointee));
          dataType = inst ? getMemberType(inst, _arrow[2], resources, 'c') : 'int';
        }
      } else if (name.includes('.')) {
        const parts = name.split('.');
        // ADO #382 — strip the array index so a record-table member resolves:
        // `table[0].name` / `table[i].name` → base `table` (an array-of-structs,
        // isStructInstance via its struct_ dataType) → getMemberType picks %s/%f.
        const baseName = parts[0].replace(/\[[^\]]*\]/g, '');
        const resource = resources.find((r) => r.name === baseName);
        if (resource && isStructInstance(resource)) {
          expr = name;
          dataType = getMemberType(resource, parts.slice(1).join('.'), resources, 'c');
        } else {
          expr = /^[a-zA-Z_][a-zA-Z0-9_]*$/.test(name) ? resolveFormulaExpr(name) : name;
          // A DOT IS NOT ALWAYS A MEMBER ACCESS. This branch is entered for any
          // placeholder containing `.`, which swallows every expression carrying
          // a decimal literal — `{price * 1.18}` was split on the dot into a
          // base of `price * 1`, matched no resource, and asked getVarType for a
          // variable named "price * 1.18". That answers undefined, which falls
          // through to %d against a double.
          const isMemberPath =
            /^[A-Za-z_]\w*(\[[^\]]*\])?(\.[A-Za-z_]\w*(\[[^\]]*\])?)+$/.test(name);
          dataType = isMemberPath ? getVarType(name) : inferExpressionType(name, getVarType);
        }
      } else if (/^[a-zA-Z_][a-zA-Z0-9_]*$/.test(name)) {
        expr = resolveFormulaExpr(name);
        dataType = getVarType(name);
      } else if (/^[a-zA-Z_][a-zA-Z0-9_]*\[[^\]]*\]$/.test(name)) {
        // ADO #400 — indexed array element (e.g. argv[i], argv[0]). For an
        // array_of_pointers, the element type is its elementType (argv[i] →
        // char* → %s).
        //
        // Every other array element takes the ELEMENT TYPE of the array it came
        // out of, which is the general form of the same rule. It used to fall
        // through to inferExpressionType, which answered `int` for anything it
        // did not recognise — so `{name[0]}` on a char array printed 110 rather
        // than n. Harmless for int arrays, which is why it survived: M6's
        // {scores[i]} wanted %d and got it by accident.
        expr = name;
        const baseName = name.replace(/\[[^\]]*\]/g, '');
        const baseRes = resources.find((r) => r.name === baseName);
        const rt = (baseRes?.resourceType || baseRes?.type || '').toLowerCase();
        if (rt === 'array_of_pointers') {
          dataType = baseRes.elementType || 'char*';
        } else if (baseRes && (rt === 'array_1d' || rt === 'array')) {
          dataType = baseRes.elementType || baseRes.dataType || inferExpressionType(name, getVarType);
        } else if (baseRes && rt === 'array_2d' && (baseRes.elementType || baseRes.dataType) === 'char') {
          // ONE index into a grid of characters is a whole ROW: a piece of
          // text. `{words[k]}` in a list of words printed the row's address
          // with %d, because it fell to inferExpressionType below.
          dataType = 'char[]';
        } else {
          dataType = inferExpressionType(name, getVarType);
        }
      } else {
        expr = name;
        dataType = inferExpressionType(name, getVarType);
      }

      // THE SPEC AND THE ARGUMENT MUST AGREE. `{total:.0f}` on two ints asked
      // printf for a double and handed it an int — which printed `0`, so the
      // learner who correctly reached for a specifier got the most confusing
      // possible answer. A float conversion over an integer expression is
      // legitimate (".0f on 612/7" is a whole number shown with no decimals),
      // so promote the ARGUMENT rather than rejecting the template.
      const wantsFloatConv = spec && /[feEgG]/.test(String(spec));
      if (wantsFloatConv && !isFloatingCType(dataType)) {
        expr = `(double)(${expr})`;
        dataType = 'double';
      }
      expressions.push(expr);

      // If the placeholder carries an explicit spec, build the printf
      // conversion from it (auto-promoting wide ints). Otherwise fall
      // back to type-driven inference (the pre-#287 behaviour).
      if (spec) {
        formatString += buildCFormatFromSpec(spec, dataType);
      } else {
        formatString += getCFormatSpecifier(dataType);
      }
    }

    formatString = formatString.replace(/"/g, '\\"');
    const nlC = endWithNewline ? '\\n' : '';
    return expressions.length > 0
      ? `${indent}printf("${formatString}${nlC}", ${expressions.join(', ')});\n`
      : `${indent}printf("${formatString}${nlC}");\n`;

  } else if (lang.name === 'C++') {
    // C++ uses cout with << chaining
    // Split template into literal parts and expression parts
    const parts = [];
    let lastIndex = 0;
    const regex = /\{([^{}]+)\}/g;
    let m;
    while ((m = regex.exec(escapedTemplate)) !== null) {
      if (m.index > lastIndex) {
        parts.push({ type: 'text', value: escapedTemplate.substring(lastIndex, m.index) });
      }
      const trimmedExpr = m[1].trim();
      if (trimmedExpr.includes('.')) {
        const dotParts = trimmedExpr.split('.');
        const baseName = dotParts[0].replace(/\[[^\]]*\]/g, ''); // ADO #382 — strip index
        const resource = resources.find(r => r.name === baseName);
        if (resource && isStructInstance(resource)) {
          parts.push({ type: 'expr', value: trimmedExpr });
        } else {
          parts.push({ type: 'expr', value: /^[a-zA-Z_][a-zA-Z0-9_]*$/.test(trimmedExpr) ? resolveFormulaExpr(trimmedExpr) : trimmedExpr });
        }
      } else {
        parts.push({ type: 'expr', value: /^[a-zA-Z_][a-zA-Z0-9_]*$/.test(trimmedExpr) ? resolveFormulaExpr(trimmedExpr) : trimmedExpr });
      }
      lastIndex = m.index + m[0].length;
    }
    if (lastIndex < escapedTemplate.length) {
      parts.push({ type: 'text', value: escapedTemplate.substring(lastIndex) });
    }

    const cppTerm = endWithNewline ? ' << endl' : '';
    if (parts.length === 0) {
      return `${indent}cout << ""${cppTerm};\n`;
    }

    const coutParts = parts.map(p =>
      p.type === 'text' ? `"${p.value.replace(/"/g, '\\"')}"` : p.value
    );
    return `${indent}cout << ${coutParts.join(' << ')}${cppTerm};\n`;

  } else if (lang.name === 'Java') {
    // ADO #309 — Java uses java.util.Formatter, which speaks a different
    // specifier alphabet from C's printf. Two specific failure modes were
    // crashing student code at runtime:
    //   1. {myBox.volume()} where volume() returned `double` emitted
    //      `printf("%d%n", ...)` and threw IllegalFormatConversionException.
    //      Root cause: the receiver.method() pattern fell through to the
    //      `%d` default. Fix: when the placeholder is clearly a method call,
    //      look up the method's return type in _functionReturnTypes (which
    //      already includes every non-entry flow's returnType), and fall
    //      back to %s when unresolved — Formatter accepts any Object.
    //   2. Bare `double price` template `{price}` emitted `%lf` (a C-only
    //      conversion), which Formatter rejects. Switching to the
    //      Java-specific specifier map fixes this as a side effect.
    let formatString = escapedTemplate;
    const expressions = [];
    formatString = formatString.replace(/\{([^{}]+)\}/g, (match, expr) => {
      const trimmedExpr = expr.trim();

      // Struct member access — `obj.field.subfield`. Keep the C/struct
      // path for the Java struct-emulation cases that already work.
      if (trimmedExpr.includes('.') && !trimmedExpr.includes('(')) {
        const parts = trimmedExpr.split('.');
        const resource = resources.find(r => r.name === parts[0]);
        if (resource && isStructInstance(resource)) {
          expressions.push(trimmedExpr);
          return getJavaFormatSpecifier(getMemberType(resource, parts.slice(1).join('.'), resources, 'java'));
        }
      }

      // Bare identifier — declared variable lookup.
      if (/^[a-zA-Z_][a-zA-Z0-9_]*$/.test(trimmedExpr)) {
        expressions.push(resolveFormulaExpr(trimmedExpr));
        return getJavaFormatSpecifier(getVarType(trimmedExpr));
      }

      // Method call — either bare `method()` or `receiver.method()`.
      // ADO #309: when _functionReturnTypes knows the method, use that
      // type; otherwise fall back to 'string' so the format specifier
      // becomes %s, which Formatter accepts for any Object.
      const callMatch = trimmedExpr.match(/(?:^|\.)(\w+)\s*\(/);
      if (callMatch) {
        expressions.push(trimmedExpr);
        const resolved = _functionReturnTypes[callMatch[1]];
        return getJavaFormatSpecifier(resolved || 'string');
      }

      // Anything else (literal numeric, operator expression, etc.) — let
      // inferExpressionType take a shot, but use %s as the safe default
      // rather than %d when nothing matches.
      expressions.push(trimmedExpr);
      return getJavaFormatSpecifier(inferExpressionType(trimmedExpr));
    });
    formatString = formatString.replace(/"/g, '\\"');
    // Java has no equivalent to Python's print(end='') — we switch the
    // helper itself: printf("…%n", …) ↔ printf("…", …); println(…) ↔ print(…).
    const javaNl = endWithNewline ? '%n' : '';
    const javaPrint = endWithNewline ? 'println' : 'print';
    return expressions.length > 0
      ? `${indent}System.out.printf("${formatString}${javaNl}", ${expressions.join(', ')});\n`
      : `${indent}System.out.${javaPrint}("${formatString}");\n`;

  } else if (lang.name === 'Python') {
    // Build an f-string carefully for Python < 3.12 (Judge0), where an f-string
    // EXPRESSION (`{…}`) may contain neither a backslash nor the same quote that
    // delimits the string. Split into text / {expr} segments so we can:
    //   1. choose the delimiter that doesn't collide with the quotes used inside
    //      an expression — default `"`, but switch to `'` when an expression
    //      contains a `"` and none contains a `'` (so `{len("Ram")}` works), and
    //   2. escape ONLY the delimiter, and ONLY in the literal text — never
    //      inside an expression (so `{len('Ram')}` is NOT mangled to `\'`).
    // Backslashes in text are left as-is, so the caller's `\n` newline survives.
    const segs = [];
    let li = 0; const re = /\{[^{}]+\}/g; let mm;
    while ((mm = re.exec(escapedTemplate)) !== null) {
      if (mm.index > li) segs.push({ t: 'text', v: escapedTemplate.slice(li, mm.index) });
      const inner = mm[0].slice(1, -1).trim();
      const innerV = /^[a-zA-Z_][a-zA-Z0-9_]*$/.test(inner) ? resolveFormulaExpr(inner) : inner;
      // ADO #441 — a class attribute in a Python method's output → {self.attr}.
      segs.push({ t: 'expr', v: pySelfRef(innerV, resources) });
      li = mm.index + mm[0].length;
    }
    if (li < escapedTemplate.length) segs.push({ t: 'text', v: escapedTemplate.slice(li) });

    const exprs = segs.filter(s => s.t === 'expr').map(s => s.v);
    const exprHasDouble = exprs.some(e => e.includes('"'));
    const exprHasSingle = exprs.some(e => e.includes("'"));
    const q = (exprHasDouble && !exprHasSingle) ? "'" : '"';

    const pyTemplate = segs.map(s =>
      s.t === 'expr' ? `{${s.v}}` : s.v.split(q).join('\\' + q)
    ).join('');
    const pyEnd = endWithNewline ? '' : ', end=""';
    return `${indent}print(f${q}${pyTemplate}${q}${pyEnd})\n`;

  } else if (lang.name === 'JavaScript') {
    const jsTemplate = escapedTemplate
      .replace(/`/g, '\\`')
      .replace(/\{([^{}]+)\}/g, (_, expr) => {
        const t = expr.trim();
        return `\${${/^[a-zA-Z_][a-zA-Z0-9_]*$/.test(t) ? resolveFormulaExpr(t) : t}}`;
      });
    // console.log always appends a newline; for inline output use
    // process.stdout.write which doesn't.
    return endWithNewline
      ? `${indent}console.log(\`${jsTemplate}\`);\n`
      : `${indent}process.stdout.write(\`${jsTemplate}\`);\n`;

  } else {
    return `${indent}DISPLAY "${escapedTemplate}"\n`;
  }
}

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Analyze code/nodes to determine which C headers are needed
 */
/**
 * Scan generated C code for function calls and determine required headers.
 * This runs AFTER code generation so it catches every function — including
 * ones the code generator introduces implicitly (strcpy, strcmp, etc.).
 */
function detectCHeadersFromCode(generatedCode) {
  const requiredHeaders = new Set();

  for (const [header, requirements] of Object.entries(C_HEADER_REQUIREMENTS)) {
    // Check function calls: match `funcName(` in the generated code
    for (const func of requirements.functions) {
      const regex = new RegExp(`\\b${func}\\s*\\(`, 'i');
      if (regex.test(generatedCode)) {
        requiredHeaders.add(header);
        break;
      }
    }

    // Check features: boolean type → <stdbool.h>
    if (!requiredHeaders.has(header)) {
      for (const feature of requirements.features) {
        if (feature === 'boolean' && /\bbool\b/.test(generatedCode)) {
          requiredHeaders.add(header);
          break;
        }
      }
    }

    // ADO #361 — type-pattern headers (<stdint.h>, <stddef.h>): include when a
    // matching type spelling appears in the generated code.
    if (!requiredHeaders.has(header) && requirements.typeRegex && requirements.typeRegex.test(generatedCode)) {
      requiredHeaders.add(header);
    }
  }

  return Array.from(requiredHeaders).sort();
}

/**
 * Generate C #include block by scanning the already-generated code.
 * @param {string} generatedCode - The C code (without headers)
 * @returns {string} - The #include lines to prepend
 */
function generateCHeader(generatedCode) {
  const headers = detectCHeadersFromCode(generatedCode);

  if (headers.length === 0) {
    return '';
  }

  return headers.map(h => `#include ${h}`).join('\n') + '\n\n';
}

// ADO #430 — Python stdlib modules whose `module.member` usage triggers an
// `import module` line, mirroring C's dynamic-header scan. Kept in sync with
// ActionNodeEditor BUILTIN_OBJECTS.python. Catches usage from action nodes AND
// raw expression strings (assignments / conditions / output templates) because
// they all land in the generated code, which is what we scan.
const PYTHON_IMPORT_MODULES = ['datetime', 'json', 'math', 'os', 'random', 're', 'sys'];

// ADO #430 — bare (unqualified) stdlib FUNCTION names → their module. A student
// who types `sqrt(x)` instead of `math.sqrt(x)` (natural coming from C/maths)
// would otherwise get a NameError, since these are NOT Python builtins. We emit
// `from math import sqrt` so the generated code runs. Python builtins (pow, abs,
// round, min, max, sum, len, …) are deliberately EXCLUDED — they need no import.
const PYTHON_BARE_FUNCTION_MODULE = {
  sqrt: 'math', ceil: 'math', floor: 'math', fabs: 'math', factorial: 'math',
  gcd: 'math', sin: 'math', cos: 'math', tan: 'math', log: 'math', log10: 'math',
  exp: 'math', trunc: 'math', radians: 'math', degrees: 'math', hypot: 'math',
  randint: 'random', uniform: 'random', randrange: 'random', shuffle: 'random',
  sample: 'random', choice: 'random', choices: 'random', gauss: 'random',
};

/**
 * Scan generated Python code for stdlib usage and return the import block to
 * place at the top. Runs AFTER generation (same strategy as generateCHeader).
 * Two forms:
 *   - `module.member` (e.g. `math.sqrt(x)`)        → `import math`
 *   - a bare stdlib function call (e.g. `sqrt(x)`)  → `from math import sqrt`
 * The `\.\w` guard requires a real member access, so prose like "do the math."
 * in a string/comment doesn't trigger a spurious import. Bare-call detection
 * skips qualified calls (`math.sqrt` — handled by the module form) and any name
 * the program defines itself (`def sqrt(...)`).
 * @param {string} generatedCode
 * @returns {string} e.g. `import math\nfrom random import randint\n\n`, or ''.
 */
export function generatePythonImports(generatedCode) {
  const lines = [];

  // `module.member` usage → `import module`.
  const modules = [];
  for (const mod of PYTHON_IMPORT_MODULES) {
    if (new RegExp(`\\b${mod}\\.\\w`).test(generatedCode)) modules.push(mod);
  }
  modules.sort();
  for (const m of modules) lines.push(`import ${m}`);

  // Bare stdlib function calls → grouped `from module import a, b`.
  const fromImports = {}; // module → Set(names)
  for (const [name, mod] of Object.entries(PYTHON_BARE_FUNCTION_MODULE)) {
    // A call `name(` not preceded by a word char or `.` (so `math.sqrt(` and
    // `mysqrt(` are excluded), and not defined by the program itself.
    const bareCall = new RegExp(`(?:^|[^\\w.])${name}\\s*\\(`);
    const userDef = new RegExp(`\\bdef\\s+${name}\\s*\\(`);
    if (bareCall.test(generatedCode) && !userDef.test(generatedCode)) {
      (fromImports[mod] = fromImports[mod] || new Set()).add(name);
    }
  }
  for (const mod of Object.keys(fromImports).sort()) {
    const names = Array.from(fromImports[mod]).sort().join(', ');
    lines.push(`from ${mod} import ${names}`);
  }

  if (lines.length === 0) return '';
  return lines.join('\n') + '\n\n';
}

/**
 * ADO #430 — Python scoping: a function that ASSIGNS a module-level global must
 * declare `global x`, or the assignment silently creates a LOCAL and the global
 * keeps its initial value. Scan a function body for assignments (`x =`, `x +=`,
 * …; NOT `x ==`, and NOT attribute/item assignment `x.f =` / `x[i] =`, which
 * don't rebind) to any global name, and return the `global a, b` line to place
 * at the top of the function (indented), or '' when none are assigned.
 * @returns {string}
 */
function pythonGlobalDeclaration(bodyCode, globalResources, indentUnit) {
  const names = (globalResources || [])
    .filter(r => {
      const rt = (r.resourceType || r.type || '').toLowerCase();
      return rt === 'variable' || rt === 'constant';
    })
    .map(r => r.name)
    .filter(Boolean);
  if (names.length === 0) return '';
  const assigned = names.filter(name =>
    new RegExp(`(?:^|[^\\w.])${name}\\s*(?:=|\\+=|-=|\\*=|/=|//=|%=|\\*\\*=)(?!=)`, 'm').test(bodyCode)
  );
  if (assigned.length === 0) return '';
  return `${indentUnit}global ${assigned.join(', ')}\n`;
}

/**
 * Insert the Python import block immediately after the `# Generated Python Code`
 * file-header comment (or at the very top if the header isn't present), so the
 * imports sit above the globals/functions. Returns the new code unchanged when
 * no modules are used.
 */
function insertPythonImports(code, fileHeader) {
  const importBlock = generatePythonImports(code);
  if (!importBlock) return code;
  const fh = fileHeader || '';
  if (fh && code.startsWith(fh)) return fh + importBlock + code.slice(fh.length);
  return importBlock + code;
}

/**
 * ADO #431 — render a value as a Python literal for dict/set initialization.
 * String-typed values are quoted (unless the student already quoted them);
 * everything else (int/float/bool/expression) is emitted verbatim. Returns null
 * for a blank value so the caller can skip the entry.
 */
function pythonLiteral(raw, type) {
  const v = String(raw == null ? '' : raw).trim();
  if (v === '') return null;
  const t = (type || '').toLowerCase();
  if (t === 'str' || t === 'string') {
    if (/^(['"]).*\1$/.test(v)) return v; // already quoted
    return `"${v.replace(/\\/g, '\\\\').replace(/"/g, '\\"')}"`;
  }
  return v;
}

/**
 * ADO #431 — visual dict-key access. When indexing a dictionary with str keys
 * (`ages["Ram"]`), quote a LITERAL key. Leaves bare: int-keyed dicts (`d[0]`),
 * already-quoted keys, numeric keys, and keys that are a declared variable
 * (`ages[name]`). Non-dict resources pass through unchanged (array indices).
 */
function quoteDictKey(key, resource, resources) {
  if (key == null || String(key).trim() === '') return key;
  const rt = (resource?.resourceType || resource?.type || '').toLowerCase();
  if (rt !== 'dictionary' && rt !== 'dict') return key;
  const keyType = (resource.keyType || 'str').toLowerCase();
  if (keyType !== 'str' && keyType !== 'string') return key;
  const k = String(key).trim();
  if (/^(['"]).*\1$/.test(k)) return k;                 // already quoted
  if (!isNaN(k)) return k;                              // numeric literal
  if ((resources || []).some(r => r.name === k)) return k; // a variable key
  return `"${k.replace(/"/g, '\\"')}"`;
}

// ADO #396 — is this resource an object-like preprocessor macro?
function isMacroResource(r) {
  return (r?.resourceType || r?.type || '').toLowerCase() === 'macro';
}

/**
 * ADO #397 — build a function-like macro body with the classic defensive
 * parenthesisation: every whole-word parameter reference is wrapped in parens
 * and the whole body is wrapped too, so `x * x` (param x) → `((x) * (x))` and
 * `a > b ? a : b` (params a,b) → `((a) > (b) ? (a) : (b))`. This avoids the
 * operator-precedence and argument-side-effect traps macros are infamous for.
 */
function buildFunctionMacroBody(body, params) {
  let b = String(body || '').trim();
  for (const p of params) {
    if (!p) continue;
    const esc = p.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    b = b.replace(new RegExp(`\\b${esc}\\b`, 'g'), `(${p})`);
  }
  return `(${b})`;
}

/**
 * ADO #396 — build the `#define` block for C/C++ macro resources.
 * Macros are hoisted to the top of the file (right after the #include block)
 * so they precede any global declaration that uses them as an array size,
 * regardless of the order the student added resources. Returns '' for no
 * macros or non-C/C++ tracks (which have no preprocessor).
 */
function generateMacroBlock(resources, lang, language) {
  if (language !== 'c' && language !== 'cpp') return '';
  const macros = (resources || []).filter(isMacroResource);
  if (macros.length === 0) return '';
  return macros.map(m => generateResourceDeclaration(m, lang, language)).join('\n') + '\n\n';
}

// ADO #400 — the C/C++ `main()` opening line. Emits the argc/argv signature
// when the entry flow opts in (`usesCommandLineArgs`); otherwise the default
// `int main() {`. Non-C tracks always use lang.mainStart (Phase 1 = C only).
function mainStartFor(lang, language, entryFlow) {
  // The flow's IPO, when written, as a comment above main (C, Ram 2026-10-29).
  const ipo = language === 'c' ? ipoComment(entryFlow, false) : '';
  if ((language === 'c' || language === 'cpp') && entryFlow && entryFlow.usesCommandLineArgs) {
    return `${ipo}int main(int argc, char *argv[]) {\n`;
  }
  return `${ipo}${lang.mainStart}`;
}

// C++ header requirements mapping
const CPP_HEADER_REQUIREMENTS = {
  '<iostream>': {
    functions: ['cout', 'cin', 'cerr', 'clog'],
    features: ['input', 'output'],
  },
  '<string>': {
    functions: ['getline', 'to_string', 'stoi', 'stol', 'stod', 'stof'],
    features: ['string_type'],
  },
  '<cmath>': {
    functions: ['sin', 'cos', 'tan', 'asin', 'acos', 'atan', 'atan2', 'sinh', 'cosh', 'tanh',
                'exp', 'log', 'log10', 'log2', 'pow', 'sqrt', 'cbrt', 'ceil', 'floor', 'round',
                'trunc', 'fabs', 'fmod', 'fmax', 'fmin', 'hypot', 'isnan', 'isinf', 'abs'],
    features: [],
  },
  '<cstdlib>': {
    functions: ['rand', 'srand', 'exit', 'abort', 'atoi', 'atol', 'atof', 'system'],
    features: [],
  },
  '<algorithm>': {
    functions: ['sort', 'find', 'reverse', 'min', 'max', 'swap', 'count', 'fill',
                'binary_search', 'lower_bound', 'upper_bound', 'unique', 'remove'],
    features: [],
  },
  '<cstring>': {
    functions: ['strlen', 'strcpy', 'strncpy', 'strcat', 'strcmp', 'strncmp',
                'strchr', 'strstr', 'memcpy', 'memset', 'memcmp'],
    features: [],
  },
  '<cctype>': {
    functions: ['isalpha', 'isdigit', 'isalnum', 'isspace', 'isupper', 'islower', 'ispunct',
                'isprint', 'toupper', 'tolower'],
    features: [],
  },
  '<ctime>': {
    functions: ['time', 'clock', 'difftime', 'mktime', 'strftime', 'localtime', 'gmtime'],
    features: [],
  },
};

/**
 * Analyze nodes to determine which C++ headers are needed
 */
function analyzeCppHeaderRequirements(flows, globalResources = []) {
  const requiredHeaders = new Set();

  const allResources = [...globalResources];
  flows.forEach(f => { if (f.resources) allResources.push(...f.resources); });

  const allNodes = [];
  flows.forEach(f => { if (f.nodes) allNodes.push(...collectAllNodes(f.nodes)); });

  // Always include <string> if any resource uses string type
  if (allResources.some(r => {
    const dt = (r.dataType || r.elementType || '').toLowerCase();
    return dt === 'string' || dt === 'text';
  })) {
    requiredHeaders.add('<string>');
  }

  for (const [header, requirements] of Object.entries(CPP_HEADER_REQUIREMENTS)) {
    let headerNeeded = false;

    for (const feature of requirements.features) {
      if ((feature === 'input' || feature === 'output') &&
          allNodes.some(n => n.type === 'input' || n.type === 'output')) {
        headerNeeded = true;
        break;
      }
      if (feature === 'string_type' &&
          allResources.some(r => {
            const dt = (r.dataType || r.elementType || '').toLowerCase();
            return dt === 'string' || dt === 'text';
          })) {
        headerNeeded = true;
        break;
      }
    }

    if (!headerNeeded && requirements.functions.length > 0) {
      const expressions = [];
      for (const node of allNodes) {
        if (node.rawExpression) expressions.push(node.rawExpression);
        if (node.outcomes) {
          node.outcomes.forEach(o => { if (o.rawExpression) expressions.push(o.rawExpression); });
        }
        if (node.assignments) {
          node.assignments.forEach(a => {
            if (a.expression) expressions.push(a.expression);
            if (a.value) expressions.push(String(a.value));
          });
        }
        if (node.type === 'output') {
          if (node.outputTemplate) expressions.push(node.outputTemplate);
          if (node.description) expressions.push(node.description);
        }
        if (node.type === 'action') {
          if (node.actionData?.expression) expressions.push(node.actionData.expression);
          if (node.actionExpression) expressions.push(node.actionExpression);
          if (node.actionData?.owner) expressions.push(`${node.actionData.functionName || node.actionData.method}()`);
        }
      }
      for (const expr of expressions) {
        for (const func of requirements.functions) {
          if (new RegExp(`\\b${func}\\s*\\(`, 'i').test(expr)) {
            headerNeeded = true;
            break;
          }
        }
        if (headerNeeded) break;
      }
    }

    if (headerNeeded) requiredHeaders.add(header);
  }

  // Check for try_catch nodes using std:: exception types → need <stdexcept>
  if (allNodes.some(n => n.type === 'try_catch' && n.branches?.some(b =>
    b.type === 'catch' && b.exceptionType && b.exceptionType.startsWith('std::') && b.exceptionType !== 'std::exception'
  ))) {
    requiredHeaders.add('<stdexcept>');
  }
  // std::exception itself is in <exception>
  if (allNodes.some(n => n.type === 'try_catch' && n.branches?.some(b =>
    b.type === 'catch' && b.exceptionType === 'std::exception'
  ))) {
    requiredHeaders.add('<exception>');
  }

  return Array.from(requiredHeaders).sort();
}

/**
 * Generate C++ file header with only required includes + using namespace std
 */
function generateCppHeader(flows, globalResources = []) {
  const headers = analyzeCppHeaderRequirements(flows, globalResources);

  if (headers.length === 0) {
    return 'using namespace std;\n\n';
  }

  return headers.map(h => `#include ${h}`).join('\n') + '\nusing namespace std;\n\n';
}

// Map flow builder types to C types
/**
 * ADO #389 — resolve the variable-picker's empty `[]` placeholders into real
 * indices for assignment targets/sources.
 *
 * The picker emits `db[]` for an indexable resource and `db[].gpa` for a
 * struct-array member. When the student supplies an index:
 *   - a MID-path placeholder (`db[].gpa`) splices it in   → `db[i].gpa`
 *   - a trailing/absent placeholder (`nos[]` / `nos`)     → `nos[i]` (`[i][j]`)
 *
 * The previous inline logic did BOTH for a mid-path path — appending `[i]` at
 * the end AND replacing the mid `[]` — emitting `db[i].gpa[i]`.
 */
export function applyArrayIndices(path, accessIsArray, idx, idx2) {
  if (typeof path !== 'string') return path;
  // ADO #408 — no index supplied: a trailing picker placeholder (`arr[]`)
  // decays to the bare name (`arr`). This is the whole-array-as-pointer form
  // used by `p = arr` (array name decays to `int*`); without the strip the
  // empty `[]` would survive into the generated code as `p = arr[];`.
  if (!idx) return path.replace(/\[\s*\]\s*$/, '');
  // Mid-path placeholder: `db[].gpa` → `db[i].gpa` (a second mid `[]` takes idx2).
  if (/\[\s*\]\s*\./.test(path)) {
    let out = path.replace(/\[\s*\]/, `[${idx}]`);
    if (idx2 && /\[\s*\]/.test(out)) out = out.replace(/\[\s*\]/, `[${idx2}]`);
    return out;
  }
  if (accessIsArray) {
    let out = path.replace(/\[\s*\]\s*$/, ''); // strip the picker's trailing []
    out = `${out}[${idx}]`;
    if (idx2) out = `${out}[${idx2}]`;
    return out;
  }
  // Safety net for any stray placeholder.
  return path.includes('[]') ? path.replace('[]', `[${idx}]`) : path;
}

/**
 * Render one C/C++ parameter, honouring an array spelling on its type.
 *
 * A parameter declared `char[]` (or `char[20]`, or `int[]`) has to become
 * `char text[]`, not `char[] text` and certainly not `int text` — which is what
 * happened before this existed, because `mapToCType` recognises no array form
 * and falls through to its `'int'` default. The result compiled to
 * `int my_strlen(int text)` and then refused `text[i]`.
 *
 * The suffix moves to the RIGHT of the name because that is where C puts it.
 * `char text[]` and `char *text` are the same parameter to the compiler; they
 * are not the same to a first-year, and this course does not teach pointers —
 * so the array spelling is the one that has to reach the generated code.
 *
 * A size inside the brackets is deliberately dropped: `char text[20]` as a
 * parameter tells the compiler nothing `char text[]` does not, and keeping it
 * would imply a guarantee the language does not make.
 */
export function formatCParam(type, name, typeMapper) {
  const dims = splitArrayDims(type);
  if (!dims) return `${typeMapper(type)} ${name}`;
  // ADO #579 — a 2-D parameter keeps every size but the first: `char grid[][40]`.
  return `${typeMapper(dims.base)} ${name}[]${dims.rest.map((d) => `[${d}]`).join('')}`;
}

// `char[]` → { base: 'char', rest: [] }; `char[][40]` → { base: 'char', rest: ['40'] };
// `int[][MAX_COLS]` → rest ['MAX_COLS']. null when the type has no array part.
export function splitArrayDims(type) {
  const m = /^\s*(.+?)\s*((?:\[\s*[\w]*\s*\]\s*)+)$/.exec(type || '');
  if (!m) return null;
  const all = [...m[2].matchAll(/\[\s*([\w]*)\s*\]/g)].map((x) => x[1]);
  return { base: m[1], rest: all.slice(1) };
}

/**
 * The same parameter in Java, where the brackets belong to the TYPE:
 * `char[] text`, not `char text[]`.
 *
 * One flow generates every track at once, so an array parameter that reads
 * correctly in C and `int text` in Java is a visible contradiction on the same
 * screen — which is why this exists even while the Java track is paused.
 */
export function formatJavaParam(type, name) {
  const dims = splitArrayDims(type);
  if (!dims) return `${mapToJavaType(type)} ${name}`;
  return `${mapToJavaType(dims.base)}${'[]'.repeat(1 + dims.rest.length)} ${name}`;
}

export function mapToCType(type) {
  const typeMap = {
    'int': 'int',
    'integer': 'int',
    'float': 'float',
    'double': 'double',
    'string': 'char*',
    'char': 'char',
    // ADO — C99 `bool`, not `int`. Mapping these to int meant a declared bool
    // emitted `int member = true;`, and `true` is undeclared in C without
    // <stdbool.h>. The header IS supported — the '<stdbool.h>' entry above
    // includes it when /\bbool\b/ appears in the generated code — but mapping
    // the type away meant that test could never match, so the support was
    // unreachable and every bool resource produced code that did not compile.
    // Judge0 builds C99+, and `bool member = true;` also reads like the flow it
    // came from, which matters more in a course than saving a header.
    'boolean': 'bool',
    'bool': 'bool',
    'text': 'char*',
    'number': 'int',
    'long': 'long',
    'short': 'short',
  };

  // ADO #292 — pointer types (e.g. 'int*', 'float *', 'struct Point*').
  // Strip trailing '*'s, map the base, re-append. Preserves pointer level
  // for double pointers (`int**` → mapped base + '**'). Without this, a
  // function with `int *a` parameter rendered as `int a` (mapToCType
  // returned the default `'int'`) and the body's `*a` failed to compile.
  const ptrMatch = type && /^(.+?)(\s*\*+\s*)$/.exec(type);
  if (ptrMatch) {
    const baseMapped = mapToCType(ptrMatch[1].trim());
    const stars = ptrMatch[2].replace(/\s+/g, '');
    return `${baseMapped}${stars}`;
  }

  // Handle struct types
  if (type?.startsWith('struct ')) {
    return type; // Already in correct format: "struct name"
  }
  if (type?.startsWith('struct_')) {
    // Convert struct_name to struct name
    return `struct ${type.replace('struct_', '')}`;
  }

  // ADO #381 — opaque stdlib types that are valid, case-sensitive C spellings
  // (FILE from <stdio.h>) must pass through verbatim. Without this, `FILE*`
  // recursed to mapToCType('FILE') → 'int', emitting `int*` for a file handle.
  if (type && type.trim() === 'FILE') return 'FILE';

  // ADO #361 — signed/unsigned + width modifiers and C99 fixed-width types are
  // already valid C spellings; pass them through verbatim instead of falling
  // back to 'int'. (`unsigned` alone normalizes to `unsigned int`.)
  const lc = (type || '').toLowerCase().trim();
  if (C_PASSTHROUGH_TYPES.has(lc)) {
    return lc === 'unsigned' ? 'unsigned int' : lc;
  }
  if (typeMap[lc]) return typeMap[lc];

  // ADO #410 — an unknown bare identifier is almost always a user-defined
  // typedef alias (`u32`, `Byte`, `ElementType`); emit it VERBATIM (preserving
  // the student's casing) rather than collapsing to `int`. Multi-word and
  // pointer/struct/FILE forms are already handled above, so only a single plain
  // identifier reaches here.
  const original = (type || '').trim();
  if (/^[A-Za-z_]\w*$/.test(original)) return original;

  return 'int';
}

// Map flow builder types to Java types
function mapToJavaType(type) {
  const typeMap = {
    'int': 'int',
    'integer': 'int',
    'float': 'float',
    'double': 'double',
    'string': 'String',
    'char': 'char',
    'boolean': 'boolean',
    'bool': 'boolean',
    'text': 'String',
    'number': 'int',
    'long': 'long',
    'short': 'short',
  };
  return typeMap[type?.toLowerCase()] || 'int';
}

// Map flow builder types to C++ types
function mapToCppType(type) {
  const typeMap = {
    'int': 'int',
    'integer': 'int',
    'float': 'float',
    'double': 'double',
    'string': 'string',
    'char': 'char',
    'boolean': 'bool',
    'bool': 'bool',
    'text': 'string',
    'number': 'int',
    'long': 'long',
    'short': 'short',
  };

  // ADO #292 — pointer types preserve trailing `*` (parallel to mapToCType).
  const ptrMatch = type && /^(.+?)(\s*\*+\s*)$/.exec(type);
  if (ptrMatch) {
    const baseMapped = mapToCppType(ptrMatch[1].trim());
    const stars = ptrMatch[2].replace(/\s+/g, '');
    return `${baseMapped}${stars}`;
  }

  // Handle struct types — C++ doesn't need the 'struct' prefix
  if (type?.startsWith('struct ')) {
    return type.replace('struct ', ''); // "struct Point" → "Point"
  }
  if (type?.startsWith('struct_')) {
    return type.replace('struct_', ''); // "struct_Point" → "Point"
  }

  return typeMap[type?.toLowerCase()] || 'int';
}

// Get Java Scanner method for type
function getJavaScannerMethod(type) {
  // NOTE: char is intentionally NOT in this map — it needs a chained
  // call (scanner.next().charAt(0)) that the simple scanner.X() template
  // can't express. The Java input() handler special-cases 'char' before
  // calling this helper.
  const methods = {
    'int': 'nextInt',
    'integer': 'nextInt',
    'float': 'nextFloat',
    'double': 'nextDouble',
    'string': 'nextLine',
    'boolean': 'nextBoolean',
    'bool': 'nextBoolean',
    'long': 'nextLong',
    'short': 'nextShort',
  };
  return methods[type?.toLowerCase()] || 'nextInt';
}

// Parse expression to extract output content
function parseOutputContent(node) {
  // Check description first (user-friendly message)
  if (node.description) {
    // Extract the message - if it contains "Display" or "Print", extract the rest
    const desc = node.description.trim();
    if (desc.toLowerCase().startsWith('display ')) {
      return desc.substring(8).trim();
    }
    if (desc.toLowerCase().startsWith('print ')) {
      return desc.substring(6).trim();
    }
    return desc;
  }
  // Fallback to label
  return node.label || 'Output';
}

// Parse input content
function parseInputContent(node) {
  if (node.variable) {
    return { varName: node.variable, type: node.variableType || 'int' };
  }
  if (node.apiName) {
    return { varName: node.apiName, type: node.variableType || 'int' };
  }
  return { varName: node.label || 'input', type: 'int' };
}

// Parse assignment content
function parseAssignmentContent(node) {
  return {
    varName: node.variable || node.apiName || 'var',
    value: node.expression || node.value || '0',
    type: node.variableType || 'int'
  };
}

/**
 * Get the type of a struct member by traversing the struct definition
 * @param {Object} resource - The struct instance resource
 * @param {string} memberPath - The member path (e.g., "name" or "address.street")
 * @param {Array} allResources - All resources to look up struct definitions
 * @param {string} language - Target language
 * @returns {string} The member type
 */
function getMemberType(resource, memberPath, allResources, language) {
  // Get the struct name
  let structName = resource.structTypeRef;
  if (!structName && resource.dataType) {
    if (resource.dataType.startsWith('struct ')) {
      structName = resource.dataType.replace('struct ', '');
    } else if (resource.dataType.startsWith('struct_')) {
      structName = resource.dataType.replace('struct_', '');
    } else {
      structName = resource.dataType;
    }
  }
  
  // Find the struct definition
  const structDef = allResources.find(r => 
    (r.resourceType === 'struct_type' || r.type === 'STRUCT_TYPE' ||
     r.resourceType === 'union_type' || r.type === 'UNION_TYPE') &&
    (r.name === structName || r.name === `struct_${structName}`)
  );
  
  if (!structDef || !structDef.structMembers) {
    return 'int'; // Default fallback
  }
  
  // Parse the member path
  const parts = memberPath.split('.');
  let currentMembers = structDef.structMembers;
  let memberType = 'int';
  
  for (const part of parts) {
    const member = currentMembers.find(m => m.name === part);
    if (!member) break;

    // Detect nested struct via three paths (matches the picker fix in
    // ResourceForms.jsx StructMemberTree):
    //   (a) explicit memberType === 'struct'
    //   (b) explicit structTypeRef set
    //   (c) dataType like 'struct_address' / 'struct address' that
    //       resolves to a known struct_type in the resource list.
    const dt = typeof member.dataType === 'string' ? member.dataType : '';
    let nestedStructName = null;
    if (member.memberType === 'struct' || member.structTypeRef) {
      nestedStructName = member.structTypeRef || member.structRef
        || (dt.startsWith('struct ') ? dt.replace('struct ', '') : null)
        || (dt.startsWith('struct_') ? dt.replace('struct_', '') : null);
    } else if (dt) {
      const candidate = dt.startsWith('struct ') ? dt.replace('struct ', '')
        : dt.startsWith('struct_') ? dt.replace('struct_', '')
        : dt;
      const found = allResources.find(r =>
        (r.resourceType === 'struct_type' || r.type === 'STRUCT_TYPE' ||
         r.resourceType === 'union_type' || r.type === 'UNION_TYPE')
        && (r.name === candidate || r.name === `struct_${candidate}`)
      );
      if (found) nestedStructName = found.name;
    }

    if (nestedStructName) {
      const nestedStructDef = allResources.find(r =>
        (r.resourceType === 'struct_type' || r.type === 'STRUCT_TYPE' ||
         r.resourceType === 'union_type' || r.type === 'UNION_TYPE') &&
        (r.name === nestedStructName || r.name === `struct_${nestedStructName}`)
      );
      if (nestedStructDef && nestedStructDef.structMembers) {
        currentMembers = nestedStructDef.structMembers;
        memberType = `struct ${nestedStructName}`;
        continue;
      }
    }

    if (member.memberType === 'array' || member.memberType === 'array_1d' || member.memberType === 'array_2d') {
      // For array member, type is the element type with array suffix
      memberType = member.arrayElementType || member.dataType || 'int';
      // If it's a char array, treat it as string for scanf/printf
      if (memberType === 'char') {
        memberType = 'char[]'; // Signal that this is a char array (string)
      }
    } else if (member.memberType === 'pointer') {
      memberType = member.pointsToType || 'int';
    } else {
      // No explicit memberType. Recognise bracketed array shapes like
      // 'char[10]' / 'char [10]' / 'char[]' so scanf chooses %s + no &.
      if (/char\s*\[/.test(dt)) {
        memberType = 'char[]';
      } else {
        memberType = dt || 'int';
      }
    }
  }

  return memberType;
}

/**
 * Check if a resource is a struct instance
 */
function isStructInstance(resource) {
  const resourceType = resource.resourceType || resource.type;
  if (resourceType === 'struct_instance' || resourceType === 'STRUCT_INSTANCE') return true;
  // ADO #406 — a union instance shares the struct member-access path (it also
  // carries a structTypeRef pointing at its UNION_TYPE), so it is "struct-like"
  // for member traversal (u.field). Only the top-level declaration differs.
  if (resourceType === 'union_instance' || resourceType === 'UNION_INSTANCE') return true;
  if (resource.structTypeRef) return true;
  if (resource.dataType?.startsWith('struct ') || resource.dataType?.startsWith('struct_')) return true;
  return false;
}

/**
 * Get struct type name from a struct instance resource
 */
function getStructTypeName(resource) {
  if (resource.structTypeRef) return resource.structTypeRef;
  if (resource.dataType?.startsWith('struct ')) return resource.dataType.replace('struct ', '');
  if (resource.dataType?.startsWith('struct_')) return resource.dataType.replace('struct_', '');
  return resource.dataType;
}

/**
 * Get struct definition by name from resources
 */
function getStructDefinition(structName, allResources) {
  if (!structName) return null;
  
  let found = allResources.find(r => 
    (r.resourceType === 'struct_type' || r.type === 'STRUCT_TYPE' ||
     r.resourceType === 'union_type' || r.type === 'UNION_TYPE') &&
    r.name === structName
  );
  if (!found) {
    found = allResources.find(r => 
      (r.resourceType === 'struct_type' || r.type === 'STRUCT_TYPE' ||
     r.resourceType === 'union_type' || r.type === 'UNION_TYPE') &&
      r.name === `struct_${structName}`
    );
  }
  if (!found && structName.startsWith('struct_')) {
    const baseName = structName.replace('struct_', '');
    found = allResources.find(r => 
      (r.resourceType === 'struct_type' || r.type === 'STRUCT_TYPE' ||
     r.resourceType === 'union_type' || r.type === 'UNION_TYPE') &&
      r.name === baseName
    );
  }
  return found;
}

/**
 * Generate scanf calls for all primitive members of a struct instance
 * Recursively handles nested structs
 * @returns {Array<{path: string, type: string}>} Array of {path, type} for each primitive member
 */
function getStructPrimitiveMembers(structDef, basePath, allResources, visited = new Set()) {
  const results = [];
  
  if (!structDef || !structDef.structMembers) return results;
  
  // Prevent infinite recursion with self-referencing structs
  const structKey = structDef.name || structDef.id;
  if (visited.has(structKey)) return results;
  visited.add(structKey);
  
  for (const member of structDef.structMembers) {
    const memberPath = `${basePath}.${member.name}`;
    const memberType = member.dataType || 'int';
    
    // Check if this member is a nested struct (three detection paths
    // so members saved without explicit memberType but with
    // dataType='struct_address' still drill in — matches the picker
    // fix in ResourceForms.jsx StructMemberTree).
    let nestedStructName = null;
    if (member.memberType === 'struct' || member.structTypeRef) {
      nestedStructName = member.structTypeRef || member.structRef
        || (memberType.startsWith('struct ') ? memberType.replace('struct ', '') : null)
        || (memberType.startsWith('struct_') ? memberType.replace('struct_', '') : null);
    } else if (typeof memberType === 'string') {
      const candidate = memberType.startsWith('struct ') ? memberType.replace('struct ', '')
        : memberType.startsWith('struct_') ? memberType.replace('struct_', '')
        : memberType;
      const probe = getStructDefinition(candidate, allResources);
      if (probe) nestedStructName = probe.name;
    }
    const isNestedStruct = !!nestedStructName;

    if (isNestedStruct) {
      const nestedStructDef = getStructDefinition(nestedStructName, allResources);
      if (nestedStructDef) {
        // Recursively get primitive members of nested struct
        const nestedMembers = getStructPrimitiveMembers(nestedStructDef, memberPath, allResources, visited);
        results.push(...nestedMembers);
      }
    } else {
      // This is a primitive member - determine the correct type for scanf
      let scanfType = memberType;

      // Handle char arrays - they should use %s
      if (member.memberType === 'array' && member.arrayElementType === 'char') {
        scanfType = 'char[]';
      } else if (typeof memberType === 'string' && /char\s*\[/.test(memberType)) {
        // 'char[30]' / 'char [30]' / 'char[]' — all string-buffer shapes.
        scanfType = 'char[]';
      }

      results.push({ path: memberPath, type: scanfType });
    }
  }
  
  return results;
}

/**
 * Generate declaration code for a resource based on its type
 * Handles all resource types: variable, constant, formula, arrays, pointers, structs, etc.
 */
export function generateResourceDeclaration(resource, lang, language, resources = []) {
  const name = resource.name;
  const resourceType = resource.resourceType || resource.type;
  const dataType = resource.dataType || resource.elementType || 'int';
  // ADO #432 follow-up — a string variable's initial value is entered BARE in the
  // resource panel (`hello`, not `"hello"`), mirroring how an assignment direct
  // value works (ADO #375). Auto-quote it here so the student never types quotes,
  // consistently across every track. needsStringQuotes() only quotes a genuine
  // literal — it leaves numbers, booleans, None/null/NULL, already-quoted values,
  // and references to other declared resources alone (the "Search variables…"
  // popover stores a picked variable's bare name, which must stay unquoted).
  const isStringDecl = (() => {
    // Confine auto-quoting to scalar variable/constant declarations — array /
    // pointer / struct initializers are shaped differently and must not be
    // wrapped in quotes.
    const rt = String(resourceType || '').toLowerCase();
    if (rt !== 'variable' && rt !== 'constant') return false;
    const dt = String(dataType).toLowerCase().trim();
    return dt === 'str' || dt === 'string'
      || (/\bchar\b/.test(dt) && (/\[/.test(dt) || /\*/.test(dt)));
  })();
  const initialValue = (() => {
    const raw0 = resource.initialValue || resource.defaultValue;
    if (raw0 === undefined || raw0 === null || raw0 === '') return raw0;
    if (!isStringDecl) return raw0;
    const raw = String(raw0);
    // Already a quoted literal → leave as-is.
    if ((raw.startsWith('"') && raw.endsWith('"')) || (raw.startsWith("'") && raw.endsWith("'"))) return raw;
    // Null-ish placeholder for an uninitialised string → leave as-is.
    if (['null', 'none', 'nil', 'nullptr'].includes(raw.toLowerCase())) return raw;
    // A reference to another declared variable (picked from the "Search
    // variables…" popover) → keep bare so it stays a variable, not a literal.
    const isRef = /^[A-Za-z_]\w*(\.[A-Za-z_]\w*)*$/.test(raw)
      && resources.some(r => r.name === raw || raw.startsWith(r.name + '.'));
    if (isRef) return raw;
    // An expression (concatenation, call, index) → leave bare; don't wrap a
    // whole expression in quotes. Plain text — even multi-word or numeric —
    // is the literal the student typed, so quote it.
    if (/[+*/()[\]<>=&|]/.test(raw)) return raw;
    return `"${raw}"`;
  })();
  
  // ADO #441 — a Python class attribute is NOT a module global or a class-body
  // declaration: it's created via `self.attr = …` inside `__init__`. So a
  // classField resource emits nothing here on the Python track (it still lives
  // in the resource list so pySelfRef can resolve `self.attr`).
  if (language === 'python' && resource.location === 'classField') {
    return '';
  }

  // Map type to language-specific type
  const getMappedType = (type) => {
    if (language === 'c') return mapToCType(type);
    if (language === 'cpp') return mapToCppType(type);
    if (language === 'java') return mapToJavaType(type);
    return type;
  };

  // ADO #410 — C/C++ storage-class prefix for variables/constants/arrays.
  // `storageClass` is a C-specific field (distinct from Java's classField
  // `isStatic`): 'static' → internal linkage / persistent local; 'extern' → a
  // declaration of a symbol defined elsewhere (emitted WITHOUT an initializer).
  // `register` is intentionally omitted (invalid at file scope, ignored by
  // modern compilers). Empty for non-C tracks so existing output is unchanged.
  const cStorage = (language === 'c' || language === 'cpp')
    ? (resource.storageClass === 'static' ? 'static '
      : resource.storageClass === 'extern' ? 'extern ' : '')
    : '';
  const isExternDecl = cStorage === 'extern ';

  switch (resourceType) {
    case 'variable':
    case 'VARIABLE': {
      const mappedType = getMappedType(dataType);
      
      // Handle Java class fields with access specifiers and modifiers
      if (language === 'java' && resource.location === 'classField') {
        const accessSpec = resource.accessSpecifier === 'package' ? '' : (resource.accessSpecifier || 'private');
        const staticMod = resource.isStatic ? 'static ' : '';
        const finalMod = resource.isFinal ? 'final ' : '';
        const prefix = accessSpec ? `${accessSpec} ` : '';
        if (initialValue !== undefined && initialValue !== '') {
          return `${prefix}${staticMod}${finalMod}${mappedType} ${name} = ${initialValue};`;
        }
        return `${prefix}${staticMod}${finalMod}${mappedType} ${name};`;
      }
      
      // Python: no type declarations, just assignment
      if (language === 'python') {
        if (initialValue !== undefined && initialValue !== '') {
          return `${name} = ${initialValue}`;
        }
        return `${name} = None  # ${dataType}`;
      }
      
      // JavaScript: use let/const
      if (language === 'javascript') {
        if (initialValue !== undefined && initialValue !== '') {
          return `let ${name} = ${initialValue};`;
        }
        return `let ${name};`;
      }
      
      // C/C++: string types need char buffer, not pointer (char* to read-only literal segfaults on scanf)
      if ((language === 'c' || language === 'cpp') && (mappedType === 'char*' || mappedType === 'char[]')) {
        const bufSize = resource.size || resource.length || 256;
        if (initialValue !== undefined && initialValue !== '' && !isExternDecl) {
          return `${cStorage}char ${name}[${bufSize}] = ${initialValue};`;
        }
        return `${cStorage}char ${name}[${bufSize}];`;
      }

      // Java (local), Pseudocode, C/C++ non-string. cStorage is '' for non-C
      // tracks; an `extern` declaration drops the initializer (defined elsewhere).
      if (initialValue !== undefined && initialValue !== '' && !isExternDecl) {
        return `${cStorage}${mappedType} ${name} = ${initialValue};`;
      }
      return `${cStorage}${mappedType} ${name};`;
    }
    
    case 'constant':
    case 'CONSTANT': {
      const mappedType = getMappedType(dataType);
      const value = initialValue || '0';
      if (language === 'c' || language === 'cpp') {
        // `extern const int x;` is a declaration (no initializer); `static
        // const` / plain keep the value.
        if (isExternDecl) return `extern const ${mappedType} ${name};`;
        return `${cStorage}const ${mappedType} ${name} = ${value};`;
      } else if (language === 'java') {
        // Handle Java class field constants
        if (resource.location === 'classField') {
          const accessSpec = resource.accessSpecifier === 'package' ? '' : (resource.accessSpecifier || 'private');
          const staticMod = resource.isStatic ? 'static ' : '';
          const prefix = accessSpec ? `${accessSpec} ` : '';
          return `${prefix}${staticMod}final ${mappedType} ${name} = ${value};`;
        }
        return `final ${mappedType} ${name} = ${value};`;
      } else if (language === 'javascript') {
        return `const ${name} = ${value};`;
      } else if (language === 'python') {
        return `${name.toUpperCase()} = ${value}  # constant`;
      }
      return `CONSTANT ${name} = ${value}`;
    }

    // ADO #396 — preprocessor object-like macro. Emitted verbatim with NO type
    // and NO trailing semicolon, so (unlike `const int`) it is a compile-time
    // token usable as a file-scope array size: `#define HT_SIZE 4096`. The
    // value round-trips in `initialValue` (int / string / char / expression).
    case 'macro':
    case 'MACRO': {
      const raw = (initialValue === undefined || initialValue === null) ? '' : String(initialValue).trim();
      // ADO #397 — function-like macro: `#define NAME(a, b) ((a) op (b))`.
      const isFunctionMacro = resource.macroKind === 'function';
      // ADO #398 — flag macro: a valueless `#define DEBUG` (a compile-time
      // on/off switch, typically paired with #ifdef).
      const isFlagMacro = resource.macroKind === 'flag';
      if (language === 'c' || language === 'cpp') {
        if (isFlagMacro) return `#define ${name}`;
        if (isFunctionMacro) {
          const params = String(resource.macroParams || '')
            .split(',').map((p) => p.trim()).filter(Boolean);
          const body = buildFunctionMacroBody(raw, params);
          return `#define ${name}(${params.join(', ')}) ${body}`;
        }
        return raw === '' ? `#define ${name}` : `#define ${name} ${raw}`;
      }
      // Flag macros have no cross-track analogue — emit nothing off-C.
      if (isFlagMacro) return '';
      // Other tracks have no preprocessor — fall back to a typed constant so
      // a macro authored on C and viewed cross-track still yields valid code.
      // (Function-like macros have no clean cross-track analogue; emit the
      // body as a comment-free constant best-effort.)
      const mappedType = getMappedType(dataType || 'int');
      const value = raw || '0';
      if (language === 'java') return `final ${mappedType} ${name} = ${value};`;
      if (language === 'javascript') return `const ${name} = ${value};`;
      if (language === 'python') return `${name.toUpperCase()} = ${value}  # constant`;
      return `CONSTANT ${name} = ${value}`;
    }

    case 'formula':
    case 'FORMULA': {
      // Formula is an expression alias — no declaration generated.
      // The expression is inlined wherever the formula name is referenced.
      return '';
    }
    
    case 'array_1d':
    case 'ARRAY':
    case 'ARRAY_1D': {
      const elementType = getMappedType(resource.elementType || dataType);
      const size = resource.size || 10;
      const hasInitialValues = resource.showInitialValues && resource.initialValues && resource.initialValues.length > 0;
      
      if (language === 'c' || language === 'cpp') {
        if (hasInitialValues && !isExternDecl) {
          const values = resource.initialValues.map(v => v || '0').join(', ');
          return `${cStorage}${elementType} ${name}[${size}] = {${values}};`;
        }
        return `${cStorage}${elementType} ${name}[${size}];`;
      } else if (language === 'java') {
        // Handle Java class field arrays
        if (resource.location === 'classField') {
          const accessSpec = resource.accessSpecifier === 'package' ? '' : (resource.accessSpecifier || 'private');
          const staticMod = resource.isStatic ? 'static ' : '';
          const finalMod = resource.isFinal ? 'final ' : '';
          const prefix = accessSpec ? `${accessSpec} ` : '';
          if (hasInitialValues) {
            const values = resource.initialValues.map(v => v || '0').join(', ');
            return `${prefix}${staticMod}${finalMod}${elementType}[] ${name} = {${values}};`;
          }
          return `${prefix}${staticMod}${finalMod}${elementType}[] ${name} = new ${elementType}[${size}];`;
        }
        if (hasInitialValues) {
          const values = resource.initialValues.map(v => v || '0').join(', ');
          return `${elementType}[] ${name} = {${values}};`;
        }
        return `${elementType}[] ${name} = new ${elementType}[${size}];`;
      } else if (language === 'javascript') {
        if (hasInitialValues) {
          const values = resource.initialValues.map(v => v || '0').join(', ');
          return `let ${name} = [${values}];`;
        }
        return `let ${name} = new Array(${size});`;
      } else if (language === 'python') {
        if (hasInitialValues) {
          const values = resource.initialValues.map(v => v || '0').join(', ');
          return `${name} = [${values}]`;
        }
        return `${name} = [None] * ${size}`;
      }
      return `ARRAY ${name}[${size}] OF ${elementType}`;
    }
    
    case 'array_2d':
    case 'ARRAY_2D': {
      const elementType = getMappedType(resource.elementType || dataType);
      const rows = resource.rows || 3;
      const cols = resource.cols || 3;
      const hasInitialValues = resource.showInitialValues && resource.initialValues2D && resource.initialValues2D.length > 0;
      
      if (language === 'c' || language === 'cpp') {
        if (hasInitialValues && !isExternDecl) {
          const rowStrs = resource.initialValues2D.map(row =>
            `{${(row || []).map(v => v || '0').join(', ')}}`
          );
          return `${cStorage}${elementType} ${name}[${rows}][${cols}] = {${rowStrs.join(', ')}};`;
        }
        return `${cStorage}${elementType} ${name}[${rows}][${cols}];`;
      } else if (language === 'java') {
        // Handle Java class field 2D arrays
        if (resource.location === 'classField') {
          const accessSpec = resource.accessSpecifier === 'package' ? '' : (resource.accessSpecifier || 'private');
          const staticMod = resource.isStatic ? 'static ' : '';
          const finalMod = resource.isFinal ? 'final ' : '';
          const prefix = accessSpec ? `${accessSpec} ` : '';
          if (hasInitialValues) {
            const rowStrs = resource.initialValues2D.map(row => 
              `{${(row || []).map(v => v || '0').join(', ')}}`
            );
            return `${prefix}${staticMod}${finalMod}${elementType}[][] ${name} = {${rowStrs.join(', ')}};`;
          }
          return `${prefix}${staticMod}${finalMod}${elementType}[][] ${name} = new ${elementType}[${rows}][${cols}];`;
        }
        if (hasInitialValues) {
          const rowStrs = resource.initialValues2D.map(row => 
            `{${(row || []).map(v => v || '0').join(', ')}}`
          );
          return `${elementType}[][] ${name} = {${rowStrs.join(', ')}};`;
        }
        return `${elementType}[][] ${name} = new ${elementType}[${rows}][${cols}];`;
      } else if (language === 'javascript') {
        if (hasInitialValues) {
          const rowStrs = resource.initialValues2D.map(row => 
            `[${(row || []).map(v => v || '0').join(', ')}]`
          );
          return `let ${name} = [${rowStrs.join(', ')}];`;
        }
        return `let ${name} = Array.from({length: ${rows}}, () => new Array(${cols}));`;
      } else if (language === 'python') {
        if (hasInitialValues) {
          const rowStrs = resource.initialValues2D.map(row => 
            `[${(row || []).map(v => v || '0').join(', ')}]`
          );
          return `${name} = [${rowStrs.join(', ')}]`;
        }
        return `${name} = [[None] * ${cols} for _ in range(${rows})]`;
      }
      return `ARRAY ${name}[${rows}][${cols}] OF ${elementType}`;
    }
    
    case 'pointer':
    case 'POINTER': {
      const pointsToType = getMappedType(resource.pointsToType || 'int');
      // ADO #292 — honour resource.initialValue when the student typed one
      // in the PointerForm (e.g. `&n1`). Blank → language-specific null
      // (NULL for C, nullptr for C++) so existing behaviour is preserved.
      const userInit = (resource.initialValue || '').trim();
      if (language === 'c') {
        const init = userInit || 'NULL';
        return `${pointsToType}* ${name} = ${init};`;
      }
      if (language === 'cpp') {
        const init = userInit || 'nullptr';
        return `${pointsToType}* ${name} = ${init};`;
      }
      // Pointers are C/C++-specific, fallback for other languages
      return `// Pointer: ${pointsToType}* ${name}`;
    }
    
    case 'array_of_pointers':
    case 'ARRAY_OF_POINTERS': {
      const pointsToType = getMappedType(resource.pointsToType || 'int');
      const size = resource.size || 10;
      if (language === 'c' || language === 'cpp') {
        return `${pointsToType}* ${name}[${size}];`;
      }
      return `// Array of pointers: ${pointsToType}* ${name}[${size}]`;
    }
    
    // ADO #406 — a union type definition is identical to a struct's except for
    // the `union` keyword (and C's all-members-share-one-address semantics,
    // which the compiler enforces — codegen just emits the members). Reuses the
    // STRUCT_TYPE member-emission below via the shared `aggregateKeyword`.
    case 'union_type':
    case 'UNION_TYPE':
    case 'struct_type':
    case 'STRUCT_TYPE': {
      // Generate struct / union type definition (C/C++)
      // Returns multi-line string WITHOUT base indentation - caller adds that
      // Members are indented with 4 additional spaces relative to the keyword
      const aggregateKeyword =
        (resourceType === 'union_type' || resourceType === 'UNION_TYPE') ? 'union' : 'struct';
      if ((language === 'c' || language === 'cpp') && resource.structMembers && resource.structMembers.length > 0) {
        let structCode = `${aggregateKeyword} ${name} {\n`;
        resource.structMembers.forEach(member => {
          // Use 4 spaces for member indentation (relative to struct)
          const memberIndent = '    ';
          if (member.memberType === 'array' || member.memberType === 'array_1d') {
            // Array member - use arrayElementType or dataType
            const elementType = getMappedType(member.arrayElementType || member.dataType || 'int');
            structCode += `${memberIndent}${elementType} ${member.name}[${member.arraySize || 10}];\n`;
          } else if (member.memberType === 'array_2d') {
            const elementType = getMappedType(member.arrayElementType || member.dataType || 'int');
            structCode += `${memberIndent}${elementType} ${member.name}[${member.arrayRows || 3}][${member.arrayCols || 3}];\n`;
          } else if (member.memberType === 'pointer' || member.memberType === 'array_of_pointers') {
            const ptrType = getMappedType(member.pointsToType || 'int');
            structCode += `${memberIndent}${ptrType}* ${member.name};\n`;
          } else if (member.memberType === 'struct') {
            // Nested struct member
            const structRef = member.structTypeRef || member.structRef || 'UnknownStruct';
            structCode += `${memberIndent}struct ${structRef} ${member.name};\n`;
          } else {
            // Scalar member
            const memberType = getMappedType(member.dataType || 'int');
            // ADO #410 — bit-field: `unsigned int flags : 3;`. Emitted when the
            // member carries a positive integer bit width. Only meaningful on an
            // integer scalar member (C allows int/unsigned/_Bool); the form
            // offers it on scalar members and the student picks an int type.
            const bits = parseInt(member.bitWidth, 10);
            if (Number.isInteger(bits) && bits > 0) {
              structCode += `${memberIndent}${memberType} ${member.name} : ${bits};\n`;
            } else {
              structCode += `${memberIndent}${memberType} ${member.name};\n`;
            }
          }
        });
        structCode += `}`; // Closing brace at same level as opening
        return structCode;
      }
      return `// ${aggregateKeyword === 'union' ? 'Union' : 'Struct'} type: ${name}`;
    }

    case 'struct_instance':
    case 'STRUCT_INSTANCE': {
      const structType = resource.structTypeRef || 'UnknownStruct';
      // An initialiser at the point of declaration — `struct addr a = {…};`.
      //
      // The ARRAY of structures has always emitted one (the array_1d branch
      // above, via showInitialValues), and a single instance silently dropped
      // it: the resource carried `initialValue`, nothing read it, and the flow
      // printed uninitialised memory with no error anywhere. An array of
      // structures could be initialised and one structure could not, which is
      // exactly the wrong way round for teaching — the single case is the one a
      // learner meets first.
      //
      // Deliberately NOT validated or reformatted here. A struct initialiser is
      // a brace list whose shape depends on the type's members, and this
      // function has no view of the type; the compiler is the authority and its
      // error is clearer than anything guessed here.
      //
      // The storage class too (static / extern), as every variable and array
      // already has. Without it a Private structure in a multi-file project came
      // out with external linkage: mini-Postgres's parser-only `struct Words
      // cmd` was reachable from any file (found reading S11's C, 2026-10-02).
      // An extern declaration carries no initialiser.
      const init = isExternDecl ? '' : (resource.initialValue || '').trim();
      const suffix = init ? ` = ${init}` : '';
      if (language === 'c') {
        return `${cStorage}struct ${structType} ${name}${suffix};`;
      }
      if (language === 'cpp') {
        return `${cStorage}${structType} ${name}${suffix};`;
      }
      return `// Struct instance: ${structType} ${name}`;
    }

    // ADO #406 — union instance. Mirrors STRUCT_INSTANCE but emits the `union`
    // tag. C requires `union Tag name;`; C++ allows the bare tag (`Tag name;`).
    case 'union_instance':
    case 'UNION_INSTANCE': {
      const unionType = resource.structTypeRef || resource.unionTypeRef || 'UnknownUnion';
      // Same initialiser treatment as STRUCT_INSTANCE above, and for the same
      // reason — a union instance dropped `initialValue` identically. Fixed in
      // the same pass rather than left to be rediscovered from the other side
      // of the same module.
      //
      // A union's brace initialiser sets its FIRST member, which is a real
      // lesson rather than a quirk: there is only one space, so there is only
      // one thing to initialise.
      const uInit = isExternDecl ? '' : (resource.initialValue || '').trim();
      const uSuffix = uInit ? ` = ${uInit}` : '';
      if (language === 'c') {
        return `${cStorage}union ${unionType} ${name}${uSuffix};`;
      }
      if (language === 'cpp') {
        return `${cStorage}${unionType} ${name}${uSuffix};`;
      }
      return `// Union instance: ${unionType} ${name}`;
    }

    // ADO #305 — Java class-instance resource. Declaration only; the
    // Instantiate node (ADO #303) writes the `new Dog(...)` value later.
    //   local                                 → `Dog d;`
    //   classField (instance, non-static)     → `private Dog d;`
    //   classField (static)                   → `private static Dog d;`
    // Access specifier and `final` mirror the existing variable / array
    // classField handling for consistency. We deliberately do NOT emit an
    // initialiser — Java initialises object fields to null automatically,
    // and locals stay uninitialised until the Instantiate node writes them.
    case 'class_instance':
    case 'CLASS_INSTANCE': {
      const className = resource.classTypeRef || resource.dataType || 'UnknownClass';
      if (language === 'java') {
        if (resource.location === 'classField') {
          const accessSpec = resource.accessSpecifier === 'package' ? '' : (resource.accessSpecifier || 'private');
          const staticMod = resource.isStatic ? 'static ' : '';
          const finalMod = resource.isFinal ? 'final ' : '';
          const prefix = accessSpec ? `${accessSpec} ` : '';
          return `${prefix}${staticMod}${finalMod}${className} ${name};`;
        }
        return `${className} ${name};`;
      }
      return `// Class instance: ${className} ${name}`;
    }

    // ADO #296 — enum support for C / C++.
    //
    // ENUM_TYPE emits a typedef-style declaration so students can write
    // `Day d` not `enum Day d`:
    //
    //     typedef enum {
    //         SUNDAY = 0,
    //         MONDAY,
    //         TUESDAY,
    //         ...
    //     } Day;
    //
    // Member values are emitted only when the student typed an explicit
    // integer; blank values let C auto-increment from the previous
    // member (or 0 for the first). Returns multi-line WITHOUT the
    // trailing `;` — the caller's globalResources loop adds it (matches
    // struct_type behaviour, see codeGenerator.js:3267-3283 / 4556-4569 /
    // 3890-3910 for the three call sites).
    case 'enum_type':
    case 'ENUM_TYPE': {
      if ((language === 'c' || language === 'cpp')
          && resource.enumMembers && resource.enumMembers.length > 0) {
        let enumCode = `typedef enum {\n`;
        const members = resource.enumMembers;
        members.forEach((m, i) => {
          const mName = (m.name || '').trim() || `MEMBER_${i}`;
          const explicit = (m.value || '').trim();
          const tail = i < members.length - 1 ? ',' : '';
          enumCode += explicit
            ? `    ${mName} = ${explicit}${tail}\n`
            : `    ${mName}${tail}\n`;
        });
        enumCode += `} ${name}`;
        return enumCode;
      }
      return `// Enum type: ${name}`;
    }

    // ADO #410 — general typedef alias: `typedef unsigned int u32;`. Unlike the
    // multi-line struct/enum type defs (whose trailing `;` is added by the
    // global-emission loop's `includes('\n')` branch), a typedef is a single
    // line, so it carries its OWN `;`. The multi-file assembler only appends a
    // `;` when one is missing, so this won't double up.
    case 'typedef':
    case 'TYPEDEF': {
      if ((language === 'c' || language === 'cpp') && resource.underlyingType) {
        return `typedef ${getMappedType(resource.underlyingType)} ${name};`;
      }
      return `// Typedef: ${name}`;
    }

    // ADO #407 — function pointer, emitted as a TYPEDEF so the name is a real C
    // type: `typedef int (*BinaryOp)(int, int);`. A typedef (not a bare variable
    // declarator) is what makes the pointer usable everywhere a type is — as a
    // variable (`BinaryOp op = add;`), a parameter (`void apply(BinaryOp f)`),
    // and an array element (dispatch tables). mapToCType passes the alias name
    // through verbatim (ADO #410), so those all compile. The `*` sits INSIDE the
    // parentheses wrapping the name; an empty parameter list → `(void)`.
    case 'function_pointer':
    case 'FUNCTION_POINTER': {
      if (language === 'c' || language === 'cpp') {
        const ret = getMappedType(resource.returnType || 'int');
        const params = (resource.paramTypes || []).filter(Boolean);
        const paramList = params.length ? params.map(getMappedType).join(', ') : 'void';
        return `typedef ${ret} (*${name})(${paramList});`;
      }
      return `// Function-pointer type: ${name}`;
    }

    // ENUM_INSTANCE emits a typedef'd variable declaration. Initial
    // value (a member name like SUNDAY) is honoured when present;
    // otherwise the variable is left uninitialised (C semantics —
    // student must assign before reading).
    case 'enum_instance':
    case 'ENUM_INSTANCE': {
      const enumType = resource.enumTypeRef || resource.dataType || 'UnknownEnum';
      const init = (resource.initialValue || '').trim();
      if (language === 'c' || language === 'cpp') {
        return init ? `${enumType} ${name} = ${init};` : `${enumType} ${name};`;
      }
      return `// Enum instance: ${enumType} ${name}`;
    }
    
    case 'dictionary':
    case 'DICTIONARY': {
      const keyType = resource.keyType || 'str';
      const valueType = resource.valueType || 'int';
      if (language === 'python') {
        // ADO #431 — emit `{key: val, …}` from dictEntries (key/value pairs);
        // empty → `{}`. Skip rows with a blank key or value.
        const entries = (resource.dictEntries || [])
          .map(e => {
            const k = pythonLiteral(e.key, keyType);
            const val = pythonLiteral(e.value, valueType);
            return (k === null || val === null) ? null : `${k}: ${val}`;
          })
          .filter(Boolean);
        const body = entries.length ? `{${entries.join(', ')}}` : '{}';
        return `${name} = ${body}  # dict[${keyType}, ${valueType}]`;
      }
      return `// Dictionary: ${name}`;
    }

    case 'set':
    case 'SET': {
      const elementType = resource.elementType || 'int';
      if (language === 'python') {
        // ADO #431 — emit `{a, b, c}` from setElements; empty → `set()` (NOT
        // `{}`, which is an empty dict). Skip blank elements; de-dup literals.
        const els = [...new Set(
          (resource.setElements || [])
            .map(e => pythonLiteral(typeof e === 'object' ? e.value : e, elementType))
            .filter(v => v !== null)
        )];
        const body = els.length ? `{${els.join(', ')}}` : 'set()';
        return `${name} = ${body}  # set of ${elementType}`;
      } else if (language === 'javascript') {
        return `let ${name} = new Set();`;
      }
      return `// Set: ${name}`;
    }

    case 'tuple':
    case 'TUPLE': {
      const elementType = resource.elementType || 'int';
      if (language === 'python') {
        // ADO #433 — emit `(a, b, c)` from tupleElements; empty → `()`. A tuple
        // is ORDERED and allows duplicates, so (unlike a set) preserve order and
        // do NOT de-dup. A single-element tuple needs a trailing comma: `(x,)`.
        const els = (resource.tupleElements || [])
          .map(e => pythonLiteral(typeof e === 'object' ? e.value : e, elementType))
          .filter(v => v !== null);
        let body;
        if (els.length === 0) body = '()';
        else if (els.length === 1) body = `(${els[0]},)`;
        else body = `(${els.join(', ')})`;
        return `${name} = ${body}  # tuple of ${elementType}`;
      } else if (language === 'javascript') {
        // JS has no tuple; an immutable array is the closest idiom.
        return `const ${name} = [];`;
      }
      return `// Tuple: ${name}`;
    }

    case 'object':
    case 'OBJECT': {
      if (language === 'javascript') {
        return `let ${name} = {};`;
      }
      return `// Object: ${name}`;
    }
    
    case 'map':
    case 'MAP': {
      if (language === 'javascript') {
        return `let ${name} = new Map();`;
      }
      return `// Map: ${name}`;
    }
    
    default: {
      // Fallback to simple variable declaration
      const mappedType = getMappedType(dataType);
      return lang.varDeclare(name, mappedType, initialValue);
    }
  }
}

// ============================================
// DEBUG: logStep function definitions per language
// ============================================

// Hard cap on trace steps written to stderr to avoid Judge0 stderr overflow
// (default ~64KB). At ~100 bytes/step, 500 steps ≈ 50KB. After the cap, a
// single sentinel line is written so the parser can flag the trace as
// truncated and downstream consumers (Debug UI, assessment attribution)
// degrade gracefully instead of reporting wrong information.
const TRACE_MAX_STEPS = 500;

function getLogStepDefinition(language) {
  switch (language) {
    case 'c':
      return `
#define TRACE_MAX_STEPS ${TRACE_MAX_STEPS}
static int __trace_count = 0;
void logStep(const char *category, const char *message,
             const char *nodeId, const char *contextValue) {
    if (__trace_count < TRACE_MAX_STEPS) {
        fprintf(stderr, "##STEP##|%s|%s|%s|%s\\n",
            category, message, nodeId,
            contextValue ? contextValue : "N/A");
        fflush(stderr);
        __trace_count++;
    } else if (__trace_count == TRACE_MAX_STEPS) {
        fprintf(stderr, "##STEP##|meta|trace truncated|__truncated__|TRUNCATED:%d\\n", TRACE_MAX_STEPS);
        fflush(stderr);
        __trace_count++;
    }
}

`;
    case 'cpp':
      return `
#include <iostream>
static const int TRACE_MAX_STEPS = ${TRACE_MAX_STEPS};
static int __trace_count = 0;
static void logStep(const std::string& category,
                    const std::string& message,
                    const std::string& nodeId,
                    const std::string& contextValue = "N/A") {
    if (__trace_count < TRACE_MAX_STEPS) {
        std::cerr << "##STEP##|" << category << "|"
                  << message << "|" << nodeId << "|"
                  << contextValue << "\\n";
        std::cerr.flush();
        __trace_count++;
    } else if (__trace_count == TRACE_MAX_STEPS) {
        std::cerr << "##STEP##|meta|trace truncated|__truncated__|TRUNCATED:"
                  << TRACE_MAX_STEPS << "\\n";
        std::cerr.flush();
        __trace_count++;
    }
}

`;
    case 'java':
      return `    private static final int TRACE_MAX_STEPS = ${TRACE_MAX_STEPS};
    private static int __traceCount = 0;
    private static void logStep(String category, String message,
                                   String nodeId, String contextValue) {
        if (__traceCount < TRACE_MAX_STEPS) {
            System.err.println("##STEP##|" + category + "|" +
                message + "|" + nodeId + "|" +
                (contextValue != null ? contextValue : "N/A"));
            System.err.flush();
            __traceCount++;
        } else if (__traceCount == TRACE_MAX_STEPS) {
            System.err.println("##STEP##|meta|trace truncated|__truncated__|TRUNCATED:" + TRACE_MAX_STEPS);
            System.err.flush();
            __traceCount++;
        }
    }

`;
    case 'python':
      return `import sys
TRACE_MAX_STEPS = ${TRACE_MAX_STEPS}
__trace_count = 0
def log_step(category, message, node_id, context_value="N/A"):
    global __trace_count
    if __trace_count < TRACE_MAX_STEPS:
        print(f"##STEP##|{category}|{message}|{node_id}|{context_value}",
              file=sys.stderr, flush=True)
        __trace_count += 1
    elif __trace_count == TRACE_MAX_STEPS:
        print(f"##STEP##|meta|trace truncated|__truncated__|TRUNCATED:{TRACE_MAX_STEPS}",
              file=sys.stderr, flush=True)
        __trace_count += 1

`;
    case 'javascript':
      return `const TRACE_MAX_STEPS = ${TRACE_MAX_STEPS};
let __traceCount = 0;
function logStep(category, message, nodeId, contextValue = 'N/A') {
    if (__traceCount < TRACE_MAX_STEPS) {
        process.stderr.write(
            \`##STEP##|\${category}|\${message}|\${nodeId}|\${contextValue}\\n\`
        );
        __traceCount++;
    } else if (__traceCount === TRACE_MAX_STEPS) {
        process.stderr.write(
            \`##STEP##|meta|trace truncated|__truncated__|TRUNCATED:\${TRACE_MAX_STEPS}\\n\`
        );
        __traceCount++;
    }
}

`;
    default:
      return '';
  }
}

/**
 * Generate a logStep call string for the given language.
 */
/**
 * Resolve variable index brackets for snprintf format strings.
 * [i] → [%d] (adds i to args), [0] → [0] (literal, no arg).
 * Returns { fmt: 'arr[%d].name', idxVars: ['i'] }
 */
function resolveIdxFmt(path) {
  const idxVars = [];
  const fmt = path.replace(/\[(\w+)\]/g, (_m, v) => {
    if (/^\d+$/.test(v)) return `[${v}]`;
    idxVars.push(v);
    return '[%d]';
  });
  return { fmt, idxVars };
}

function getLogStepCall(language, category, message, nodeId, contextExpr) {
  const safeNodeId = (nodeId || '').replace(/"/g, '\\"');
  switch (language) {
    case 'c':
    case 'cpp':
      return `logStep("${category}", "${message}", "${safeNodeId}", ${contextExpr});`;
    case 'java':
      return `logStep("${category}", "${message}", "${safeNodeId}", ${contextExpr});`;
    case 'python':
      // contextExpr should be a Python expression (f-string or variable)
      return `log_step("${category}", "${message}", "${safeNodeId}", ${contextExpr})`;
    case 'javascript':
      return `logStep("${category}", "${message}", "${safeNodeId}", ${contextExpr});`;
    default:
      return '';
  }
}


/**
 * Main code generator function
 * @param {Object} flowData - The flow data object containing nodes and resources
 * @param {string} language - Target language ('c', 'javascript', 'python', 'pseudocode')
 * @returns {string} Generated code
 */
// ADO #211 — check if a branch terminates (last node is End, Return, or
// a fully-terminating Decision). Used to skip unreachable code after
// terminating if-else blocks.
// ADO #304 — Throw is also terminal (control transfers to the catch site).
function _branchTerminates(nodes) {
  if (!nodes || nodes.length === 0) return false;
  const last = nodes[nodes.length - 1];
  if (last.type === 'end' || last.type === 'return' || last.type === 'break' || last.type === 'continue' || last.type === 'throw') return true;
  if (last.type === 'decision' || last.type === 'switch') {
    const outcomes = last.outcomes || [];
    // A switch only fully terminates if it has a default that's also covered.
    const hasDefault = outcomes.some(o => o.isDefault);
    return outcomes.length > 0 && (last.type === 'decision' || hasDefault)
      && outcomes.every(o => _branchTerminates(o.nodes || []));
  }
  return false;
}

/**
 * Resolve the Java file header (import + class/record declaration) using the
 * correct class name and file-class type. Multi-file projects pass these via
 * options; single-file projects fall back to `lang.fileHeader` which still
 * hardcodes `Main`. Non-Java languages return `lang.fileHeader` unchanged.
 *
 * @param {Object} lang                Language config
 * @param {string} className           File name minus `.java`
 * @param {string} [fileClassType]     'class' | 'record' (default 'class')
 * @param {Array}  [recordComponents]  [{ name, type }] — record only
 */
function resolveFileHeader(lang, className, fileClassType, recordComponents, extraImports) {
  if (lang.name === 'Java' && typeof lang.javaFileHeader === 'function' && className) {
    return lang.javaFileHeader(className, fileClassType, recordComponents, extraImports);
  }
  return lang.fileHeader || '';
}

/**
 * ADO #307 — Collect FQCN imports needed for a Java file's class-instance
 * resources. Two sources per resource:
 *   1. resource.classFqcn (set by the Custom… escape hatch in the modal —
 *      explicit student-supplied package wins)
 *   2. resource.classTypeRef or resource.dataType → look up in the curated
 *      JAVA_BUILTIN_PACKAGE_BY_NAME map (StringBuilder → java.lang → null;
 *      ArrayList → java.util.ArrayList; project classes / unknowns → null)
 *
 * Returns a Set of FQCN strings. Caller dedupes against the hardcoded
 * `java.util.Scanner` line inside javaFileHeader.
 */
function collectJavaImports(resources, flows) {
  const imports = new Set();
  if (Array.isArray(resources)) {
    for (const r of resources) {
      if (!r) continue;
      const rt = (r.resourceType || r.type || '').toLowerCase();
      if (rt !== 'class_instance') continue;
      if (r.classFqcn) {
        imports.add(r.classFqcn);
        continue;
      }
      const className = r.classTypeRef || r.dataType;
      const fqcn = className ? resolveJavaImportForClass(className) : null;
      if (fqcn) imports.add(fqcn);
    }
  }
  // ADO #313 — also add imports for built-in checked exceptions referenced
  // either in a method's throws clause (flow.throwsTypes) or by a Throw node
  // somewhere in the flow's body. The `flows` argument is optional for
  // backward compat — single-flow callers (generateCode) pass [flowData],
  // multi-flow callers pass the full flows array.
  if (Array.isArray(flows)) {
    const addExceptionImport = (name) => {
      const fqcn = importFqcnForBuiltinException(name);
      if (fqcn) imports.add(fqcn);
    };
    const walkBody = (nodes) => {
      if (!Array.isArray(nodes)) return;
      for (const n of nodes) {
        if (!n) continue;
        if (n.type === 'throw' && n.exceptionType) {
          addExceptionImport((n.exceptionType || '').trim());
        }
        if (n.outcomes) for (const o of n.outcomes) walkBody(o.nodes);
        if (n.loopBody) walkBody(n.loopBody);
        if (n.afterLast) walkBody(n.afterLast);
        if (n.branches) for (const b of n.branches) walkBody(b.nodes);
      }
    };
    for (const f of flows) {
      if (!f) continue;
      if (Array.isArray(f.throwsTypes)) {
        for (const t of f.throwsTypes) addExceptionImport((t || '').trim());
      }
      walkBody(f.nodes);
    }
  }
  return imports;
}

export function generateCode(flowData, language = 'c', options = {}) {
  const { isDebugMode = false } = options;
  const lang = LANGUAGE_CONFIG[language.toLowerCase()] || LANGUAGE_CONFIG.c;
  const nodes = flowData.nodes || [];
  // ADO #400 — argc/argv participate in type resolution (argv[i] → %s) when
  // the entry flow opts into command-line args.
  const resources = withCommandLineArgs(flowData.resources || [], flowData);

  let code = '';
  let indentLevel = 1;

  const getIndent = () => lang.indent.repeat(indentLevel);

  // Add file header — Java uses the className from options when provided.
  // ADO #231 — fileClassType + recordComponents drive class vs record emission.
  // ADO #307 — extraImports collected from class-instance resources so the
  // header includes `import java.util.ArrayList;` etc. when needed.
  // ADO #313 — also pass [flowData] so the import set includes built-in
  // checked exceptions referenced by a throws clause or a Throw node body.
  const extraImports = collectJavaImports(resources, [flowData]);
  code += resolveFileHeader(lang, options.className, options.fileClassType, options.recordComponents, extraImports);

  // Debug: inject logStep function definition before main
  if (isDebugMode) {
    code += getLogStepDefinition(language);
  }

  // Generate variable declarations from resources
  // ADO #311 — inline-declared class_instance resources (owned by a
  // New Object node) skip the standalone declaration: the merged
  // `ClassName var = new ClassName(args);` is emitted by the
  // instantiate case below.
  const declarableResources = resources.filter((r) => !r._inlineDeclaredByNodeId);
  if (declarableResources.length > 0) {
    code += lang.comment('Variable Declarations') + '\n';
    declarableResources.forEach(resource => {
      const declaration = generateResourceDeclaration(resource, lang, language, resources);
      code += declaration + '\n';
    });
    code += '\n';
  }

  // Add main function start
  code += mainStartFor(lang, language, flowData);

  // Debug: declare __ctx buffer at function scope (C/C++ only)
  if (isDebugMode && (language === 'c' || language === 'cpp')) {
    code += getIndent() + 'char __ctx[256];\n';
  }

  // Debug: logStep for start node
  if (isDebugMode) {
    const startNode = nodes.find(n => n.type === 'start');
    if (startNode) {
      code += getIndent() + getLogStepCall(language, 'Execution', 'Start', startNode.id, '"Program started"') + '\n';
    }
  }

  // Debug: logStep for resources — shows each declared variable with storage size
  if (isDebugMode && resources.length > 0) {
    const declarableRes = resources.filter(r => {
      const rt = r.resourceType || r.type;
      // struct/union/typedef are type defs, not variables
      return rt !== 'struct_type' && rt !== 'union_type' && rt !== 'typedef';
    });
    if (declarableRes.length > 0) {
      const count = declarableRes.length;
      if (language === 'c' || language === 'cpp') {
        const sizeofSum = declarableRes.map(r => `sizeof(${r.name})`).join(' + ');
        code += getIndent() + `snprintf(__ctx, sizeof(__ctx), "${count} variable${count > 1 ? 's' : ''} (%zu bytes total)", (size_t)(${sizeofSum}));\n`;
        code += getIndent() + getLogStepCall(language, 'Setup', 'Resources', '__resources__', '__ctx') + '\n';
      } else if (language === 'java') {
        const jlsSizes = { 'byte': 1, 'boolean': 1, 'short': 2, 'char': 2, 'int': 4, 'float': 4, 'long': 8, 'double': 8 };
        let totalBytes = 0;
        declarableRes.forEach(r => {
          const dt = r.dataType || 'int';
          const bytes = jlsSizes[dt] || 4;
          const rt = r.resourceType || r.type || 'variable';
          if (rt === 'array_1d') totalBytes += bytes * (r.size || r.length || 0);
          else if (rt === 'array_2d') totalBytes += bytes * (r.rows || 0) * (r.cols || 0);
          else totalBytes += bytes;
        });
        code += getIndent() + getLogStepCall(language, 'Setup', 'Resources', '__resources__', `"${count} variable${count > 1 ? 's' : ''} (${totalBytes} bytes total)"`) + '\n';
      } else if (language === 'python') {
        code += getIndent() + getLogStepCall(language, 'Setup', 'Resources', '__resources__', `f"${count} variable${count > 1 ? 's' : ''}"`) + '\n';
      } else if (language === 'javascript') {
        code += getIndent() + getLogStepCall(language, 'Setup', 'Resources', '__resources__', `\`${count} variable${count > 1 ? 's' : ''}\``) + '\n';
      }
    }
  }

  // Process nodes (skip start and end)
  const processableNodes = nodes.filter(n => n.type !== 'start' && n.type !== 'end');

  // ADO #308 — stash flow context for the subflow `this.` decision.
  _currentFlowType = flowData.flowType || (flowData.isEntry ? 'main' : null);
  _currentFlowName = flowData.name || null;
  _currentReturnType = flowData.returnType || null;
  _pyInClassMethod = false;
  _pyMethodParams = new Set();

  // ADO #211 — stop after a fully-terminating decision or return node
  let mainAlreadyReturned = false;
  for (const node of processableNodes) {
    code += generateNodeCode(node, lang, getIndent, () => indentLevel++, () => indentLevel--, resources, language, null, 0, isDebugMode);
    if (node.type === 'return') { mainAlreadyReturned = true; break; }
    if (node.type === 'decision' && _branchTerminates([node])) { mainAlreadyReturned = true; break; }
  }

  // Debug: logStep for end node
  if (isDebugMode) {
    const endNode = nodes.find(n => n.type === 'end');
    if (endNode) {
      code += getIndent() + getLogStepCall(language, 'Execution', 'End', endNode.id, '"Program finished"') + '\n';
    }
  }

  // Add main function end.
  //
  // `mainEnd` carries the automatic `return 0;` for C/C++, because main falls
  // off the end otherwise. But main declares a return type, so a student adding
  // their own Return node to hand back 0 is doing something correct and the
  // palette allows it — and appending the automatic one after theirs emitted
  // `return 0;` twice in a row. Compiles fine, reads like a mistake, and the
  // generated code is the artefact Code Next teaches from.
  code += (mainAlreadyReturned && lang.mainEndAfterReturn) ? lang.mainEndAfterReturn : lang.mainEnd;

  return code;
}

/**
 * Generate code for a single node
 * @param {Object} resources - Available resources for type lookup
 * @param {string} language - Target language
 */
function generateNodeCode(node, lang, getIndent, increaseIndent, decreaseIndent, resources = [], language = 'c', nodeLineMap = null, baseLineNum = 0, isDebugMode = false, withNodeComments = false) {
  let code = '';
  const indent = getIndent();
  const countLines = (str) => (str.match(/\n/g) || []).length;

  // Helper: generate a child node with optional comment + line mapping
  const generateChild = (childNode) => {
    let commentAdded = false;
    if (withNodeComments && childNode.type) {
      const comment = getNodeComment(childNode, resources);
      if (comment) {
        code += `${getIndent()}${lang.comment(comment)}\n`;
        commentAdded = true;
      }
    }
    const childStartLine = nodeLineMap ? baseLineNum + countLines(code) : 0;
    code += generateNodeCode(childNode, lang, getIndent, increaseIndent, decreaseIndent, resources, language, nodeLineMap, childStartLine, isDebugMode, withNodeComments);
    if (nodeLineMap && childNode.id) {
      const childEndLine = baseLineNum + countLines(code) - 1;
      const effectiveStart = commentAdded ? childStartLine - 1 : childStartLine;
      nodeLineMap[childNode.id] = { startLine: effectiveStart, endLine: Math.max(childEndLine, effectiveStart) };
    }
  };

  // Helper to look up variable type from resources
  const getVarType = (varName) => {
    const resource = resources.find(r => r.name === varName);
    if (!resource) return 'int';
    const resType = resource.resourceType || resource.type;
    const dt = resource.dataType || resource.type || 'int';
    // char array_1d is a string, not a single char
    if (resType === 'array_1d' && dt === 'char') return 'char[]';
    // ADO #409 — an enum instance resolves to its enum type name (e.g. Day).
    // Downstream specifier lookups don't know that name and fall back to %d,
    // which is correct for C (enums are ints).
    if (resType === 'enum_instance' || resType === 'ENUM_INSTANCE') {
      return resource.enumTypeRef || resource.dataType || 'int';
    }
    // ADO #406 — a union instance resolves to its union type name; downstream
    // specifier lookups don't recognise it and fall back to %d, but printf/scanf
    // of a whole union is meaningless anyway — students read a member (u.field),
    // which resolves through getMemberType instead.
    if (resType === 'union_instance' || resType === 'UNION_INSTANCE') {
      return resource.structTypeRef || resource.unionTypeRef || resource.dataType || 'int';
    }
    return dt;
  };
  
  // Helper to check if variable is already declared in resources
  const isVarDeclared = (varName) => {
    return resources.some(r => r.name === varName);
  };

  /**
   * Resolve the data type for ANY variable path. Handles all edge cases:
   * - Simple variables: age → int, height → float
   * - Char arrays (1D): str → char[] (for %s)
   * - Struct members: s1.name → char[] (via getMemberType traversal)
   * - Nested struct members: person.address.city → char[]
   * - Array indexed vars: nos[i] → int (strips index, looks up base)
   * - 2D array indexed: twoD[i][j] → int
   * - Struct array members: student.grades → char[]
   * - Unknown/fallback: → int
   */
  const resolveVarType = (fullPath) => {
    if (!fullPath) return 'int';

    // THROUGH A POINTER. `*p` and `p->member` were not handled at all, so both
    // fell through to the 'int' default — and a char shown through a pointer
    // printed its CODE while the same char shown by name printed the letter:
    //
    //     by name A through pointer 65
    //
    // Same value, two routes, two answers, and the second looks like the
    // learner's mistake. A pointer's row already records what it points to;
    // this reads it.
    const deref = /^\s*\*\s*([A-Za-z_]\w*)\s*$/.exec(fullPath);
    if (deref) {
      const pr = resources.find((r) => r.name === deref[1]);
      if (pr) {
        return (pr.pointsToType
                || String(pr.dataType || '').replace(/\s*\*\s*$/, '')
                || 'int');
      }
    }
    const arrow = /^\s*([A-Za-z_]\w*)\s*->\s*(.+)$/.exec(fullPath);
    if (arrow) {
      const pr = resources.find((r) => r.name === arrow[1]);
      if (pr) {
        const pointee = (pr.pointsToType
                         || String(pr.dataType || '').replace(/\s*\*\s*$/, ''));
        // Resolve the member against the struct the pointer points AT, by
        // borrowing the type it names — the struct_instance path below already
        // knows how to read a member, it just needs something to read it from.
        const asInstance = resources.find(
          (r) => isStructInstance(r)
            && (r.structTypeRef === String(pointee).replace(/^struct\s+/, '')
                || r.dataType === pointee));
        if (asInstance) {
          return getMemberType(asInstance, arrow[2], resources, language);
        }
      }
    }

    // Struct member path: s1.name, person.address.city
    if (fullPath.includes('.')) {
      const dotIdx = fullPath.indexOf('.');
      const baseName = fullPath.substring(0, dotIdx).replace(/\[.*\]/g, '');
      const memberPath = fullPath.substring(dotIdx + 1);
      const baseRes = resources.find(r => r.name === baseName);
      if (baseRes && isStructInstance(baseRes)) {
        return getMemberType(baseRes, memberPath, resources, language);
      }
    }
    // Strip array indices: nos[i] → nos, twoD[i][j] → twoD
    const bareName = fullPath.replace(/\[.*\]/g, '');
    // ONE index into a grid of characters is a whole ROW: a piece of text.
    // `words[k]` in a list of words was shown with %d — the row's address —
    // because the indices were stripped and the element type (char) won.
    const grid = resources.find((r) => r.name === bareName);
    if (grid && (grid.resourceType || grid.type) === 'array_2d'
        && (grid.elementType || grid.dataType) === 'char'
        && (fullPath.match(/\[[^\]]*\]/g) || []).length === 1) {
      return 'char[]';
    }
    // Input bugs (epic #581) — ONE index into a char array is one character.
    // getVarType answers 'char[]' for the whole array (a string), so the debug
    // trace after an Input of `word[0]` logged it with %s: a char passed where
    // a char * was expected.
    if (grid && ['array_1d', 'array'].includes(String(grid.resourceType || grid.type || '').toLowerCase())
        && (grid.elementType || grid.dataType) === 'char'
        && (fullPath.match(/\[[^\]]*\]/g) || []).length === 1) {
      return 'char';
    }
    return getVarType(bareName);
  };

  /**
   * Replace every formula name in an expression string with its expression.
   * Wraps substituted expressions in () to preserve operator precedence.
   * Handles nested formulas by iterating up to 5 passes.
   *
   * Edge cases handled:
   *  - Formula used as condition LHS:  nomodulus2 == 0  →  (no%2) == 0
   *  - Formula used as condition RHS:  x == nomodulus2  →  x == (no%2)
   *  - Formula in raw expressions, assignment values, action/subflow args
   *  - Chained formulas (formula A references formula B) — resolved iteratively
   *  - \b word-boundary prevents false matches inside longer identifiers
   */
  const resolveFormulas = (expr) => {
    if (!expr || typeof expr !== 'string') return expr;
    const formulas = resources.filter(r => {
      const t = (r.resourceType || r.type || '').toLowerCase();
      return t === 'formula';
    });
    if (formulas.length === 0) return expr;
    let resolved = expr;
    for (let pass = 0; pass < 5; pass++) {
      let changed = false;
      for (const f of formulas) {
        if (!f.name || !f.expression) continue;
        const regex = new RegExp(`\\b${f.name}\\b`, 'g');
        const next = resolved.replace(regex, `(${f.expression})`);
        if (next !== resolved) { resolved = next; changed = true; }
      }
      if (!changed) break;
    }
    return resolved;
  };

  switch (node.type) {
    case 'input': {
      // Handle input with selected resources
      if (node.selectedResources && node.selectedResources.length > 0) {
        // Defensive cleanup before iterating: legacy projects can hold
        // both `p1.addr` (struct path) AND `p1.addr.city` (leaf) in
        // selectedResources because earlier picker versions stored the
        // struct path. Expand struct paths to their leaves and de-dupe
        // so we never emit `scanf("%d", &p1.addr)`.
        const expandedSelections = [];
        const seen = new Set();
        const stripBracketedSuffix = (segment) => segment.replace(/\[[^\]]*\]$/, '');
        const tryExpandStructPath = (resIdOrPath) => {
          if (!resIdOrPath.includes('.')) return null;
          const parts = resIdOrPath.split('.');
          const baseName = stripBracketedSuffix(parts[0]);
          const baseRes = resources.find(r => r.name === baseName);
          if (!baseRes) return null;
          const memberPath = parts.slice(1).join('.');
          const t = getMemberType(baseRes, memberPath, resources, language);
          if (typeof t !== 'string' || !t.startsWith('struct ')) return null;
          // It's a struct member — expand to the struct's leaves.
          const structName = t.replace('struct ', '');
          const def = getStructDefinition(structName, resources);
          if (!def) return null;
          const leaves = getStructPrimitiveMembers(def, resIdOrPath, resources);
          return leaves.map(l => l.path);
        };
        // Canonical-key dedupe: two paths are duplicates if they refer
        // to the same conceptual leaf — `students.id`, `students[].id`,
        // and `students[i].id` all collapse to base `students` + chain
        // `.id`. The entry with a non-empty bracket wins (then empty
        // bracket, then no bracket) so codegen emits the indexed form.
        const canonicalKey = (path) => {
          if (typeof path !== 'string') return String(path);
          // Strip the FIRST [..] segment from the head and re-append.
          const m = path.match(/^([^[.]+)(?:\[[^\]]*\])?(.*)$/);
          if (!m) return path;
          return `${m[1]}${m[2]}`;
        };
        const bracketRank = (path) => {
          if (typeof path !== 'string') return 0;
          const m = path.match(/^[^[.]+\[([^\]]*)\]/);
          if (!m) return 0;          // no bracket
          if (m[1] === '') return 1; // empty `[]`
          return 2;                  // explicit index
        };
        const keyToBest = new Map();
        const addCandidate = (path) => {
          const key = canonicalKey(path);
          const prev = keyToBest.get(key);
          if (!prev || bracketRank(path) > bracketRank(prev)) {
            keyToBest.set(key, path);
          }
        };
        node.selectedResources.forEach(resIdOrPath => {
          const expanded = tryExpandStructPath(resIdOrPath);
          if (expanded && expanded.length > 0) {
            expanded.forEach(p => addCandidate(p));
          } else {
            addCandidate(resIdOrPath);
          }
        });
        for (const path of keyToBest.values()) {
          if (!seen.has(path)) { seen.add(path); expandedSelections.push(path); }
        }
        // Sort member paths by declaration order: walk the struct
        // definition and assign each leaf a stable index. Non-member
        // entries (bare resource ids / array refs) keep their input
        // order. This stabilises the scanf sequence so the form's
        // visible row order matches the runtime read order.
        const orderIndex = new Map();
        let nextIdx = 0;
        for (const entry of expandedSelections) {
          if (!entry.includes('.')) { orderIndex.set(entry, nextIdx++); continue; }
          const parts = entry.split('.');
          const baseName = stripBracketedSuffix(parts[0]);
          const baseRes = resources.find(r => r.name === baseName);
          if (!baseRes) { orderIndex.set(entry, nextIdx++); continue; }
          // Compute the path's declaration-order rank by walking the struct.
          const sName = baseRes.structTypeRef
            || (typeof baseRes.dataType === 'string' && baseRes.dataType.startsWith('struct ') ? baseRes.dataType.replace('struct ', '') : null)
            || (typeof baseRes.dataType === 'string' && baseRes.dataType.startsWith('struct_') ? baseRes.dataType.replace('struct_', '') : null)
            || baseRes.dataType;
          const def = getStructDefinition(sName, resources);
          if (!def) { orderIndex.set(entry, nextIdx++); continue; }
          const allLeaves = getStructPrimitiveMembers(def, parts[0], resources).map(l => l.path);
          const rank = allLeaves.indexOf(entry);
          orderIndex.set(entry, rank >= 0 ? rank : nextIdx++);
        }
        const sortedSelections = [...expandedSelections].sort(
          (a, b) => (orderIndex.get(a) ?? 0) - (orderIndex.get(b) ?? 0)
        );
        // Generate input for each selected resource (could be resource ID or member path)
        sortedSelections.forEach(resIdOrPath => {
          // Check if this is a struct member path (contains '.')
          if (resIdOrPath.includes('.')) {
            // This is a struct member path like "person11.name" — or
            // an array-of-struct path like "students[i].gpa". Strip the
            // bracket from the first segment when looking up the base
            // resource, but keep the bracketed form in `fullPath` so
            // codegen emits `scanf("%s", students[i].name);`.
            const parts = resIdOrPath.split('.');
            const rawFirst = parts[0];
            const resourceName = rawFirst.replace(/\[[^\]]*\]$/, '');
            const memberPath = parts.slice(1).join('.');

            // Find the base resource by name
            const resource = resources.find(r => r.name === resourceName);
            // ADO #582 — `students[].gpa`: which student was never chosen.
            if (resource && /\[\s*\]/.test(rawFirst)) {
              code += `${indent}${lang.comment(`Input: choose which position of ${resourceName} to read into`)}\n`;
              return;
            }
            if (resource) {
              // Get the member type by traversing the struct definition
              const memberType = getMemberType(resource, memberPath, resources, language);
              const fullPath = resIdOrPath;

              // Add prompt comment if available
              if (resource.prompt) {
                code += `${indent}${lang.comment(`Input for ${fullPath}`)}\n`;
              }
              code += `${indent}${lang.input(fullPath, memberType, true)}\n`;
            }
          } else {
            // Match "resourceId[i]" and legacy "resourceId[]" (pre-#263 data where
            // char array_1d was wrongly stored with empty brackets). Empty string
            // gets normalized to null so downstream treats it as "no index".
            const arrayMatch = resIdOrPath.match(/^(.+?)\[(.*)\]$/);
            let resourceId = resIdOrPath;
            let arrayIndex = null;
            if (arrayMatch) {
              resourceId = arrayMatch[1];
              arrayIndex = arrayMatch[2] || null;
            }

            // This is a resource ID (possibly with array index)
            const resource = resources.find(r => r.id === resourceId || r.name === resourceId);
            if (resource) {
              const resType = (resource.resourceType || resource.type || '').toLowerCase();
              // ADO #409 — enum_type / struct_type are type definitions, not
              // variables, so they're never valid scanf targets. (enum_instance
              // and struct_instance fall through and are handled normally.)
              // ADO #406 — union_type joins them; a whole union isn't a scanf
              // target either (students scanf a member: u.field).
              if (resType === 'enum_type' || resType === 'struct_type' || resType === 'union_type' || resType === 'typedef') return;
              const resDataType = (resource.dataType || 'int').toLowerCase();
              // Legacy resources use resourceType 'ARRAY' (→ 'array'); treat as 1D.
              const is1DArray = resType === 'array_1d' || resType === 'array';
              const isCharArray1D = is1DArray && resDataType === 'char';
              // Non-char array with an empty position (`nos[]`, `grid[i][]`) =
              // the index field was never filled. ADO #582 — never skip
              // silently: say so in the code. Validation flags the node with the
              // same words and Run is blocked.
              const blankPosition = !!arrayMatch && !isCharArray1D && /\[\s*\]/.test(resIdOrPath.slice(resourceId.length));
              if (blankPosition) {
                const pos = (arrayIndex || '').includes('][') ? 'row and column' : 'position';
                code += `${indent}${lang.comment(`Input: choose which ${pos} of ${resource.name} to read into`)}\n`;
                return;
              }
              let varName = resource.name;

              // If array index provided, append it to var name
              if (arrayIndex) {
                varName = `${varName}[${arrayIndex}]`;
              }

              // char array_1d resources are strings — use char[] so scanf gets %s not %c
              const varType = (isCharArray1D && !arrayIndex)
                ? 'char[]'
                : (resource.dataType || 'int');
              const isDeclared = true; // Resource variables are always declared
              
              // Add prompt comment if available
              if (resource.prompt) {
                code += `${indent}${lang.comment(resource.prompt)}\n`;
              }
              
              // Check if this is a struct instance - need to generate input for each primitive member
              if (isStructInstance(resource)) {
                const structTypeName = getStructTypeName(resource);
                const structDef = getStructDefinition(structTypeName, resources);
                
                if (structDef && structDef.structMembers) {
                  // Get all primitive members of the struct (recursively handles nested structs)
                  const primitiveMembers = getStructPrimitiveMembers(structDef, varName, resources);
                  
                  if (primitiveMembers.length > 0) {
                    primitiveMembers.forEach(({ path, type }) => {
                      code += `${indent}${lang.input(path, type, true)}\n`;
                    });
                  } else {
                    // No primitive members found, add a comment
                    code += `${indent}${lang.comment(`Cannot read struct ${varName} - no primitive members found`)}\n`;
                  }
                } else {
                  // Struct definition not found - add comment
                  code += `${indent}${lang.comment(`Cannot read struct ${varName} - struct type not found`)}\n`;
                }
              } else {
                // ADO #359 — per-variable read mode. A char[] set to 'line'
                // reads the WHOLE line via fgets (auto buffer size from the
                // declaration) instead of scanf("%s") which stops at whitespace.
                // Default (no readMode) = scanf, so existing nodes are unchanged.
                const readMode = node.readModes && node.readModes[resource.id];
                if (language === 'c' && isCharArray1D && !arrayIndex && readMode === 'line') {
                  const bufSize = resource.size || resource.arraySize || resource.length || 100;
                  // scanf("%d") leaves its newline in the buffer, and fgets used to
                  // swallow that as an empty line — so a flow that read a NUMBER and
                  // then a LINE lost the line, and every value after it shifted by
                  // one. It bites hardest in a loop, where iteration 2's first line
                  // read eats iteration 1's trailing newline.
                  //
                  // `scanf(" %<n>[^\n]")` is the fix and needs no state: the leading
                  // space skips any pending whitespace INCLUDING newlines, and the
                  // scanset then takes everything up to the next one. The newline is
                  // left behind, which is fine — the next read's leading space eats
                  // it too.
                  //
                  // It also removes the strcspn line, because nothing trailing is
                  // captured to strip.
                  // The size may be a #define (`char line[MAX_LINE];`); Number() of
                  // that was NaN and the read became scanf(" %NaN[^\n]") -- which
                  // reads nothing at all. Resolve it through the project's macros;
                  // if it cannot be resolved, fgets with sizeof is always bounded.
                  const width = resolveCSize(bufSize, resources);
                  code += width
                    ? `${indent}scanf(" %${Math.max(1, width - 1)}[^\\n]", ${varName});\n`
                    : `${indent}if (fgets(${varName}, sizeof ${varName}, stdin)) ${varName}[strcspn(${varName}, "\\n")] = '\\0';\n`;
                } else {
                  // Regular variable - generate single input (scanf)
                  code += `${indent}${lang.input(varName, varType, isDeclared)}\n`;
                }
              }
            }
          }
        });
      } else {
        // Fallback: Look for resources marked as readFromUser
        const readFromUserResources = resources.filter(r => r.readFromUser);
        if (readFromUserResources.length > 0) {
          readFromUserResources.forEach(resource => {
            const varName = resource.name;
            const varType = resource.dataType || 'int';
            if (resource.prompt) {
              code += `${indent}${lang.comment(resource.prompt)}\n`;
            }
            
            // Check if this is a struct instance - need to generate input for each primitive member
            if (isStructInstance(resource)) {
              const structTypeName = getStructTypeName(resource);
              const structDef = getStructDefinition(structTypeName, resources);
              
              if (structDef && structDef.structMembers) {
                const primitiveMembers = getStructPrimitiveMembers(structDef, varName, resources);
                
                if (primitiveMembers.length > 0) {
                  primitiveMembers.forEach(({ path, type }) => {
                    code += `${indent}${lang.input(path, type, true)}\n`;
                  });
                }
              }
            } else {
              code += `${indent}${lang.input(varName, varType, true)}\n`;
            }
          });
        } else {
          // Last resort: the node names no resource at all.
          //
          // This used to derive a variable name from the node's LABEL, so an
          // Input labelled "Read the price" emitted
          //
          //     scanf("%d", &Read the price);
          //
          // — uncompilable, and reported to the learner as `use of undeclared
          // identifier 'Read'`, about a node they had simply not finished
          // setting up. The property editor already calls that state
          // *Required*; the generator should agree with it rather than invent
          // an identifier out of prose.
          //
          // Keep the fallback where the derived name is actually usable (an
          // apiName that names a declared variable), and otherwise emit a
          // comment. A comment rather than nothing, for the same reason
          // forEach-in-C emits one: an unconfigured step must not disappear
          // silently from the code the learner is reading.
          const { varName, type } = parseInputContent(node);
          const usable = /^[a-zA-Z_][a-zA-Z0-9_]*$/.test(varName || '') && isVarDeclared(varName);
          if (node.label && node.label !== 'Input') {
            code += `${indent}${lang.comment(node.label)}\n`;
          }
          if (usable) {
            code += `${indent}${lang.input(varName, type, isVarDeclared(varName))}\n`;
          } else {
            code += `${indent}${lang.comment('Input node has no Input Resources set — nothing is read')}\n`;
          }
        }
      }

      // Debug: logStep after input
      if (isDebugMode && node.selectedResources) {
        const inputRes = node.selectedResources.map(resIdOrPath => {
          if (resIdOrPath.includes('.')) return resIdOrPath;
          // Strip array index suffix [i] for resource lookup
          const bareId = resIdOrPath.replace(/\[.*\]$/, '');
          const indexSuffix = resIdOrPath.includes('[') ? resIdOrPath.slice(resIdOrPath.indexOf('[')) : '';
          const r = resources.find(res => res.id === bareId || res.name === bareId);
          return r ? (r.name + indexSuffix) : resIdOrPath;
        });
        // ADO #365 — emit EVERY variable this input node reads (a single Input
        // node can read multiple resources, e.g. no1 and no2), joined as
        // "a=.., b=.." so the parser yields a multi-variable step. Previously
        // only inputRes[0] was logged, so extra inputs were missing from the
        // trace, Variables tab, and memory view.
        // splitIndexed('nos[i][j]') → { base:'nos', indices:['i','j'] }
        const splitIndexed = (v) => {
          const indices = [];
          const base = v.replace(/\[(\w+)\]/g, (_m, idx) => { indices.push(idx); return ''; });
          return { base, indices };
        };
        if (inputRes.length > 0 && (language === 'c' || language === 'cpp')) {
          const fmtParts = [];
          const args = [];
          for (const v of inputRes) {
            const { base, indices } = splitIndexed(v);
            const fmt = getCFormatSpecifier(resolveVarType(v));
            fmtParts.push(`${base}${indices.map(() => '[%d]').join('')}=${fmt}`);
            indices.forEach(ix => args.push(ix));
            args.push(v);
          }
          code += `${indent}snprintf(__ctx, sizeof(__ctx), "${fmtParts.join(', ')}", ${args.join(', ')});\n`;
          code += `${indent}${getLogStepCall(language, 'Execution', 'Input', node.id, '__ctx')}\n`;
        } else if (inputRes.length > 0 && language === 'java') {
          const parts = inputRes.map(v => {
            const { base, indices } = splitIndexed(v);
            const name = [`"${base}"`, ...indices.map(ix => `"[" + ${ix} + "]"`)].join(' + ');
            return `${name} + "=" + ${v}`;
          });
          const expr = parts.join(' + ", " + ');
          code += `${indent}${getLogStepCall(language, 'Execution', 'Input', node.id, expr)}\n`;
        } else if (inputRes.length > 0 && language === 'python') {
          const parts = inputRes.map(v => {
            const { base, indices } = splitIndexed(v);
            const name = `${base}${indices.map(ix => `[{${ix}}]`).join('')}`;
            return `${name}={${v}}`;
          });
          code += `${indent}${getLogStepCall(language, 'Execution', 'Input', node.id, `f"${parts.join(', ')}"`)}\n`;
        } else if (inputRes.length > 0 && language === 'javascript') {
          const parts = inputRes.map(v => {
            const { base, indices } = splitIndexed(v);
            const name = `${base}${indices.map(ix => `[\${${ix}}]`).join('')}`;
            return `${name}=\${${v}}`;
          });
          code += `${indent}${getLogStepCall(language, 'Execution', 'Input', node.id, '`' + parts.join(', ') + '`')}\n`;
        }
      }

      break;
    }

    case 'output': {
      // Get the template content
      const template = node.outputTemplate || node.description || '';

      // ADO #325 — explicit per-node toggle. Missing field reads as true so
      // every pre-existing Output node generates identical code to before.
      const endWithNewline = node.endWithNewline !== false;

      // Escape actual newlines in the template (convert real newlines to \n escape sequences)
      const escapedTemplate = template.replace(/\r?\n/g, '\\n');

      // Check if template has placeholders like {varName} or {expression}
      // Match: {varName}, {abs(x)}, {x + 1}, etc.
      const placeholderRegex = /\{([^{}]+)\}/g;
      const hasPlaceholders = placeholderRegex.test(escapedTemplate);

      if (hasPlaceholders) {
        // Resolves a formula name to its expression (used inside buildOutputStatement)
        const resolveFormulaExpr = (name) => {
          const res = resources.find(r => r.name === name &&
            (r.resourceType === 'formula' || r.resourceType === 'FORMULA' ||
             r.type === 'formula' || r.type === 'FORMULA'));
          return res ? (res.expression || name) : name;
        };
        code += buildOutputStatement(lang, escapedTemplate, resources, indent, getVarType, resolveFormulaExpr, endWithNewline);
      } else {
        // No placeholders — plain text, and ONLY plain text.
        //
        // There used to be two ways for a brace-free template to mean a
        // variable: a `$name` prefix, and — the one that bit — a template that
        // happened to be exactly the name of a declared resource. So `total`
        // printed the value while `the total` printed the words, which is not a
        // rule anyone can hold, and it made the bare word "total" unprintable
        // in any flow with a variable of that name.
        //
        // It also contradicted the lesson sitting directly above it: "Plain
        // words print as written; each {name} is replaced by that variable's
        // value." Braces are how you say you mean the value; that is what they
        // are for, and a second implicit spelling only undermines them.
        //
        // Neither form appeared anywhere in the database and `$name` was
        // undocumented, so both are gone rather than one.
        // The text as the learner wrote it. parseOutputContent reads the
        // DESCRIPTION, trims it and strips a leading "Display " / "Print " - so
        // ` Schema | Name` lost its indentation and `Print receipt` printed
        // `receipt`. It stays only for old nodes that have no outputTemplate.
        const content = (typeof node.outputTemplate === 'string' && node.outputTemplate !== '')
          ? node.outputTemplate
          : parseOutputContent(node);
        const escapedContent = content.replace(/\r?\n/g, '\\n').replace(/"/g, '\\"');
        // C's printf reads its first argument as a FORMAT string, so a literal
        // `%` in plain text has to be doubled. The placeholder path above has
        // escaped literal segments since ADO #287; this branch never did, so an
        // Output saying `Attendance 83% short` emitted
        //   printf("Attendance 83% short\n");
        // and `% s` was read as a conversion — the line came out truncated or
        // filled with garbage, with nothing to see in the builder. Every other
        // language streams or concatenates (`cout <<`, `println`, `print`), so
        // this is a C-only escape; doubling it for them would print `%%`.
        const printable = lang.name === 'C'
          ? escapedContent.replace(/%/g, '%%')
          : escapedContent;
        code += `${indent}${lang.print(printable, endWithNewline)}\n`;
      }

      // Debug: logStep AFTER output with resolved values
      if (isDebugMode) {
        const resolveFormulaExprDebug = (name) => {
          const res = resources.find(r => r.name === name &&
            (r.resourceType === 'formula' || r.resourceType === 'FORMULA' ||
             r.type === 'formula' || r.type === 'FORMULA'));
          return res ? (res.expression || name) : name;
        };

        if (language === 'c') {
          // ADO #287 added per-placeholder format specs ({price:.2f}) and taught
          // buildOutputStatement to parse them. This branch was never updated
          // and kept its own regex, which does not allow `:` in a placeholder —
          // so `{price:.2f}` failed the simple-variable test, fell through to
          // the catch-all, and emitted `snprintf(..., "%d", price:.2f)`. That is
          // not valid C, so the whole program failed to compile the moment
          // instrumentation was on: no Debug stepping and no run-path highlight
          // for any flow whose Output formats a number.
          //
          // Use the same parser as the real emitter rather than a second regex.
          // Two parsers for one syntax is how this drifted in the first place.
          let cleanFmt = '';
          const cleanExprs = [];
          for (const seg of parseOutputTemplate(escapedTemplate)) {
            if (seg.type === 'literal') { cleanFmt += seg.value.replace(/%/g, '%%'); continue; }
            const t = (seg.name || '').trim();
            if (/^\*?[a-zA-Z_][\w.\[\]]*(?:->[\w.\[\]]+)?$/.test(t)) {
              cleanExprs.push(resolveFormulaExprDebug(t));
              const dt = resolveVarType(t);
              cleanFmt += seg.spec ? buildCFormatFromSpec(seg.spec, dt) : getCFormatSpecifier(dt);
            } else {
              // Same inference as the real emitter, for the same reason: a
              // hardcoded `int` here made every float EXPRESSION print garbage
              // into the Debug trace. Keeping two rules for one syntax is what
              // ADO #287 drifted on, per the note above — so this branch reads
              // the operand types too, and promotes the argument when a float
              // conversion is asked of an integer expression.
              let dt = inferExpressionType(t, resolveVarType);
              let dbgExpr = t;
              if (seg.spec && /[feEgG]/.test(String(seg.spec)) && !isFloatingCType(dt)) {
                dbgExpr = `(double)(${t})`;
                dt = 'double';
              }
              cleanExprs.push(dbgExpr);
              cleanFmt += seg.spec ? buildCFormatFromSpec(seg.spec, dt) : getCFormatSpecifier(dt);
            }
          }
          cleanFmt = cleanFmt.replace(/"/g, '\\"');
          // Keep the record on ONE line.
          //
          // The trace is line-based — `##STEP##|cat|msg|node|context` per line —
          // and escapedTemplate has already turned real newlines into the C
          // escape `\n`, which the compiler turns back into a real newline at
          // runtime. So a two-line Output template wrote a real newline into the
          // middle of its own record: the first line parsed, and everything
          // after the break landed on a line with no ##STEP## prefix and was
          // dropped on the floor. Half the output vanished from the debugger
          // with nothing to say it had.
          //
          // Double the backslash so the runtime string carries a literal `\n`
          // instead. The trace stays one record per step, and the renderer can
          // show the break.
          cleanFmt = cleanFmt.replace(/\\n/g, '\\\\n');
          if (cleanExprs.length > 0) {
            code += `${indent}snprintf(__ctx, sizeof(__ctx), "OUTPUT:${cleanFmt}", ${cleanExprs.join(', ')});\n`;
          } else {
            code += `${indent}snprintf(__ctx, sizeof(__ctx), "OUTPUT:${cleanFmt}");\n`;
          }
          code += `${indent}${getLogStepCall(language, 'Execution', 'Output', node.id, '__ctx')}\n`;
        } else if (language === 'java') {
          let jFmt = escapedTemplate;
          const jExprs = [];
          jFmt = jFmt.replace(/\{([^{}]+)\}/g, (_m, expr) => {
            const t = expr.trim();
            jExprs.push(/^\*?[a-zA-Z_][\w.\[\]]*(?:->[\w.\[\]]+)?$/.test(t) ? resolveFormulaExprDebug(t) : t);
            return getCFormatSpecifier(resolveVarType(t));
          });
          jFmt = jFmt.replace(/"/g, '\\"');
          const jCtx = jExprs.length > 0
            ? `String.format("OUTPUT:${jFmt}", ${jExprs.join(', ')})`
            : `"OUTPUT:${jFmt}"`;
          code += `${indent}${getLogStepCall(language, 'Execution', 'Output', node.id, jCtx)}\n`;
        } else if (language === 'python') {
          const pyTpl = escapedTemplate.replace(/\{([^{}]+)\}/g, (_m, expr) => {
            const t = expr.trim();
            return `{${/^\*?[a-zA-Z_][\w.\[\]]*(?:->[\w.\[\]]+)?$/.test(t) ? resolveFormulaExprDebug(t) : t}}`;
          });
          code += `${indent}${getLogStepCall(language, 'Execution', 'Output', node.id, `f"OUTPUT:${pyTpl}"`)}\n`;
        } else if (language === 'javascript') {
          const jsTpl = escapedTemplate.replace(/\{([^{}]+)\}/g, (_m, expr) => {
            const t = expr.trim();
            return `\${${/^\*?[a-zA-Z_][\w.\[\]]*(?:->[\w.\[\]]+)?$/.test(t) ? resolveFormulaExprDebug(t) : t}}`;
          });
          code += `${indent}${getLogStepCall(language, 'Execution', 'Output', node.id, '`OUTPUT:' + jsTpl + '`')}\n`;
        }
      }

      break;
    }

    case 'assignment': {
      // Handle multiple assignments (Salesforce Flow Builder style)
      const assignments = node.assignments || [];
      
      if (assignments.length > 0) {
        // Generate code for each VALID assignment only
        let validAssignmentCount = 0;
        assignments.forEach((assignment, idx) => {
          // ADO #433 — tuple unpacking: `a, b = func()` / `a, b = b, a`. Two or
          // more LHS targets live in `targetVariables`; the RHS comes from the
          // same value sources as a normal row (a tuple variable, or an
          // expression like `func()` / `b, a`). Python-only syntax.
          if (assignment.unpack && Array.isArray(assignment.targetVariables)
              && assignment.targetVariables.filter(t => t && (t.variableId || t.variableName)).length >= 2) {
            const names = assignment.targetVariables
              .map(t => {
                if (!t) return null;
                if (t.variableName) return t.variableName;
                const r = resources.find(x => x.id === t.variableId);
                return r?.name || t.variableId || null;
              })
              .filter(Boolean);
            if (names.length < 2) return;

            let rhs = null;
            if (assignment.valueSource === 'expression') {
              rhs = assignment.expression ? resolveFormulas(assignment.expression) : null;
            } else if (assignment.valueSource === 'variable' && assignment.sourceVariable) {
              rhs = assignment.sourceFullPath
                ? resolveFormulas(assignment.sourceFullPath)
                : resolveFormulas(
                    (resources.find(r => r.id === assignment.sourceVariable)?.name) || assignment.sourceVariable
                  );
              // A tuple picked whole comes through as `t[]` (the picker's index
              // placeholder); unpacking wants the whole tuple, so drop it.
              if (rhs) rhs = String(rhs).replace(/\[\s*\]\s*$/, '');
            }
            if (!rhs || !String(rhs).trim()) return;

            validAssignmentCount++;
            if (lang.name === 'Python') {
              code += `${indent}${names.join(', ')} = ${rhs}\n`;
            } else {
              // Unpacking is Python-only; other tracks get an explanatory comment
              // so the intent is visible without emitting broken syntax.
              code += `${indent}${lang.comment(`${names.join(', ')} = ${rhs}  (tuple unpacking — Python only)`)}\n`;
            }
            return;
          }

          // Skip invalid assignments - must have target variable and value
          if (!assignment.targetVariable) return;

          // Use fullPath if available (for struct members like person1.address.city)
          // Otherwise fall back to looking up the resource name
          let varName;
          if (assignment.fullPath) {
            varName = assignment.fullPath;
          } else {
            const targetResource = resources.find(r => r.id === assignment.targetVariable);
            varName = targetResource?.name || assignment.targetVariable;
          }
          if (!varName || varName === 'var') return; // Skip if no valid variable name
          
          // ADO #389 — resolve the picker's `[]` placeholders. A struct-array
          // member (`db[].gpa`) splices the index mid-path (→ `db[i].gpa`); a
          // plain/trailing array (`nos[]` / `nos`) appends it (→ `nos[i][j]`).
          // ADO #431 — a dictionary target quotes a literal str key → `ages["Ram"]`.
          const tDictRes = resources.find(r => r.id === assignment.targetVariable)
            || resources.find(r => r.name === String(varName).split(/[[.]/)[0]);
          varName = applyArrayIndices(
            varName,
            assignment.accessType === 'array',
            quoteDictKey(assignment.arrayIndex, tDictRes, resources),
            assignment.arrayIndex2,
          );
          // ADO #441 — a class-attribute target inside a Python method → self.attr
          // (precise: the target resource was picked by id, so a same-named param
          // doesn't interfere).
          varName = pySelfRefByResource(varName, tDictRes);

          const operator = assignment.operator || '=';

          // ADO #408 — standalone increment/decrement (`p++`, `i--`). These are
          // complete statements with NO right-hand side, so handle them before
          // value resolution (which would otherwise skip the row for a null
          // value). C/C++/Java/JS emit `target++;`; Python/Pseudocode have no
          // `++`, so desugar to `target += 1` / `target -= 1`.
          if (operator === '++' || operator === '--') {
            validAssignmentCount++;
            const hasNativeUpdate = ['C', 'C++', 'Java', 'JavaScript'].includes(lang.name);
            if (hasNativeUpdate) {
              code += `${indent}${varName}${operator};\n`;
            } else if (lang.name === 'Python') {
              code += `${indent}${varName} ${operator === '++' ? '+= 1' : '-= 1'}\n`;
            } else {
              // Pseudocode (and any other non-native track) — desugar with `;`
              // to match the existing compound-operator branch below.
              code += `${indent}${varName} ${operator === '++' ? '+= 1' : '-= 1'};\n`;
            }
            return;
          }

          // Determine the value based on source type
          let value = null;
          let targetResource = resources.find(r => r.id === assignment.targetVariable);
          
          if (assignment.valueSource === 'direct') {
            if (assignment.directValue !== undefined && assignment.directValue !== '') {
              value = assignment.directValue;
              // ADO #375 — quote a bare direct value ONLY when the target is
              // genuinely a string destination (legacy `string` type, or a
              // char array `char[N]`). The old heuristic ("any member ⇒ string")
              // wrongly quoted pointer/scalar members, so `n->next = NULL` became
              // strcpy(n->next, "NULL"). Use the resolved target type, and let
              // needsStringQuotes() exclude NULL/nullptr/numbers/booleans/refs.
              const resolvedTargetType = String(
                assignment.targetResolvedType || targetResource?.dataType || ''
              ).toLowerCase().trim();
              const isCharArrayTarget = resolvedTargetType === 'string'
                || (/\bchar\b/.test(resolvedTargetType) && /\[/.test(resolvedTargetType));
              if (isCharArrayTarget && needsStringQuotes(value, resources)) {
                value = `"${value}"`;
              }
            }
          } else if (assignment.valueSource === 'variable') {
            if (assignment.sourceVariable) {
              // Use sourceFullPath if available (for struct members)
              if (assignment.sourceFullPath) {
                value = resolveFormulas(assignment.sourceFullPath);
              } else {
                const sourceResource = resources.find(r => r.id === assignment.sourceVariable);
                value = resolveFormulas(sourceResource?.name || assignment.sourceVariable);
              }
              // ADO #389 — same placeholder resolution as the target side, so a
              // struct-array source (`db[].gpa`) becomes `db[i].gpa`, not
              // `db[].gpa[i]`.
              // ADO #431 — a dictionary source quotes a literal str key → `ages["Ram"]`.
              const sDictRes = resources.find(r => r.id === assignment.sourceVariable)
                || resources.find(r => r.name === String(value).split(/[[.]/)[0]);
              value = applyArrayIndices(
                value,
                assignment.sourceAccessType === 'array',
                assignment.sourceArrayIndex != null ? quoteDictKey(resolveFormulas(assignment.sourceArrayIndex), sDictRes, resources) : undefined,
                assignment.sourceArrayIndex2 != null ? resolveFormulas(assignment.sourceArrayIndex2) : undefined,
              );
              // ADO #441 — a class-attribute source → self.attr, but ONLY when the
              // source was picked as the field (id resolves to a classField). A
              // parameter of the same name (the `name` in `self.name = name`)
              // resolves to no classField, so it stays bare.
              value = pySelfRefByResource(value, resources.find(r => r.id === assignment.sourceVariable));
            }
          } else if (assignment.valueSource === 'expression') {
            if (assignment.expression) {
              value = resolveFormulas(assignment.expression);
            }
          } else if (assignment.valueSource === 'comprehension') {
            // ADO #434 — list comprehension `[expr for var in iterable if cond]`,
            // authored in four dedicated fields (skeleton fixed, blanks filled —
            // build, don't type). Python-only; other tracks have no one-liner
            // equivalent, so emit an explanatory comment.
            const c = assignment.comprehension || {};
            const outExpr = resolveFormulas(String(c.outExpr || '').trim());
            const loopVar = String(c.varName || '').trim();
            const iterable = resolveFormulas(String(c.iterable || '').trim());
            const filt = String(c.filter || '').trim();
            if (outExpr && loopVar && iterable) {
              const body = filt
                ? `[${outExpr} for ${loopVar} in ${iterable} if ${resolveFormulas(filt)}]`
                : `[${outExpr} for ${loopVar} in ${iterable}]`;
              if (lang.name === 'Python') {
                value = body;
              } else {
                validAssignmentCount++;
                code += `${indent}${lang.comment(`${varName} = ${body}  (list comprehension — Python only)`)}\n`;
                return;
              }
            }
          } else if (assignment.valueSource === 'lambda') {
            // ADO #448 — lambda `lambda params: expr`, authored in two fields
            // (params optional, body required). Python-only; other tracks have
            // no anonymous-function one-liner, so emit an explanatory comment.
            const l = assignment.lambda || {};
            const params = String(l.params || '').trim();
            const body = resolveFormulas(String(l.body || '').trim());
            if (body) {
              const lam = params ? `lambda ${params}: ${body}` : `lambda: ${body}`;
              if (lang.name === 'Python') {
                value = lam;
              } else {
                validAssignmentCount++;
                code += `${indent}${lang.comment(`${varName} = ${lam}  (lambda — Python only)`)}\n`;
                return;
              }
            }
          }

          // Skip if no valid value
          if (value === null || value === undefined) return;
          
          validAssignmentCount++;
          
          // Check if this is a string assignment in C (needs strcpy)
          //
          // DOUBLE quotes only. A single-quoted value in C is a CHARACTER literal,
          // not a string, and `grade = 'A'` on a plain char emitted
          // strcpy(grade, 'A') — which does not compile. Assigning a character is
          // the most ordinary thing there is to do with a char, and it could not
          // be built: the same shape as the strcpy-from-a-variable fault, and it
          // survived for the same reason, that nothing in the corpus had tried it.
          //
          // A char literal aimed at a char ARRAY still takes the strcpy path below
          // via targetIsCharArray. That is not valid C either, but it was already
          // the behaviour and it is not what this fixes.
          const isStringValue = value.startsWith('"');
          const isCLanguage = lang.name === 'C'; // C++ uses std::string, no strcpy needed

          // A char ARRAY target needs strcpy whatever the source is. Keying only on
          // the VALUE being a literal meant `p.name = "Ravi"` compiled and
          // `p.name = nm` did not — it emitted a raw assignment and clang rejected
          // it with "array type 'char[30]' is not assignable".
          //
          // That is the ordinary shape of reading text in: an Input node fills a
          // text resource, an Assignment moves it into the member. It is Program 1
          // of the question bank's Derived Types chapter, and it could not be built.
          //
          // Widening rather than replacing: every literal that emitted strcpy before
          // still does, so no existing item changes.
          //
          // The type the assignment RECORDS wins when it names one that is not
          // char. The lookup below goes by id, and a local's id is unique only
          // within its own flow: a single-file program hands every flow the
          // locals of all of them, so `digits = (int) strlen(w) - neg` (an int,
          // l-digits in fitsInInt) found avgFinish's `char digits[]`, also
          // l-digits, and came out as strcpy(digits, ...). The toolbox and the
          // mini-Postgres engine in one program hit exactly that.
          const recordedType = String(assignment.targetResolvedType || '').trim();
          // One letter of a 1-D text (`name[i] = 'R'`) is a char, not the text:
          // a plain assignment. No stored row had a position on a 1-D char array
          // before the Assignment editor v2 offered one (checked across the
          // production corpus), so no existing program changes.
          const oneLetter = assignment.accessType === 'array' && !!String(assignment.arrayIndex ?? '').trim()
            && !String(assignment.arrayIndex2 ?? '').trim() && /^\s*[A-Za-z_]\w*\s*\[\s*\]\s*$/.test(String(assignment.fullPath || ''));
          const targetIsCharArray =
            /^char\s*\[/.test(recordedType) ||
            (!oneLetter && (!recordedType || /^char\b/.test(recordedType)) && (() => {
              const r = resources.find((x) => x.id === assignment.targetVariable);
              if (!r) return false;
              const et = String(r.elementType || r.dataType || '').trim();
              return (r.resourceType === 'array_1d' || r.resourceType === 'ARRAY_1D')
                && (et === 'char' || /^char\b/.test(et));
            })());

          // Handle compound operators
          if (operator === '=') {
            // In C, string assignments to char arrays need strcpy
            if (isCLanguage && (isStringValue || targetIsCharArray)) {
              code += `${indent}strcpy(${varName}, ${value});\n`;
            } else {
              code += `${indent}${lang.assignment(varName, value)}\n`;
            }
          } else {
            // For +=, -=, *=, /= etc.
            if (lang.name === 'Python') {
              code += `${indent}${varName} ${operator} ${value}\n`;
            } else {
              code += `${indent}${varName} ${operator} ${value};\n`;
            }
          }
        });
        
        // If no valid assignments, add a comment
        if (validAssignmentCount === 0) {
          code += `${indent}${lang.comment('TODO: Configure assignment in properties panel')}\n`;
        }
      } else {
        // No assignments configured - add a comment
        code += `${indent}${lang.comment('TODO: Add assignments in properties panel')}\n`;
      }

      // Debug: logStep after assignments — emit ALL assignments as one context string
      if (isDebugMode && assignments.length > 0) {
        const validAsgns = assignments.filter(a => a.targetVariable && a.fullPath);
        if (validAsgns.length > 0) {
          // Helper: resolve [i] to [%d] in format string, collect index args
          if (language === 'c' || language === 'cpp') {
            const fmtParts = [];
            const allArgs = [];
            validAsgns.forEach(a => {
              const { fmt, idxVars } = resolveIdxFmt(a.fullPath);
              // ADO #368 — pointer targets/members print as %p (a real address)
              // so the heap model can match them to nodes and draw links.
              const tt = a.targetResolvedType || resolveVarType(a.fullPath);
              const isPtr = typeof tt === 'string' && /\*\s*$/.test(tt.trim());
              fmtParts.push(`${fmt}=${isPtr ? '%p' : getCFormatSpecifier(tt)}`);
              allArgs.push(...idxVars);
              allArgs.push(isPtr ? `(void*)(${a.fullPath})` : a.fullPath);
            });
            code += `${indent}snprintf(__ctx, sizeof(__ctx), "${fmtParts.join(', ')}", ${allArgs.join(', ')});\n`;
            code += `${indent}${getLogStepCall(language, 'Execution', 'Assignment', node.id, '__ctx')}\n`;
          } else if (language === 'java') {
            const parts = validAsgns.map(a => {
              const { fmt, idxVars } = resolveIdxFmt(a.fullPath);
              if (idxVars.length > 0) {
                const brackets = a.fullPath.match(/\[\w+\]/g) || [];
                const resolved = brackets.map(b => `"[" + ${b.slice(1, -1)} + "]"`).join(' + ');
                const baseName = a.fullPath.replace(/\[.*/, '');
                const memberPart = a.fullPath.includes('.') ? a.fullPath.replace(/^[^.]+/, '').replace(/\[\w+\]/g, '') : '';
                return `"${baseName}" + ${resolved} + "${memberPart}=" + ${a.fullPath}`;
              }
              return `"${a.fullPath}=" + ${a.fullPath}`;
            });
            code += `${indent}${getLogStepCall(language, 'Execution', 'Assignment', node.id, parts.join(' + ", " + '))}\n`;
          } else if (language === 'python') {
            const parts = validAsgns.map(a => {
              // Python f-strings naturally resolve variables in brackets
              return `${a.fullPath}={${a.fullPath}}`;
            });
            code += `${indent}${getLogStepCall(language, 'Execution', 'Assignment', node.id, `f"${parts.join(', ')}"`)}\n`;
          } else if (language === 'javascript') {
            const parts = validAsgns.map(a => `${a.fullPath}=\${${a.fullPath}}`);
            code += `${indent}${getLogStepCall(language, 'Execution', 'Assignment', node.id, '`' + parts.join(', ') + '`')}\n`;
          }
        }
      }

      break;
    }

    // ADO #303 — Instantiate (Java New Object). Emits a constructor
    // invocation assignment into a class-instance resource declared via
    // ADO #305. Java-only; in other languages this falls through as a
    // pseudocode comment so unrecognised tracks don't produce broken code.
    //
    //   local target          → `target = new ClassName(args);`
    //   classField target     → `this.target = new ClassName(args);` when
    //                            emitted inside a constructor / method body
    //
    // Note: the existing declaration emitted by the class-instance resource
    // (e.g. `Dog d;`) stays put. v1 keeps the two lines separate
    // (`Dog d;` then `d = new Dog(...)`); a v2 follow-up can fold them
    // into a single `Dog d = new Dog(...)` when Instantiate is the first
    // write to a local target.
    case 'instantiate': {
      const indent = getIndent();
      const target = resources.find((r) => r.id === node.targetResourceId);
      if (!target) {
        code += `${indent}// Instantiate: target resource missing — pick a Class Instance resource\n`;
        break;
      }
      const className = target.classTypeRef || target.dataType || 'UnknownClass';
      // ADO #310 — argument source switches on the target's class origin:
      //   - 'custom' targets have no curated signature, so the editor
      //     stores a free-form string the student typed; we trim and
      //     pass through verbatim.
      //   - Everything else (project, built-in, legacy) uses the
      //     index-aligned argumentValues list. Empty rows are dropped
      //     so a partially-filled form still produces a syntactically
      //     valid `new Foo(filled, args)` rather than a stray comma.
      let argList;
      if (target.classOrigin === 'custom' && typeof node.customExtraArgs === 'string') {
        argList = node.customExtraArgs.trim();
      } else {
        const argValues = (node.argumentValues || [])
          .map((v) => (v || '').trim())
          .filter((v) => v !== '');
        argList = argValues.join(', ');
      }
      // Java is the only track that uses `new ClassName(...)`. Other tracks
      // get a comment so unfinished Python / JS class work doesn't emit
      // broken syntax silently.
      if (language === 'java') {
        // ADO #311 — declaration merge. When the target resource is owned
        // by this very Instantiate node (created via the "Create new"
        // mode), emit the merged `ClassName var = new ClassName(args);`
        // form. The standalone declaration line is suppressed at the
        // resource-emission site. Class-field targets never inline-declare
        // (their declaration lives at class scope), so the `this.` branch
        // never collides with this case.
        if (target._inlineDeclaredByNodeId === node.id) {
          code += `${indent}${className} ${target.name} = new ${className}(${argList});\n`;
        } else {
          const lhs = target.location === 'classField' ? `this.${target.name}` : target.name;
          code += `${indent}${lhs} = new ${className}(${argList});\n`;
        }
      } else if (language === 'python') {
        // ADO #442 — Python has no `new`: `obj = Dog(args)`. A class-field
        // target inside a method becomes `self.attr = ...`. Quote a bare string
        // argument (`Rex` → `"Rex"`); a declared variable / number stays bare
        // (needsStringQuotes excludes refs/numbers/keywords) — same rule as #437.
        const pyArgs = (target.classOrigin === 'custom' && typeof node.customExtraArgs === 'string')
          ? node.customExtraArgs.trim()
          : (node.argumentValues || [])
              .map((v) => (v || '').trim())
              .filter((v) => v !== '')
              .map((v) => (needsStringQuotes(v, resources) ? `"${v}"` : v))
              .join(', ');
        const lhs = target.location === 'classField' ? `self.${target.name}` : target.name;
        code += `${indent}${lhs} = ${className}(${pyArgs})\n`;
      } else {
        code += `${indent}// Instantiate node (Java/Python only): ${target.name} = new ${className}(${argList})\n`;
      }
      break;
    }

    // ADO #304 — Throw node. Visual `throw new ExceptionType(msg);` (Java OOP).
    // Terminal: control does not continue past Throw in its branch
    // (see branchTerminates() in components/visual-flow-builder/constants.js).
    // Java-only — surfaced in the palette via JavaTrackProvider; for other
    // tracks emit a no-op comment so a multi-track preview doesn't break.
    case 'throw': {
      const indent = getIndent();
      // ADO #435 — Python raises instead of throws: `raise ValueError("bad")`.
      if (language === 'python') {
        const excType = (node.exceptionType || '').trim() || 'Exception';
        const mode = node.messageMode || 'literal';
        const rawMsg = (node.message || '').trim();
        let argExpr = '';
        if (rawMsg) {
          if (mode === 'expression') {
            argExpr = rawMsg;
          } else {
            // Literal: prefer double quotes; switch to single only if the text
            // contains a double quote (Python<3.12 f-string limits don't apply
            // here — this is a plain string literal — but keep it clean).
            argExpr = rawMsg.includes('"') && !rawMsg.includes("'")
              ? `'${rawMsg}'`
              : `"${rawMsg.replace(/"/g, '\\"')}"`;
          }
        }
        code += `${indent}raise ${excType}(${argExpr})\n`;
        break;
      }
      if (language !== 'java') {
        code += `${indent}${lang.comment('Throw node is Java-only')}\n`;
        break;
      }
      const excType = (node.exceptionType || '').trim() || 'RuntimeException';
      const mode = node.messageMode || 'literal';
      const rawMsg = (node.message || '').trim();
      let argExpr = '';
      if (rawMsg) {
        if (mode === 'expression') {
          argExpr = rawMsg;
        } else {
          // Literal mode: escape embedded double quotes so the emitted
          // Java compiles cleanly when the student types "she said \"hi\"".
          argExpr = `"${rawMsg.replace(/\\/g, '\\\\').replace(/"/g, '\\"')}"`;
        }
      }
      code += `${indent}throw new ${excType}(${argExpr});\n`;
      break;
    }

    case 'decision': {
      const outcomes = node.outcomes || [];
      outcomes.forEach((outcome, idx) => {
        // Build condition from the conditions array (new format from DecisionEditorPanel)
        // ADO #582 — an outcome with no condition is never given one. It used to
        // become `true` (or the hidden `x > 0` seed via expression1); now it is a
        // visible placeholder. Validation reports the outcome, so Run is blocked.
        let condition = null;

        // Raw expression mode (per-outcome). When the student typed a
        // raw expression — `students[i].gpa >= highestGpa`, etc. —
        // honor it directly, ignoring any stale guided `conditions`
        // left over from a mode switch. resolveFormulas expands any
        // FORMULA references inside the expression. This branch must
        // come BEFORE the guided-conditions block; otherwise codegen
        // silently falls back to guided/true.
        if (outcome.rawExpression && outcome.rawExpression.trim() !== '') {
          condition = convertRawStringEquality(resolveFormulas(outcome.rawExpression.trim()), lang.name);
        } else if (outcome.conditions && outcome.conditions.length > 0) {
          // Filter out invalid/empty conditions
          const validConditions = outcome.conditions.filter(cond =>
            (cond.resource && cond.resource.trim() !== '') ||
            (cond.resourceFullPath && cond.resourceFullPath.trim() !== '')
          );
          
          if (validConditions.length > 0) {
            const conditionParts = validConditions.map(cond => guidedConditionPart(cond, lang.name, resources, resolveFormulas));
            const isPython = lang.name === 'Python';
            // ADO #300 — Custom Condition Logic Is Met. When the
            // outcome carries `customLogicExpression`, substitute the
            // numbered references with the rendered conditions and
            // emit AND/OR/NOT as the language's logical operators.
            if (outcome.conditionLogic === 'CUSTOM' && outcome.customLogicExpression && outcome.customLogicExpression.trim()) {
              // eslint-disable-next-line global-require
              const { substituteCustomLogic } = require('@/components/visual-flow-builder/customConditionLogic');
              condition = substituteCustomLogic(
                outcome.customLogicExpression,
                conditionParts,
                isPython ? 'python' : 'c'
              );
            } else {
              const logic = outcome.conditionLogic === 'OR'
                ? (isPython ? ' or ' : ' || ')
                : (isPython ? ' and ' : ' && ');
              condition = conditionParts.join(logic);
            }
          }
          // If no valid conditions, condition stays missing (null)
        } else if (typeof outcome.condition === 'string' && outcome.condition.trim()) {
          // Fallback to simple condition string
          condition = resolveFormulas(outcome.condition);
        }
        // ADO #582 — `expression1/operator/expression2` was only ever written by
        // the store's hidden `x > 0` seed (the editor that wrote it is no
        // longer rendered), so it is not read as a condition any more.
        const missingCondition = (!outcome.isDefault || idx === 0) && (!condition || !String(condition).trim());
        if (missingCondition) condition = missingConditionPlaceholder(lang.name);
        
        // Debug: logStep for decision with resolved variable values
        if (isDebugMode && idx === 0 && !missingCondition && condition !== 'true') {
          if (language === 'c' || language === 'cpp') {
            // Build format string: replace known vars with format specifiers
            const condVarArgs = [];
            // Sort var names longest-first to avoid partial matches (e.g., 'ab' before 'a')
            const knownVars = resources.map(r => r.name).filter(n => n).sort((a, b) => b.length - a.length);
            // First escape any literal % in the condition (e.g., modulo).
            let resolvedFmt = condition.replace(/%/g, '%%');
            // Replace variable names with format specifiers in a SINGLE
            // left-to-right pass so the args line up with placeholder ORDER.
            // ADO #367 — a per-variable loop pushed all of one var's args before
            // the next, misaligning multi-variable conditions: `a > b && a > c`
            // became `%d > %d && %d > %d` with args [a, a, b, c] → "67 > 67 && 5
            // > 62" instead of "67 > 5 && 67 > 62". knownVars is sorted
            // longest-first so the alternation prefers the longer name.
            if (knownVars.length > 0) {
              const escaped = knownVars.map(v => v.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
              const anyVarRe = new RegExp(`\\b(${escaped.join('|')})\\b`, 'g');
              resolvedFmt = resolvedFmt.replace(anyVarRe, (m) => {
                const spec = getCFormatSpecifier(resolveVarType(m));
                condVarArgs.push(cFormatArg(m, spec));
                return spec;
              });
            }
            const cleanFmt = resolvedFmt.replace(/"/g, '\\"');
            code += `${indent}{\n`;
            code += `${indent}    int __branch = (${condition});\n`;
            if (condVarArgs.length > 0) {
              code += `${indent}    snprintf(__ctx, sizeof(__ctx), "%s:${cleanFmt}", __branch ? "TRUE" : "FALSE", ${condVarArgs.join(', ')});\n`;
            } else {
              code += `${indent}    snprintf(__ctx, sizeof(__ctx), "%s:${cleanFmt}", __branch ? "TRUE" : "FALSE");\n`;
            }
            code += `${indent}    ${getLogStepCall(language, 'Execution', 'Decision', node.id, '__ctx')}\n`;
            code += `${indent}}\n`;
          } else if (language === 'java') {
            // Java: use String concatenation with actual values
            code += `${indent}${getLogStepCall(language, 'Execution', 'Decision', node.id, `((${condition}) ? "TRUE" : "FALSE") + ":" + ${JSON.stringify(condition)}`)}\n`;
          } else if (language === 'python') {
            // Python: f-string resolves variables naturally
            const pyCondEscaped = condition.replace(/"/g, '\\"');
            code += `${indent}${getLogStepCall(language, 'Execution', 'Decision', node.id, `f"{'TRUE' if (${condition}) else 'FALSE'}:${pyCondEscaped}"`)}\n`;
          } else if (language === 'javascript') {
            const jsCondEscaped = condition.replace(/"/g, '\\"');
            code += `${indent}${getLogStepCall(language, 'Execution', 'Decision', node.id, `((${condition}) ? "TRUE" : "FALSE") + ":${jsCondEscaped}"`)}\n`;
          }
        }

        // Track outcome start for path-label highlighting
        const outcomeStartLine = nodeLineMap ? baseLineNum + countLines(code) : 0;

        // ADO #211 — skip empty default branch entirely (guard clause pattern).
        // An else { // No actions } is useless code.
        const branchNodes = outcome.nodes || [];
        if (outcome.isDefault && branchNodes.length === 0) {
          // Skip — no else block emitted
        } else {
          if (idx === 0) {
            code += `${indent}${lang.ifStart(condition)}\n`;
          } else if (outcome.isDefault) {
            code += `${indent}${lang.elseStart}\n`;
          } else {
            code += `${indent}${lang.elseIfStart(condition)}\n`;
          }

          increaseIndent();
          // Process nested nodes in this branch
          // ADO #211 — stop generating after a terminating node (End/Return)
          // ADO #304 — Throw is also terminal (transfers to nearest catch).
          for (const branchNode of branchNodes) {
            generateChild(branchNode);
            if (branchNode.type === 'end' || branchNode.type === 'return' || branchNode.type === 'break' || branchNode.type === 'continue' || branchNode.type === 'throw') break;
            if (branchNode.type === 'decision' && _branchTerminates([branchNode])) break;
          }

          // Add placeholder if branch is empty (non-default only — default was already skipped above)
          if (branchNodes.length === 0) {
            code += `${getIndent()}${lang.comment('No actions in this branch')}\n`;
          }
          decreaseIndent();
        }

        // Map outcome ID to its line range (if/else if block) for path-label highlighting
        if (nodeLineMap && outcome.id) {
          const outcomeEndLine = baseLineNum + countLines(code) - 1;
          nodeLineMap[outcome.id] = { startLine: outcomeStartLine, endLine: Math.max(outcomeEndLine, outcomeStartLine) };
        }
      });
      code += `${indent}${lang.ifEnd}\n`;
      break;
    }

    case 'switch': {
      // ADO #405 — switch / case / default. The branch structure reuses the
      // decision node's `outcomes` shape: each non-default outcome is a case
      // carrying `caseValue` + `breakAfter`; the `isDefault` outcome is the
      // default branch. C/C++/Java/JS emit a native `switch`; Python (no switch)
      // and Pseudocode fall back to an if/elif/else chain on the controlling
      // expression so all tracks stay valid.
      const switchExpr = resolveFormulas((node.switchExpression || '').trim()) || '/* expression */';
      const cases = node.outcomes || [];
      const isTerminal = (t) => t === 'end' || t === 'return' || t === 'break' || t === 'continue' || t === 'throw';

      if (['C', 'C++', 'Java', 'JavaScript'].includes(lang.name)) {
        code += `${indent}switch (${switchExpr}) {\n`;
        cases.forEach((c) => {
          increaseIndent();
          const labelLine = nodeLineMap ? baseLineNum + countLines(code) : 0;
          if (c.isDefault) {
            code += `${getIndent()}default:\n`;
          } else {
            const caseVal = resolveFormulas((c.caseValue || '').trim()) || '/* value */';
            code += `${getIndent()}case ${caseVal}:\n`;
          }
          increaseIndent();
          const branchNodes = c.nodes || [];
          let endedTerminal = false;
          for (const bn of branchNodes) {
            generateChild(bn);
            if (isTerminal(bn.type)) { endedTerminal = true; break; }
            if (bn.type === 'decision' && _branchTerminates([bn])) { endedTerminal = true; break; }
          }
          // Emit `break;` unless the student opted into fall-through, the body
          // already left the case (return/throw/…), or this is the default
          // (last, so a break is redundant).
          if (!c.isDefault && c.breakAfter !== false && !endedTerminal) {
            code += `${getIndent()}break;\n`;
          }
          decreaseIndent();
          decreaseIndent();
          if (nodeLineMap && c.id) {
            const endLine = baseLineNum + countLines(code) - 1;
            nodeLineMap[c.id] = { startLine: labelLine, endLine: Math.max(endLine, labelLine) };
          }
        });
        code += `${indent}}\n`;
      } else {
        // Python / Pseudocode — if/elif/else on `expr == caseValue`.
        let first = true;
        cases.forEach((c) => {
          if (c.isDefault) {
            if ((c.nodes || []).length === 0) return; // skip empty default
            code += `${indent}${first ? lang.ifStart('True') : lang.elseStart}\n`;
          } else {
            const caseVal = resolveFormulas((c.caseValue || '').trim()) || '/* value */';
            const cond = `${switchExpr} == ${caseVal}`;
            code += `${indent}${first ? lang.ifStart(cond) : lang.elseIfStart(cond)}\n`;
          }
          first = false;
          increaseIndent();
          const branchNodes = c.nodes || [];
          if (branchNodes.length === 0) {
            code += `${getIndent()}${lang.comment('No actions in this case')}\n`;
          } else {
            for (const bn of branchNodes) {
              generateChild(bn);
              if (isTerminal(bn.type)) break;
              if (bn.type === 'decision' && _branchTerminates([bn])) break;
            }
          }
          decreaseIndent();
        });
        if (lang.ifEnd) code += `${indent}${lang.ifEnd}\n`;
      }
      break;
    }
    
    case 'loop': {
      const loopType = node.loopType || 'while';
      const loopBody = node.loopBody || [];
      
      // Build condition string - check rawExpression first (Raw Expression mode)
      let conditionStr = 'true';

      if (node.rawExpression && node.rawExpression.trim() !== '') {
        conditionStr = convertRawStringEquality(resolveFormulas(node.rawExpression.trim()), lang.name);
      } else if (node.conditions && Array.isArray(node.conditions) && node.conditions.length > 0) {
        const validConditions = node.conditions.filter(c =>
          (c.resource && c.resource.trim() !== '') ||
          (c.resourceFullPath && c.resourceFullPath.trim() !== '')
        );
        if (validConditions.length > 0) {
          const conditionParts = validConditions.map(c => guidedConditionPart(c, lang.name, resources, resolveFormulas));

          // Handle condition logic (AND, OR, or CUSTOM)
          // Python uses 'and'/'or' instead of '&&'/'||'
          const isPythonLoop = lang.name === 'Python';
          if (node.conditionLogic === 'CUSTOM' && node.customLogicExpression) {
            // Custom logic like "1 AND (2 OR 3)"
            // Replace numbers with actual conditions
            conditionStr = node.customLogicExpression
              .replace(/(\d+)/g, (match) => {
                const idx = parseInt(match) - 1;
                return conditionParts[idx] || 'true';
              })
              .replace(/\bAND\b/gi, isPythonLoop ? 'and' : '&&')
              .replace(/\bOR\b/gi, isPythonLoop ? 'or' : '||');
          } else {
            const logic = node.conditionLogic === 'OR'
              ? (isPythonLoop ? ' or ' : ' || ')
              : (isPythonLoop ? ' and ' : ' && ');
            conditionStr = conditionParts.join(logic);
          }
        }
      } else if (node.condition && typeof node.condition === 'object') {
        // Fallback to single condition object (backward compatibility)
        const { resource: _resource, operator, value: _value } = node.condition;
        const resource = resolveFormulas(_resource);
        const value = resolveFormulas(_value);
        if (resource && resource.trim() !== '') {
          const operatorMap = {
            'equals': '==',
            'notEquals': '!=',
            'greaterThan': '>',
            'greaterOrEqual': '>=',
            'lessThan': '<',
            'lessOrEqual': '<=',
          };
          const op = operatorMap[operator] || operator || '<';
          let condValue = value || '0';
          // ADO #409 — pass resources so enum constants (FRIDAY) aren't quoted.
          const isStringComparison = needsStringQuotes(condValue, resources);
          if (isStringComparison) {
            condValue = `"${condValue}"`;
          }
          
          // In C, string comparisons must use strcmp()
          if (lang.name === 'C' && isStringComparison) {
            if (operator === 'equals') {
              conditionStr = `strcmp(${resource}, ${condValue}) == 0`;
            } else if (operator === 'notEquals') {
              conditionStr = `strcmp(${resource}, ${condValue}) != 0`;
            } else {
              conditionStr = `strcmp(${resource}, ${condValue}) ${op.replace('==', '').replace('!=', '')} 0`;
            }
          } else {
            conditionStr = `${resource} ${op} ${condValue}`;
          }
        }
      } else if (typeof node.condition === 'string') {
        conditionStr = resolveFormulas(node.condition);
      }
      
      if (loopType === 'for') {
        // Use forConfig for numeric for loops
        const forConfig = node.forConfig || {};
        let init, condition, increment;
        
        if (forConfig.useExpressionMode && forConfig.init) {
          // Expression mode - use raw expressions
          init = forConfig.init;
          condition = forConfig.conditionExpr || 'true';
          increment = forConfig.increment;
        } else {
          // Guided mode. A For is `for (init; condition; iteration)`, and the
          // CONDITION is an ordinary loop condition — it lives in node.conditions,
          // exactly where a While keeps its own, and `conditionStr` above has
          // already built it. forConfig supplies only the two parts a While does
          // not have: where the counter starts, and how it moves.
          //
          // It used to supply a third, `end`, and the condition was hardcoded to
          // `counter < end`. That made the field labelled "End" exclusive, so
          // Start 1 / End 15 printed 1..14, and it made counting down impossible
          // — step -1 emitted `i < end`, which is false immediately, so the loop
          // ran zero times and said nothing about it. Neither was a lesson; both
          // were the tool disagreeing with its own labels.
          //
          // ADO #582 — `end` is NOT a fallback any more. The editor used to seed
          // it as '10' and no longer shows it, so a For with no condition ran a
          // hidden `i < 10`. Now it emits a visible placeholder; validation says
          // "Say when the loop should stop" and Run is blocked.
          const counter = forConfig.counter || 'i';
          const start = forConfig.start || '0';
          const step = String(forConfig.step || '1');
          const guided = conditionStr && conditionStr !== 'true';
          const forCondition = guided ? conditionStr : missingConditionPlaceholder(lang.name);
          const stepIsNegative = step.trim().startsWith('-');

          if (lang.name === 'Python') {
            // Python's `for` is a for-each over a range, not a three-part loop.
            // A range can express `counter <op> bound` and nothing else, so a
            // condition that does not have that shape is emitted as a `while` —
            // which IS the Python equivalent of a general C for loop, not a
            // fudge. Getting this wrong would break the promise the course is
            // built on: one flow, the same output in all four languages.
            const rangeArgs = pythonRangeArgs(node, counter, start, step, resources);
            if (rangeArgs) {
              code += `${indent}for ${counter} in range(${rangeArgs}):\n`;
            } else {
              code += `${indent}${counter} = ${start}\n`;
              code += `${indent}while ${forCondition}:\n`;
            }
            increaseIndent();
            loopBody.forEach(bodyNode => {
              generateChild(bodyNode);
            });
            if (loopBody.length === 0) {
              code += `${getIndent()}pass  # Loop body\n`;
            }
            if (!rangeArgs) {
              // The iteration clause has nowhere else to go in a while form.
              code += `${getIndent()}${counter} += ${step}\n`;
            }
            decreaseIndent();
            break; // Exit early for Python for loop
          } else {
            // C, Java, JavaScript standard for loop
            const dataType = lang.name === 'JavaScript' ? 'let ' : 'int ';
            init = `${dataType}${counter} = ${start}`;
            condition = forCondition;
            if (step === '1') {
              increment = `${counter}++`;
            } else if (step === '-1') {
              increment = `${counter}--`;
            } else if (stepIsNegative) {
              increment = `${counter} -= ${step.trim().slice(1)}`;
            } else {
              increment = `${counter} += ${step}`;
            }
          }
        }
        
        code += `${indent}${lang.forStart(init, condition, increment)}\n`;
      } else if (loopType === 'forEach') {
        // Use forEachConfig for collection iteration
        const forEachConfig = node.forEachConfig || {};
        // Use collectionFullPath if available (for struct members like person.addresses)
        const collection = forEachConfig.collectionFullPath || forEachConfig.collection || 'items';
        const itemVar = forEachConfig.itemVariable || 'item';
        
        if (lang.name === 'Python') {
          code += `${indent}for ${itemVar} in ${collection}:\n`;
        } else if (lang.name === 'JavaScript') {
          code += `${indent}for (const ${itemVar} of ${collection}) {\n`;
        } else if (lang.name === 'Java') {
          // Java needs type, default to Object
          code += `${indent}for (var ${itemVar} : ${collection}) {\n`;
        } else if (lang.name === 'C++') {
          // C++ range-based for
          code += `${indent}for (auto ${itemVar} : ${collection}) {\n`;
        } else {
          // C doesn't support forEach, fallback to comment
          code += `${indent}${lang.comment(`forEach not supported in C - iterate ${collection}`)}\n`;
          break;
        }
        
        increaseIndent();
        loopBody.forEach(bodyNode => {
          generateChild(bodyNode);
        });
        if (loopBody.length === 0) {
          if (lang.name === 'Python') {
            code += `${getIndent()}pass  # Loop body\n`;
          } else {
            code += `${getIndent()}${lang.comment('Loop body')}\n`;
          }
        }
        decreaseIndent();

        if (lang.name !== 'Python') {
          code += `${indent}}\n`;
        }
        break; // Exit early for forEach
      } else if (loopType === 'doWhile') {
        // do...while - currently disabled but handle if somehow used
        code += `${indent}${lang.doWhileStart}\n`;
      } else {
        // Default: while loop
        code += `${indent}${lang.whileStart(conditionStr)}\n`;
      }
      
      increaseIndent();

      // Debug: logStep at top of loop body with counter + condition
      if (isDebugMode) {
        const loopIndent = getIndent();
        const forCfg = node.forConfig || {};
        // Only use forConfig counter for actual 'for' loops. While loops
        // don't have a counter variable — using forCfg.counter on a while
        // loop references a variable that doesn't exist in the generated
        // code, causing a compilation error (ADO #205–208 fix).
        const counter = loopType === 'for'
          ? (forCfg.counter || 'i')
          : loopType === 'forEach'
            ? (node.forEachConfig?.itemVariable || 'item')
            : null;
        // The trace says the counter's value and nothing else. It used to add
        // "i<end=TRUE" from the old hidden `forConfig.end` — a bound the loop no
        // longer reads (the stop is the loop's own condition), so the claim was
        // false, and the trace parser took everything after "=" as the counter's
        // value ("0, 0<10=TRUE"). Debug trace text only; the program is the same.
        if (counter && (language === 'c' || language === 'cpp')) {
          code += `${loopIndent}snprintf(__ctx, sizeof(__ctx), "ITER:${counter}=%d", ${counter});\n`;
          code += `${loopIndent}${getLogStepCall(language, 'Execution', 'Loop', node.id, '__ctx')}\n`;
        } else if (counter && language === 'java') {
          code += `${loopIndent}${getLogStepCall(language, 'Execution', 'Loop', node.id, `"ITER:${counter}=" + ${counter}`)}\n`;
        } else if (counter && language === 'python') {
          code += `${loopIndent}${getLogStepCall(language, 'Execution', 'Loop', node.id, `f"ITER:${counter}={${counter}}"`)}\n`;
        } else if (counter && language === 'javascript') {
          code += `${loopIndent}${getLogStepCall(language, 'Execution', 'Loop', node.id, '`ITER:' + counter + '=${' + counter + '}`')}\n`;
        } else {
          // While loop — no counter
          code += `${loopIndent}${getLogStepCall(language, 'Execution', 'Loop', node.id, '"ITER:loop"')}\n`;
        }
      }

      // Process loop body nodes
      loopBody.forEach(bodyNode => {
        generateChild(bodyNode);
      });
      
      // Add placeholder if loop body is empty
      if (loopBody.length === 0) {
        if (lang.name === 'Python') {
          code += `${getIndent()}pass  # Loop body\n`;
        } else {
          code += `${getIndent()}${lang.comment('Loop body')}\n`;
        }
      }
      decreaseIndent();
      
      if (loopType === 'for') {
        code += `${indent}${lang.forEnd}\n`;
      } else if (loopType === 'doWhile') {
        code += `${indent}${lang.doWhileEnd(conditionStr)}\n`;
      } else {
        code += `${indent}${lang.whileEnd}\n`;
      }
      break;
    }
    
    case 'try_catch': {
      const branches = node.branches || [];
      const isPython = lang.name === 'Python';
      const isCpp = lang.name === 'C++';

      // Helper to generate child nodes for a branch
      const generateBranchBody = (branchNodes) => {
        increaseIndent();
        if (branchNodes.length === 0) {
          if (isPython) {
            code += `${getIndent()}pass  ${lang.comment('No actions in this branch')}\n`;
          } else {
            code += `${getIndent()}${lang.comment('No actions in this branch')}\n`;
          }
        } else {
          branchNodes.forEach(childNode => {
            generateChild(childNode);
          });
        }
        decreaseIndent();
      };

      // TRY block
      if (isPython) {
        code += `${indent}try:\n`;
      } else {
        code += `${indent}try {\n`;
      }
      const tryBranch = branches.find(b => b.type === 'try');
      generateBranchBody(tryBranch?.nodes || []);
      if (!isPython) {
        code += `${indent}}\n`;
      }

      // CATCH / EXCEPT blocks
      const catchBranches = branches.filter(b => b.type === 'catch');
      catchBranches.forEach(catchBranch => {
        const excType = catchBranch.exceptionType || '';
        const excVar = catchBranch.exceptionVar || 'e';

        if (isPython) {
          if (excType) {
            code += `${indent}except ${excType} as ${excVar}:\n`;
          } else {
            code += `${indent}except Exception as ${excVar}:\n`;
          }
        } else if (isCpp) {
          if (excType === '...') {
            code += `${indent}catch (...) {\n`;
          } else if (excType) {
            code += `${indent}catch (const ${excType}& ${excVar}) {\n`;
          } else {
            code += `${indent}catch (...) {\n`;
          }
        } else {
          // Java / JavaScript
          if (lang.name === 'JavaScript') {
            code += `${indent}catch (${excVar}) {\n`;
          } else {
            code += `${indent}catch (${excType || 'Exception'} ${excVar}) {\n`;
          }
        }
        generateBranchBody(catchBranch.nodes || []);
        if (!isPython) {
          code += `${indent}}\n`;
        }
      });

      // ELSE block (Python only)
      const elseBranch = branches.find(b => b.type === 'else');
      if (elseBranch && isPython) {
        code += `${indent}else:\n`;
        generateBranchBody(elseBranch.nodes || []);
      }

      // FINALLY block (not for C++)
      const finallyBranch = branches.find(b => b.type === 'finally');
      if (finallyBranch && !isCpp) {
        if (isPython) {
          code += `${indent}finally:\n`;
        } else {
          code += `${indent}finally {\n`;
        }
        generateBranchBody(finallyBranch.nodes || []);
        if (!isPython) {
          code += `${indent}}\n`;
        }
      }
      break;
    }

    case 'subflow': {
      // Subflow - generate as function call with parameter mapping
      const inputMappings = node.inputMappings || {};
      const outputMapping = node.outputMapping;

      // Try to get the flow name from node or use label
      const subflowName = node.targetFlowName || node.label || 'subflow';

      // Build the argument list from input mappings (formula names inlined).
      // ADO #392 — order arguments by the TARGET flow's PARAMETER order, not by
      // the inputMappings object's key order. The two can differ (keys are saved
      // in the order the student filled them), which silently emitted swapped
      // args, e.g. updateGpa(n, 9.4, 2) instead of updateGpa(n, 2, 9.4).
      // ADO #443 — prefer the id match: a method flow and its Contract Flow can
      // share a name (a constructor flow named `Dog` collides with `Dog`'s
      // contract), and a name-first match could return the contract (wrong
      // flowType), breaking the super().__init__ vs super().Dog decision.
      const targetFlow = (_allFlowsForCodegen || []).find((f) => f && f.id === node.targetFlowId)
        || (_allFlowsForCodegen || []).find((f) => f && f.name === node.targetFlowName);
      const orderedKeys = targetFlow && Array.isArray(targetFlow.parameters) && targetFlow.parameters.length > 0
        ? targetFlow.parameters
            .map((p) => p && p.name)
            .filter((name) => name && Object.prototype.hasOwnProperty.call(inputMappings, name))
        : Object.keys(inputMappings); // fallback: legacy key order
      const args = orderedKeys
        .map((k) => inputMappings[k])
        .filter(v => v && v !== '__literal__')
        .map(v => resolveFormulas(v))
        .join(', ');

      // ADO #308 — receiver-aware call expression. Three receiver kinds
      // are stored on the node (legacy = 'none'):
      //   'none'      → "Same class" on Java. Codegen auto-promotes to
      //                 `this.method()` when the enclosing flow is an
      //                 instance method / constructor, otherwise emits a
      //                 bare `method()`. Non-Java tracks always emit a
      //                 bare call (their free-function / module-function
      //                 semantics are real).
      //   'this'      → legacy / explicit; emits `this.method()` (Java/JS)
      //                 or `self.method()` (Python). Kept for backward
      //                 compatibility with any node saved before the
      //                 picker collapsed to single 'Same class' option.
      //   'instance'  → emits `<resourceName>.method()` resolved from
      //                 receiverRef. Defensive: if the reference is
      //                 dangling, fall through to a bare call.
      const receiverKind = node.receiverKind || 'none';
      let callExpr;
      // ADO #443 — call a parent-class method via super(). For the parent
      // constructor (the target flow's flowType is 'constructor') Python uses
      // `super().__init__(args)`; Java uses `super(args)`.
      if (receiverKind === 'super') {
        const isCtor = targetFlow && targetFlow.flowType === 'constructor';
        if (lang.name === 'Java') {
          callExpr = isCtor ? `super(${args})` : `super.${subflowName}(${args})`;
        } else {
          const methodName = isCtor ? '__init__' : subflowName;
          callExpr = `super().${methodName}(${args})`;
        }
      } else {
        const receiverName = (() => {
          if (receiverKind === 'this') {
            if (lang.name === 'Python') return 'self';
            return 'this'; // Java + JS + C++ (when we get there)
          }
          if (receiverKind === 'instance' && node.receiverRef) {
            // receiverRef stores a resource id; resolve to its name.
            const target = (resources || []).find(r => r.id === node.receiverRef);
            return target?.name || null;
          }
          // ADO #308 — Java "Same class" auto-promotion. Static and main
          // contexts must NOT prepend `this.` (the JLS rejects it from a
          // static frame). Instance methods + constructors emit
          // `this.method()` so the receiver is visible in the generated
          // source — same compiled bytecode, clearer pedagogy.
          if (receiverKind === 'none'
              && lang.name === 'Java'
              && (_currentFlowType === 'instance' || _currentFlowType === 'constructor')) {
            return 'this';
          }
          return null;
        })();
        callExpr = receiverName
          ? `${receiverName}.${subflowName}(${args})`
          : `${subflowName}(${args})`;
      }

      // ADO #368 — emit a CALL event so the trace shows the subflow call and
      // the memory model pushes a frame for the callee (its body is now
      // instrumented too). Args fill in from the callee's own write events.
      if (isDebugMode) {
        // Carry the ARGUMENTS. The parser reads "CALL:fnName:param=value" and
        // this emitted "CALL:hra:" -- nothing after the colon -- so the callee's
        // frame opened with its parameter showing `??`. The comment here used to
        // say args "fill in from the callee's own write events", but a parameter
        // is never written by an assignment, so nothing ever filled it in.
        //
        // Types come from the TARGET flow's declared parameters, which is the
        // only place they are known: the argument at the call site may be a
        // literal or an expression with no resource to look up.
        const _dbgParams = (targetFlow && Array.isArray(targetFlow.parameters))
          ? targetFlow.parameters : [];
        const _dbgPairs = (lang.name === 'C' || lang.name === 'C++')
          ? orderedKeys
              .map((k) => {
                const p = _dbgParams.find((q) => q && q.name === k);
                const v = inputMappings[k];
                if (!p || !v || v === '__literal__') return null;
                return { fmt: `${k}=${getCFormatSpecifier(p.type || p.dataType || 'int')}`,
                         arg: resolveFormulas(v) };
              })
              .filter(Boolean)
          : [];
        if (_dbgPairs.length) {
          code += `${indent}snprintf(__ctx, sizeof(__ctx), "CALL:${subflowName}:${_dbgPairs.map((x) => x.fmt).join(', ')}", ${_dbgPairs.map((x) => x.arg).join(', ')});\n`;
          code += `${indent}${getLogStepCall(language, 'Execution', `Call ${subflowName}`, node.id, '__ctx')}\n`;
        } else {
          code += `${indent}${getLogStepCall(language, 'Execution', `Call ${subflowName}`, node.id, `"CALL:${subflowName}:"`)}\n`;
        }
      }

      // Generate the function call
      if (outputMapping) {
        // Has return value - assign to variable
        if (lang.name === 'Python') {
          code += `${indent}${outputMapping} = ${callExpr}\n`;
        } else {
          code += `${indent}${outputMapping} = ${callExpr};\n`;
        }
      } else {
        // No return value - just call
        if (lang.name === 'Python') {
          code += `${indent}${callExpr}\n`;
        } else if (lang.name === 'Pseudocode') {
          code += `${indent}CALL ${callExpr}\n`;
        } else {
          code += `${indent}${callExpr};\n`;
        }
      }
      break;
    }
    
    case 'action': {
      // Action node - library function call or expression
      // Support both new expression format and legacy operationData format
      
      const actionData = node.actionData || {};
      const expression = actionData.expression;
      const assignTo = actionData.assignTo?.fullPath;
      
      // New format: expression-based (Salesforce Formula Builder style)
      if (expression) {
        if (assignTo) {
          if (lang.name === 'Python') {
            code += `${indent}${assignTo} = ${expression}\n`;
          } else {
            code += `${indent}${assignTo} = ${expression};\n`;
          }
        } else {
          if (lang.name === 'Python') {
            code += `${indent}${expression}\n`;
          } else {
            code += `${indent}${expression};\n`;
          }
        }
        // ADO #368 — heap trace. ALLOC on malloc/calloc/realloc into a simple
        // pointer target; FREE on free(x). Addressed by the REAL pointer value
        // (%p), so heap node identity & links are faithful (C/C++ only).
        if (isDebugMode && (language === 'c' || language === 'cpp')) {
          const simpleTarget = assignTo && /^[A-Za-z_]\w*$/.test(assignTo);
          if (simpleTarget && /\b(malloc|calloc|realloc)\s*\(/.test(expression)) {
            const tgtRes = resources.find(r => r.name === assignTo);
            const pointee = String(tgtRes?.pointsToType || '').replace(/^struct[_\s]+/i, '').replace(/\*+$/, '').trim();
            const typeLabel = pointee ? `struct ${pointee}` : 'block';
            code += `${indent}if (${assignTo}) { snprintf(__ctx, sizeof(__ctx), "ALLOC:%p:%zu:${typeLabel}:${assignTo}", (void*)${assignTo}, sizeof(*${assignTo})); ${getLogStepCall(language, 'Heap', 'Allocate', node.id, '__ctx')} }\n`;
          } else if (!assignTo) {
            const fm = expression.match(/\bfree\s*\(\s*([A-Za-z_]\w*)\s*\)/);
            if (fm) {
              code += `${indent}snprintf(__ctx, sizeof(__ctx), "FREE:%p", (void*)${fm[1]});\n`;
              code += `${indent}${getLogStepCall(language, 'Heap', 'Free', node.id, '__ctx')}\n`;
            }
          }
        }
        break;
      }

      // Legacy format: operationData-based
      const operationData = node.operationData || actionData;
      const args = node.args || actionData.args || {};
      const receiverFullPath = node.receiverFullPath || actionData.receiverFullPath;
      const assignToFullPath = node.assignToFullPath || actionData.assignTo?.fullPath;
      
      if (!operationData || !operationData.method) {
        code += `${indent}${lang.comment('Action: Not configured')}\n`;
        break;
      }
      
      const { kind, method, owner, params } = operationData;
      
      // Build arguments string (formula names inlined via resolveFormulas)
      const argValues = (params || []).map(p => {
        const argVal = args[p.name];
        if (argVal?.fullPath) return resolveFormulas(argVal.fullPath);
        if (argVal?.value !== undefined && argVal.value !== '') return resolveFormulas(argVal.value);
        if (p.optional) return null;
        return `/* ${p.name} */`;
      }).filter(v => v !== null).join(', ');
      
      // Build the function call
      let callCode = '';
      
      if (kind === 'STATIC_METHOD') {
        // Static method: Owner.method(args)
        callCode = `${owner}.${method}(${argValues})`;
      } else if (kind === 'INSTANCE_METHOD') {
        // Instance method: receiver.method(args)
        const receiver = receiverFullPath || '/* target */';
        callCode = `${receiver}.${method}(${argValues})`;
      } else {
        // Function: method(args)
        callCode = `${method}(${argValues})`;
      }
      
      // Add assignment if specified
      if (assignToFullPath && operationData.returns && operationData.returns !== 'void' && operationData.returns !== 'None') {
        if (lang.name === 'Python') {
          code += `${indent}${assignToFullPath} = ${callCode}\n`;
        } else {
          code += `${indent}${assignToFullPath} = ${callCode};\n`;
        }
      } else {
        if (lang.name === 'Python') {
          code += `${indent}${callCode}\n`;
        } else {
          code += `${indent}${callCode};\n`;
        }
      }
      break;
    }

    case 'return': {
      // Return node — emits a return statement for the current function flow
      const expr = (node.returnValue || '').trim();
      // ADO #368 — RETURN event BEFORE the return statement (it exits the
      // function) so the memory model pops the callee's frame.
      //
      // Carry the VALUE. debugTraceParser reads "RETURN:fnName:var=value" and
      // codegen emitted a bare "RETURN::", so the panel showed a return with
      // nothing in it -- a student stepping a subflow call could not see what
      // came back, which is the one thing the call is for.
      //
      // The expression goes into a temp first. Emitting it into snprintf and
      // again into `return` would evaluate it twice, and `return f(x)` would
      // then call f twice.
      const _dbgRetType = (_currentReturnType || '').trim();
      const _dbgCanTrace = isDebugMode && expr
        && (lang.name === 'C' || lang.name === 'C++')
        && _dbgRetType && !/^void$/i.test(_dbgRetType);
      if (_dbgCanTrace) {
        const tmp = `__ret_${(node.id || 'r').replace(/[^a-zA-Z0-9_]/g, '')}`;
        code += `${indent}${_dbgRetType} ${tmp} = ${expr};\n`;
        code += `${indent}snprintf(__ctx, sizeof(__ctx), "RETURN:${_currentFlowName || ''}:${getCFormatSpecifier(_dbgRetType)}", ${tmp});\n`;
        code += `${indent}${getLogStepCall(language, 'Execution', 'Return', node.id, '__ctx')}\n`;
        code += `${indent}return ${tmp};\n`;
        break;
      }
      if (isDebugMode) {
        code += `${indent}${getLogStepCall(language, 'Execution', 'Return', node.id, '"RETURN::"')}\n`;
      }
      if (lang.name === 'Python') {
        code += expr ? `${indent}return ${expr}\n` : `${indent}return\n`;
      } else if (lang.name === 'Pseudocode') {
        code += expr ? `${indent}RETURN ${expr}\n` : `${indent}RETURN\n`;
      } else {
        // C, C++, Java, JavaScript
        code += expr ? `${indent}return ${expr};\n` : `${indent}return;\n`;
      }
      break;
    }

    case 'file_io': {
      // ADO #438 — Python file I/O as a self-contained context-manager block:
      //   with open(path, mode) as f:
      //       f.write(text)              # write / append
      //       var = f.read()/readlines() # read
      // Python-only — C uses the Action node's fopen/fprintf/fscanf path.
      const indent = getIndent();
      if (lang.name !== 'Python') {
        code += `${indent}${lang.comment('File I/O node is Python-only (use the Action node for C file I/O)')}\n`;
        break;
      }
      const op = node.fileOp || 'write';
      const fvar = (node.fileVar || 'f').trim() || 'f';
      const bodyIndent = indent + lang.indent;
      const isReadOp = op === 'read' || op === 'readlines';

      // Path: a literal filename is quoted (`data.txt` → `"data.txt"`); an
      // expression (a variable holding the path) is emitted verbatim.
      const rawPath = String(node.filePath || '').trim();
      const pathExpr = node.pathMode === 'expression'
        ? rawPath
        : (/^(['"]).*\1$/.test(rawPath) ? rawPath : `"${rawPath}"`);
      if (!rawPath) break; // nothing to emit without a path

      const mode = op === 'append' ? 'a' : op === 'write' ? 'w' : 'r';
      code += `${indent}with open(${pathExpr}, "${mode}") as ${fvar}:\n`;

      if (isReadOp) {
        const target = String(node.targetVar || 'content').trim() || 'content';
        const call = op === 'readlines' ? 'readlines' : 'read';
        code += `${bodyIndent}${target} = ${fvar}.${call}()\n`;
      } else {
        const rawText = String(node.writeText ?? '');
        const textExpr = node.textMode === 'expression'
          ? rawText.trim()
          : (/^(['"]).*\1$/.test(rawText.trim()) ? rawText.trim() : `"${rawText.replace(/\\/g, '\\\\').replace(/"/g, '\\"')}"`);
        code += `${bodyIndent}${fvar}.write(${textExpr})\n`;
      }
      break;
    }

    case 'break': {
      // ADO #212 — Break node inside a loop = exit loop immediately
      if (lang.name === 'Python') {
        code += `${indent}break\n`;
      } else if (lang.name === 'Pseudocode') {
        code += `${indent}BREAK\n`;
      } else {
        code += `${indent}break;\n`;
      }
      break;
    }

    case 'continue': {
      // ADO #212 — Continue node inside a loop = skip to next iteration
      if (lang.name === 'Python') {
        code += `${indent}continue\n`;
      } else if (lang.name === 'Pseudocode') {
        code += `${indent}CONTINUE\n`;
      } else {
        code += `${indent}continue;\n`;
      }
      break;
    }

    case 'end': {
      // ADO #211 — End node inside a Decision branch = void early exit.
      // Top-level End nodes are filtered by processableNodes and never
      // reach here. Only branch-internal End nodes hit this case.
      if (lang.name === 'Python') {
        code += `${indent}return\n`;
      } else if (lang.name === 'Pseudocode') {
        code += `${indent}RETURN\n`;
      } else {
        code += `${indent}return;\n`;
      }
      break;
    }

    case 'placeholder':
      // A step still to choose (utils/placeholderStep.js): no code until it is chosen.
      code += `${indent}${lang.comment(`Step to choose${node.label && node.label !== 'Step to choose' ? `: ${node.label}` : ''}`)}\n`;
      break;

    default:
      // Unknown node type - add as comment
      code += `${indent}${lang.comment(`Unknown node: ${node.type} - ${node.label || ''}`)}\n`;
  }

  return code;
}

// ADO #240 — helpers for emitting Java sessions (nested + sibling).
// The primary public class is emitted by the file header that the
// caller passes in via options. These helpers handle the inner/sibling
// types that can live in the same .java file.

function findPrimarySession(sessions) {
  if (!sessions || sessions.length === 0) return null;
  return sessions.find(s =>
    (s.kind === 'class' || s.kind === 'record')
    && (s.accessSpecifier === 'public' || s.accessSpecifier == null)
    && !s.parentSessionId
  ) || null;
}

// Emit a nested session (`staticNested` or `innerClass`) inside its parent
// class braces. Returns a Java block beginning with a leading newline.
function emitJavaInnerSessionBlock(session, allFlows, lang, allResources, language, fnOpts) {
  const sessionFlows = allFlows.filter(f => f.sessionId === session.id);
  const staticKeyword = session.kind === 'staticNested' ? 'static ' : '';
  const nestedFnOpts = { ...fnOpts, className: session.name };
  let block = `\n    ${staticKeyword}class ${session.name} {\n`;
  sessionFlows.forEach(flow => {
    block += generateFunctionCode(flow, lang, allResources, language, nestedFnOpts);
    block += '\n';
  });
  block += '    }\n';
  return block;
}

// Emit a sibling session (`siblingClass`) at file level, after the primary
// class is closed. Sibling classes are package-private (no `public`).
function emitJavaSiblingSessionBlock(session, allFlows, lang, allResources, language, fnOpts) {
  const sessionFlows = allFlows.filter(f => f.sessionId === session.id);
  const siblingFnOpts = { ...fnOpts, className: session.name };
  let block = `\nclass ${session.name} {\n`;
  sessionFlows.forEach(flow => {
    block += generateFunctionCode(flow, lang, allResources, language, siblingFnOpts);
    block += '\n';
  });
  block += '}\n';
  return block;
}

// ADO #441 — which sessions are real Python classes to wrap in `class Name:`.
// Every file gets a default `kind:'class'` session, so the MAIN file's session
// (the one that holds the entry flow) must be excluded — otherwise `main()`
// would be wrapped in `class main:`. A genuine class session is a class-kind
// session that does NOT own the entry flow.
function pythonClassSessions(sessions, entryFlow) {
  const entrySessionId = entryFlow ? entryFlow.sessionId : undefined;
  return (sessions || []).filter(s => s && s.kind === 'class' && s.id !== entrySessionId);
}

// ADO #441 — emit a Python class block:
//   class Name[(Parent, ...)]:
//       def __init__(self, ...): ...
//       def method(self, ...): ...
// Parents come from the contract flow's configuration node (parentsList + ABC).
// An empty class emits `class Name:\n    pass`. Each method's generated `def`
// block is indented one level into the class body.
function emitPythonClassBlock(session, allFlows, lang, allResources, language, fnOpts) {
  const contractFlow = allFlows.find(f => f.flowType === 'contract' && f.sessionId === session.id);
  const config = (contractFlow?.nodes || []).find(n => n.type === 'configuration') || {};
  const parents = Array.isArray(config.parentsList) ? config.parentsList.filter(Boolean) : [];
  if (config.isABC && !parents.includes('ABC')) parents.push('ABC');
  const header = parents.length ? `class ${session.name}(${parents.join(', ')}):` : `class ${session.name}:`;

  // ADO #446 — class variables (shared across instances) are emitted at the
  // class-body level (`species = "Canis"`), before the methods. These are
  // classField resources flagged isClassVariable; instance attributes (plain
  // classField) stay suppressed here and appear as self.attr in __init__.
  const classVars = (allResources || []).filter(r => {
    if (!r || r.location !== 'classField' || !r.isClassVariable) return false;
    // Match the outline's per-class scoping (attributesForSession): prefer
    // sessionId, fall back to fileId, then "belongs to all" for legacy fields.
    // Keeps the emitted class body consistent with what the outline shows.
    if (r.sessionId) return r.sessionId === session.id;
    if (r.fileId) return r.fileId === session.fileId;
    return true;
  });
  const varLines = classVars.map(r => {
    const raw = (r.initialValue ?? '').toString().trim();
    let val;
    if (raw === '') val = 'None';
    else if ((r.dataType === 'str' || r.dataType === 'string') && needsStringQuotes(raw, allResources)) val = `"${raw}"`;
    else val = raw;
    return `${lang.indent}${r.name} = ${val}`;
  });

  // Constructor + methods for this class (contract + entry excluded). Constructor first.
  const methodFlows = allFlows
    .filter(f => f.sessionId === session.id && f.flowType !== 'contract' && !f.isEntry)
    .sort((a, b) => (a.flowType === 'constructor' ? -1 : 0) - (b.flowType === 'constructor' ? -1 : 0));

  if (methodFlows.length === 0 && varLines.length === 0) {
    return `${header}\n${lang.indent}pass\n`;
  }

  const classFnOpts = { ...fnOpts, className: session.name, pyClass: session.name };
  let block = `${header}\n`;
  if (varLines.length) block += varLines.join('\n') + '\n';
  methodFlows.forEach((flow, i) => {
    const raw = generateFunctionCode(flow, lang, allResources, language, classFnOpts);
    const indented = raw.replace(/\n$/, '').split('\n')
      .map(line => (line.trim() ? lang.indent + line : line)).join('\n');
    block += ((i > 0 || varLines.length) ? '\n' : '') + indented + '\n';
  });
  return block;
}

/**
 * Predicate: a flow has at least one node beyond start/end.
 * Configuration / Resources nodes count as content.
 */
function flowHasContent(f) {
  return (f.nodes || []).some(n => n.type !== 'start' && n.type !== 'end');
}

/**
 * Predicate: a flow is marked as the program entry.
 * Accepts both legacy `flowType: 'main'` and the newer `isEntry: true` flag,
 * so historical projects and current ones both resolve.
 */
function isMarkedEntryFlow(f) {
  return f.flowType === 'main' || f.isEntry === true;
}

/**
 * Predicate: a flow is a Contract Flow (data members + signatures only).
 * Contract flows must NEVER be emitted as functions — see ADO #248 / #278.
 */
function isContractFlow(f) {
  return f.flowType === 'contract';
}

/**
 * ADO #580 — which flow is the program's entry. One rule for every C path
 * (Run's assembleProject, the old walker, the multi-file build):
 *
 *   1. C / C++: a non-contract flow NAMED `main` is the entry, whatever it is
 *      marked. A project saved with `indexOf` flagged entry (or nothing
 *      flagged) and an ordinary function called main must build ONE
 *      `int main(void)`, never indexOf's body as main plus a `void main`.
 *   2. A flow marked entry (`flowType === 'main'` or `isEntry`) with content.
 *   3. ADO #218 — a marked-but-empty entry yields to a same-named sibling
 *      with content (never a Contract, ADO #358).
 *   4. Nothing marked: only a flow named main (#549). Never "the first flow
 *      with content": that made a helper main.
 *
 * @param {Object[]} flows
 * @param {{ language?: string }} options
 * @returns {Object|null}
 */
export function resolveEntryFlow(flows, options = {}) {
  const list = (flows || []).filter(Boolean);
  const lang = String(options.language || '').toLowerCase();
  const markedEntry = list.find((f) => !isContractFlow(f) && isMarkedEntryFlow(f));
  if ((lang === 'c' || lang === 'cpp') && markedEntry && markedEntry.name !== 'main') {
    const named = list.filter((f) => !isContractFlow(f) && f.name === 'main');
    const pick = named.find(flowHasContent) || named[0];
    if (pick) return pick;
  }
  if (markedEntry && flowHasContent(markedEntry)) return markedEntry;
  if (markedEntry) {
    const sameNameWithContent = list.find((f) =>
      f !== markedEntry && f.name === markedEntry.name && flowHasContent(f) && !isContractFlow(f)
    );
    return sameNameWithContent || markedEntry;
  }
  // Nothing is flagged entry. Older projects never set the flag, and their main is the flow NAMED main, so
  // that is the fallback. Anything else has NO entry: no main is written, and it fails to build, as it
  // should. Falling back to flows[0] made the Contract itself main ("Unknown node: configuration"); falling
  // back to "any other flow" made a learner's helper main, so a project with only `welcome` built and passed
  // an item whose whole task was to add main (#549). A library (My Toolbox) has no main either.
  const namedMain = list.filter((f) => !isContractFlow(f) && String(f.name || '').toLowerCase() === 'main');
  return namedMain.find(flowHasContent) || namedMain[0] || null;
}

/**
 * Single source of truth for splitting a project's flows into the entry
 * (main / driver) flow vs. every other flow that should be emitted as a
 * standalone function. Used by both `generateProgramCode` and
 * `generateCodeWithLineMap` so the two parallel pipelines can't drift.
 *
 * Rules: `options.skipMain` selects no entry; otherwise resolveEntryFlow
 * (ADO #580) picks it — see there.
 *
 * `otherFlows` excludes the entry, contract flows (ADO #248 Phase 6b /
 * #278), and any flow with no content.
 *
 * @param {Array<Object>} flows
 * @param {{ skipMain?: boolean, language?: string }} options
 * @returns {{ entryFlow: Object|null, otherFlows: Array<Object> }}
 */
function partitionFlows(flows, options = {}) {
  const entryFlow = options.skipMain ? null : resolveEntryFlow(flows, options);

  const otherFlows = flows.filter(f =>
    f !== entryFlow && flowHasContent(f) && !isContractFlow(f)
  );

  return { entryFlow, otherFlows };
}

/**
 * Build the function-name → return-type map that buildOutputStatement /
 * inferExpressionType consult when picking format specifiers. Mutates the
 * module-scoped `_functionReturnTypes` (existing contract — preserved here
 * so the consolidation is behavior-neutral).
 */
function populateFunctionReturnTypes(otherFlows) {
  _functionReturnTypes = {};
  for (const flow of otherFlows) {
    const fname = flow.name || 'function1';
    const hasExplicit = Array.isArray(flow.parameters)
      && (flow.parameters.length > 0 || (flow.returnType && flow.returnType !== ''));
    if (hasExplicit) {
      _functionReturnTypes[fname] = flow.returnType || 'void';
    } else {
      const outRes = (flow.resources || []).find(r => r.availableForOutput);
      _functionReturnTypes[fname] = outRes ? (outRes.dataType || 'int') : 'void';
    }
  }
}

/**
 * Generate code for multiple flows (functions)
 * @param {Object} programData - Program data with flows array
 * @param {string} language - Target language
 * @returns {string} Generated code
 */
/** A resource that DEFINES a type (typedef, function-pointer type, struct/union/enum). */
export function isTypeDefinition(r) {
  const t = String(r && r.resourceType).toLowerCase();
  return ['function_pointer', 'typedef', 'struct_type', 'union_type', 'enum_type'].includes(t);
}

/**
 * An array size as a number: a literal, or a #define resolved through the
 * project's macro resources (`MAX_LINE`, `BPCHAR_LEN + 1`). null when it cannot
 * be worked out, so a caller never does arithmetic on NaN.
 */
export function resolveCSize(size, resources) {
  const macros = {};
  (resources || []).forEach((r) => {
    if (String(r && r.resourceType).toLowerCase() === 'macro' && r.macroKind !== 'function') {
      macros[r.name] = String(r.initialValue ?? '').trim();
    }
  });
  let text = String(size ?? '').trim();
  for (let i = 0; i < 4; i += 1) {
    text = text.replace(/[A-Za-z_]\w*/g, (w) => (w in macros ? `(${macros[w]})` : w));
  }
  if (!/^[\d\s()+\-*/]+$/.test(text)) return null;       // only integer arithmetic is evaluated
  try {
    // eslint-disable-next-line no-new-func
    const n = Function(`"use strict"; return (${text});`)();
    return Number.isInteger(n) && n > 0 ? n : null;
  } catch (e) {
    return null;
  }
}

/**
 * Order global resources so every type is DEFINED before it is USED.
 *
 * C requires a complete type before a declaration that needs its size, and the
 * order `globalResources` arrives in is not the order they were authored in —
 * a saved project's rows come back from the database unordered. Sorting here
 * rather than at every call site means the guarantee holds however the
 * resources were loaded.
 *
 * Stable within each group, so a project that already emitted correctly keeps
 * byte-identical output.
 */
export function globalsInTypeOrder(resources) {
  if (!Array.isArray(resources)) return [];
  const rank = (r) => {
    const t = String(r && r.resourceType).toLowerCase();
    // A function-pointer resource emits `typedef void (*Fn)(void);`, which a
    // struct member may name — so typedefs come before struct definitions.
    if (t === 'function_pointer' || t === 'typedef') return 0;
    if (t === 'struct_type' || t === 'union_type' || t === 'enum_type') return 1;
    return 2;
  };
  const ranked = resources
    .map((r, i) => ({ r, i }))
    .sort((a, b) => (rank(a.r) - rank(b.r)) || (a.i - b.i))
    .map((x) => x.r);

  // The rank is a default, not a law: a function-pointer type may itself take a
  // struct — `typedef int (*AggFinal)(struct Agg);` — and then it must follow
  // that struct, while a struct holding an AggFinal must follow IT. So among
  // the type definitions, each goes after every other one it names; where
  // nothing forces an order, the ranked order stands. (Before a struct is
  // complete, `struct Agg` in a typedef's parameter list declares a different,
  // prototype-scoped type, and every call through the pointer mismatches.)
  const types = ranked.filter((r) => rank(r) < 2);
  const names = new Set(types.map((r) => r.name));
  const mentions = (r) => {
    const text = JSON.stringify([r.dataType, r.returnType, r.paramTypes, r.baseType,
      (r.structMembers || []).map((m) => [m.dataType, m.structTypeRef, m.arrayElementType, m.pointsToType])]);
    return [...names].filter((n) => n !== r.name && new RegExp(`\\b${n}\\b`).test(text));
  };
  const done = new Set();
  const ordered = [];
  const visit = (r, path) => {
    if (done.has(r.name) || path.has(r.name)) return;       // a cycle keeps the ranked order
    path.add(r.name);
    mentions(r).forEach((n) => visit(types.find((t) => t.name === n), path));
    path.delete(r.name);
    done.add(r.name);
    ordered.push(r);
  };
  types.forEach((r) => visit(r, new Set()));
  return [...ordered, ...ranked.filter((r) => rank(r) === 2)];
}

// The #defines a C file writes: none when its globals come from a header
// (suppressGlobalDecls), only its own when it owns some (emitOnly), else all.
function macrosToEmit(globalResources, options = {}) {
  if (options.suppressGlobalDecls) return [];
  if (options.emitOnly) return (globalResources || []).filter((r) => options.emitOnly.has(r.name));
  return globalResources;
}

export function generateProgramCode(programData, language = 'c', options = {}) {
  const lang = LANGUAGE_CONFIG[language.toLowerCase()] || LANGUAGE_CONFIG.c;
  const flows = programData.flows || [];
  // ADO #392 — for subflow arg ordering. A multi-file build generates ONE file
  // at a time, and a call into another file must still find its target to
  // order the arguments by the target's parameters: without the project's
  // flows it fell back to the inputMappings KEY order, which a save through
  // Postgres JSONB rewrites (shorter keys first) - readTuple(rel, fp) became
  // readTuple(fp, rel). `projectFlows` is the whole project, for lookup only.
  _allFlowsForCodegen = options.projectFlows || flows;
  const globalResources = programData.globalResources || [];
  const isCLike = lang.name === 'C' || lang.name === 'C++';
  let deferredGlobals = '';
  
  let code = '';

  // For non-C/C++ languages, add header immediately.
  // Java uses options.className to emit `public class <ClassName>` with the
  // correct name (derived from the .java file). Falls back to `Main` when
  // className isn't provided (single-file or legacy projects).
  // ADO #307 — Java auto-import: gather FQCNs from every class-instance
  // resource in scope (this file's globalResources + each flow's local
  // resources) so `ArrayList` / `HashMap` etc. get a single dedupe'd
  // `import …;` line at the top.
  if (lang.name !== 'C' && lang.name !== 'C++') {
    const allFileResources = [
      ...globalResources,
      ...flows.flatMap((f) => f.resources || []),
    ];
    // ADO #313 — pass flows so the import set picks up checked exceptions
    // from each flow's throws clause + Throw nodes in the body.
    const extraImports = collectJavaImports(allFileResources, flows);
    const header = resolveFileHeader(lang, options.className, options.fileClassType, options.recordComponents, extraImports);
    if (header) code += header;
  }
  // C/C++ headers are prepended AFTER code generation (see bottom of function)

  // Debug: inject logStep function definition
  if (options.isDebugMode) {
    code += getLogStepDefinition(language);
  }

  // Generate global/class-level variable declarations
  // ADO #215 Option B — #include directives for the auto-generated headers of a
  // multi-file C/C++ project. THESE MUST COME BEFORE THE GLOBALS. `_globals.h`
  // carries the struct and typedef definitions, so a global declared from one of
  // those types -- `struct Command commands[24];` -- is an incomplete type until
  // the include has been seen. Emitted after the globals, as they were, the entry
  // file failed with "array has incomplete element type" on every struct it
  // owned, while _globals.h itself was perfectly correct.
  if (options.includeDirectives && options.includeDirectives.length > 0) {
    options.includeDirectives.forEach(inc => { code += inc + '\n'; });
    code += '\n';
  }

  // `suppressGlobalDecls` keeps the globals IN SCOPE for type resolution while
  // emitting none of them. A multi-file non-entry file needs exactly that: the
  // entry file owns the definitions, but this file still has to know that
  // `pg_type[k].oid` is an int.
  //
  // Passing an empty array instead — which is what the assembler did — loses
  // the types, and the condition builder then falls back to treating the
  // operands as strings: `pg_type[k].oid == typeoid` came out as
  // `strcmp(pg_type[k].oid, "typeoid") == 0`. The single-file build of the same
  // flow was correct, so the split silently changed what the program DID.
  if (globalResources.length > 0 && !options.suppressGlobalDecls) {
    if (lang.name === 'Java') {
      // Java: Class fields go inside the class but before main method
      code += '    // Class Fields\n';
      globalResources.forEach(resource => {
        const declaration = generateResourceDeclaration(resource, lang, language, globalResources);
        // Handle multi-line declarations
        if (declaration.includes('\n')) {
          const lines = declaration.split('\n');
          lines.forEach(line => {
            if (line.trim()) {
              code += `    ${line}\n`;
            }
          });
          code += `    ;\n`;
        } else {
          code += `    ${declaration}\n`;
        }
      });
      code += '\n';
    } else {
      // Other languages: module-level globals before main. Build the lines
      // first and only emit the header when there's real content — Python class
      // fields emit inside their class (empty declaration here), so an all-
      // classField pool must NOT print an empty "# Global Variables" section.
      const globalLines = [];
      const typeLines = [];
      // A TYPE MUST BE COMPLETE BEFORE ANYTHING USES IT, and the order this
      // array arrives in is not the author's. A saved project's
      // global_resources come back from the database in no guaranteed order,
      // so `struct PgClass pg_class[16];` could be emitted before
      // `struct PgClass { ... };` — "array type has incomplete element type",
      // from a project that compiles perfectly when the same resources are
      // read from a spec file in authoring order.
      //
      // So the declaration order is DERIVED rather than inherited:
      //   1. typedefs (a function-pointer type a struct member may name)
      //   2. struct / union / enum definitions
      //   3. everything else
      // Within each group the incoming order is preserved, which keeps the
      // output stable for every project that was already correct.
      globalsInTypeOrder(globalResources).forEach(resource => {
        // ADO #396 — macros are hoisted to the top (after #include), not
        // emitted inline among the globals; skip them here.
        if (isMacroResource(resource)) return;
        // `emitOnly`: every global in scope, only these written out (a file
        // that owns some of a project's globals defines those and no others).
        if (options.emitOnly && !options.emitOnly.has(resource.name)) return;
        const declaration = generateResourceDeclaration(resource, lang, language, globalResources);
        if (!declaration || !declaration.trim()) return;
        // In C a TYPE goes before the prototypes and a VARIABLE after them (see
        // below); everywhere else the two stay together, as they always were.
        const into = (isCLike && isTypeDefinition(resource)) ? typeLines : globalLines;
        if (declaration.includes('\n')) {
          declaration.split('\n').forEach(line => { if (line.trim()) into.push(line); });
          into.push(';');
        } else {
          into.push(declaration);
        }
      });
      if (typeLines.length > 0) {
        code += typeLines.join('\n') + '\n\n';
      }
      if (globalLines.length > 0) {
        const section = lang.comment('Global Variables') + '\n' + globalLines.join('\n') + '\n\n';
        // C: types, then PROTOTYPES, then variables. A variable's initialiser
        // may name a function — a command table `{ {"\\q", doQuit}, … }` — and
        // C needs that function declared first; emitted before the prototypes,
        // a table naming a function defined in the same file did not compile.
        if (isCLike) deferredGlobals = section; else code += section;
      }
    }
  }
  
  const { entryFlow, otherFlows } = partitionFlows(flows, { ...options, language });

  // Combine all resources for type lookup
  const allResources = [...globalResources];
  flows.forEach(f => {
    if (f.resources) allResources.push(...f.resources);
  });

  populateFunctionReturnTypes(otherFlows);

  // Helper to get function signature info — prefers explicit flow.parameters/returnType
  const getFunctionSignature = (flow) => {
    const funcName = flow.name || 'function1';
    const flowResources = flow.resources || [];
    const hasExplicit = Array.isArray(flow.parameters)
      && (flow.parameters.length > 0 || (flow.returnType && flow.returnType !== ''));

    if (hasExplicit) {
      const paramInfo = (flow.parameters || []).map(p => ({
        name: p.name,
        dataType: p.type || p.dataType || 'int',
      }));
      return {
        funcName,
        paramInfo,
        returnTypeStr: flow.returnType || 'void',
        // legacy mirror for callers that still expect these
        inputResources: paramInfo.map(p => ({ name: p.name, dataType: p.dataType })),
        outputResource: (flow.returnType && flow.returnType !== 'void')
          ? { name: '__return__', dataType: flow.returnType }
          : null,
      };
    }

    // Legacy: scan resources for availableForInput/availableForOutput
    const inputResources = flowResources.filter(r => r.availableForInput);
    const outputResource = flowResources.find(r => r.availableForOutput);
    return {
      funcName,
      inputResources,
      outputResource,
      paramInfo: inputResources.map(r => ({ name: r.name, dataType: r.dataType || 'int' })),
      returnTypeStr: outputResource ? (outputResource.dataType || 'int') : 'void',
    };
  };

  // For C/C++ language: Generate function prototypes before main()
  // ADO #525 — a function its own header already declares is not declared
  // again here: the .c includes that header first.
  const protoFlows = otherFlows.filter((f) => !options.declaredInHeader?.has?.(f.name));
  if ((lang.name === 'C' || lang.name === 'C++') && protoFlows.length > 0) {
    const typeMapper = lang.name === 'C++' ? mapToCppType : mapToCType;
    code += lang.comment('Function Declarations') + '\n';
    protoFlows.forEach(flow => {
      const { funcName, paramInfo, returnTypeStr } = getFunctionSignature(flow);
      const returnType = returnTypeStr && returnTypeStr !== 'void' ? typeMapper(returnTypeStr) : 'void';
      const params = paramInfo.map(p => formatCParam(p.dataType, p.name, typeMapper)).join(', ');
      const list = withVariadicEllipsis(params, flow.isVariadic);
      code += `${returnType} ${funcName}(${lang.name === 'C' ? cParamList(list) : list});\n`;
    });
    code += '\n';
  }
  code += deferredGlobals;
  
  // Language-specific function ordering
  // ADO #226 — forward className so constructor headers use the right name
  const fnOpts = { className: options.className, isDebugMode: options.isDebugMode };
  if (lang.name === 'C' || lang.name === 'C++') {
    // C/C++: Prototypes -> main() -> Function definitions
    if (entryFlow) {
      code += generateMainFunctionCode(entryFlow, lang, allResources, language, options.isDebugMode, globalResources);
      code += '\n';
    }
    otherFlows.forEach(flow => {
      code += generateFunctionCode(flow, lang, allResources, language, fnOpts);
      code += '\n';
    });
  } else if (lang.name === 'Java') {
    // Java: All methods must be inside the class.
    //
    // ADO #240 — when the file has multiple sessions (a primary class plus
    // any combination of nested / sibling classes), scope `otherFlows` to
    // the primary session, then emit nested sessions inside the primary's
    // braces, close the primary, and finally emit sibling classes after.
    // For single-session / legacy projects (sessions undefined or one),
    // behaviour is byte-identical to before — `otherFlows` flows through
    // unchanged.
    const sessionList = options.sessions || [];
    const primarySession = findPrimarySession(sessionList);
    const isMultiSession = sessionList.length > 1 && primarySession;

    let primaryOtherFlows = otherFlows;
    if (isMultiSession) {
      // Primary session's "other" methods. Flows with no sessionId fall
      // through to the primary (legacy fallback).
      primaryOtherFlows = otherFlows.filter(f =>
        !f.sessionId || f.sessionId === primarySession.id
      );
    }

    if (entryFlow) {
      code += generateMainFunctionCode(entryFlow, lang, allResources, language, options.isDebugMode, globalResources);
      code += '\n';
    }
    primaryOtherFlows.forEach(flow => {
      code += generateFunctionCode(flow, lang, allResources, language, fnOpts);
      code += '\n';
    });

    // ADO #240 — Nested sessions (staticNested / innerClass) emitted
    // INSIDE the primary class braces, after the primary's methods.
    if (isMultiSession) {
      const nested = sessionList.filter(s => s.parentSessionId === primarySession.id);
      nested.forEach(ns => {
        code += emitJavaInnerSessionBlock(ns, otherFlows, lang, allResources, language, fnOpts);
      });
    }

    // Close the primary class
    code += lang.classEnd || '}\n';

    // ADO #240 — Sibling classes (siblingClass) emitted at file level
    // AFTER the primary's closing brace. Package-private (no `public`).
    if (isMultiSession) {
      const siblings = sessionList.filter(s => s.kind === 'siblingClass' && !s.parentSessionId);
      siblings.forEach(s => {
        code += emitJavaSiblingSessionBlock(s, otherFlows, lang, allResources, language, fnOpts);
      });
    }
  } else if (lang.name === 'Python' && pythonClassSessions(options.sessions, entryFlow).length > 0) {
    // ADO #441 — Python OOP: emit each class session as a `class Name:` block,
    // then module-level (non-class) functions, then main(). Class method flows
    // (sessionId matches a class session) are pulled out of the flat function
    // list and emitted inside their class.
    const classSessions = pythonClassSessions(options.sessions, entryFlow);
    const classSessionIds = new Set(classSessions.map(s => s.id));
    classSessions.forEach(s => {
      code += emitPythonClassBlock(s, flows, lang, allResources, language, fnOpts) + '\n';
    });
    otherFlows
      .filter(f => !classSessionIds.has(f.sessionId))
      .forEach(flow => {
        code += generateFunctionCode(flow, lang, allResources, language, fnOpts);
        code += '\n';
      });
    if (entryFlow) {
      code += generateMainFunctionCode(entryFlow, lang, allResources, language, options.isDebugMode, globalResources);
    }
  } else {
    // JavaScript, Python (no classes), Pseudocode: Functions before main.
    otherFlows.forEach(flow => {
      code += generateFunctionCode(flow, lang, allResources, language, fnOpts);
      code += '\n';
    });
    if (entryFlow) {
      code += generateMainFunctionCode(entryFlow, lang, allResources, language, options.isDebugMode, globalResources);
    }
  }

  // For C/C++, scan the generated code and prepend required headers, then the
  // macro block (ADO #396 — #defines sit between includes and globals).
  // A multi-file non-entry body (suppressGlobalDecls) gets its macros from
  // _globals.h, like every other global: repeating all of them in every .c
  // file is duplication, and a value changed in one place would disagree.
  if (lang.name === 'C') {
    code = generateCHeader(code)
      + generateMacroBlock(macrosToEmit(globalResources, options), lang, language) + code;
  } else if (lang.name === 'C++') {
    code = generateCppHeader(flows, globalResources) + generateMacroBlock(globalResources, lang, language) + code;
  } else if (lang.name === 'Python') {
    // ADO #430 — scan for used stdlib modules and insert `import` lines at top.
    code = insertPythonImports(code, lang.fileHeader);
  }

  return code;
}

/**
 * Generate a function from a flow
 * Uses resources with availableForInput/availableForOutput to define function signature
 */
// ADO #449 — append the C/C++ variadic ellipsis to a built parameter list.
// A variadic function has `...` after its named params (`int count, ...`); with
// no named params it's a bare `...` (valid but rare). Callers already sit in
// C/C++ branches, so this just handles the string join.
function withVariadicEllipsis(params, isVariadic) {
  if (!isVariadic) return params;
  return params ? `${params}, ...` : '...';
}

// ADO #525 — a C function that takes nothing says so: `int helper(void)`.
// Before C23, `int helper()` means "unknown arguments", so a wrong call
// compiles silently. C++ keeps `()`, which already means none.
export function cParamList(params) {
  return params || 'void';
}

function generateFunctionCode(flow, lang, allResources = [], language = 'c', options = {}) {
  let code = '';
  let indentLevel = 1;
  // ADO #368 — instrument non-entry function bodies too, so subflow calls trace
  // (and the heap fills, since malloc/free live inside functions like push()).
  const isDebugMode = !!options.isDebugMode;

  const getIndent = () => lang.indent.repeat(indentLevel);

  // ADO #350 — optional line mapping for non-main flows. When the caller passes
  // `options.lineMap = { map, baseLineNum }`, record the absolute line ranges of
  // this function's signature and body nodes into `map` (so Master Forever's
  // progress highlight can light up multi-function given code). Derived from the
  // ACTUAL emitted text — not a re-scan estimate — so it stays in lock-step with
  // generation and the produced `code` string is byte-for-byte unchanged.
  const lineMap = options.lineMap || null;
  const _cl = (s) => (s.match(/\n/g) || []).length;
  const sigKey = lineMap ? (flow.id ? `fn:${flow.id}` : `fn:${flow.name || 'function1'}`) : null;

  const funcName = flow.name || 'function1';
  const resources = flow.resources || [];
  // A parameter is a name this function can use, so everything that resolves a
  // name has to be able to see it — the same way a local or a global is seen.
  //
  // It could not before, and the symptom was a type error nobody would connect
  // to a missing declaration: `needsStringQuotes` asks whether a bare name is a
  // resource, finds no `y` in the list, concludes it must be a string literal,
  // and a Decision comparing two int parameters emitted `strcmp(x, "y")`.
  //
  // That is the fourth appearance of one fault — a name the resource list does
  // not contain gets quoted. ADO #354 was NULL, #409 was an enum member, and the
  // indexed-reference branch below was `word[j]`. Each was patched where it
  // showed; a parameter is not a special case to add to that list, it is a
  // declaration that was never in the list at all.
  //
  // `_fromParameter` is the existing marker for these — the local-declaration
  // filter already skips resources carrying it, so mirroring here cannot make a
  // parameter declared twice.
  const paramResources = (flow.parameters || [])
    .filter((p) => p && p.name)
    .map((p) => ({
      name: p.name,
      resourceType: /\[\s*\d*\s*\]\s*$/.test(p.type || '') ? 'array_1d' : 'variable',
      dataType: (p.type || p.dataType || 'int').replace(/\s*\[\s*\d*\s*\]\s*$/, ''),
      elementType: (p.type || p.dataType || 'int').replace(/\s*\[\s*\d*\s*\]\s*$/, ''),
      _fromParameter: true,
    }));
  // A FUNCTION'S OWN NAMES COME FIRST. allResources is the globals plus every
  // flow's variables (so a struct type resolves from anywhere), and lookups take
  // the first match: listed first, another flow's `int w` decided how this
  // flow's `char w[40]` was printed (%d). In C a function's locals and
  // parameters shadow everything else; the lookup order now says the same.
  const combinedResources = [...resources, ...paramResources, ...allResources];
  // ADO #226 — className is the Java file's public class name; required for
  // constructor emission so the header reads `public ClassName(params)`.
  const className = options.className || funcName;

  // ── Function signature: prefer explicit flow.parameters / flow.returnType ──
  // Falls back to legacy availableForInput/availableForOutput resource flags
  // for backwards compatibility with older flows.
  const hasExplicitSignature = Array.isArray(flow.parameters) && flow.parameters.length >= 0
    && (flow.parameters.length > 0 || (flow.returnType && flow.returnType !== ''));

  // Build parameter info: [{ name, dataType }]
  let paramInfo;
  if (hasExplicitSignature) {
    paramInfo = (flow.parameters || []).map(p => ({
      name: p.name,
      dataType: p.type || p.dataType || 'int',
    }));
  } else {
    paramInfo = resources
      .filter(r => r.availableForInput)
      .map(r => ({ name: r.name, dataType: r.dataType || 'int' }));
  }

  // Determine return type
  let returnType;
  let outputResource = null;
  if (hasExplicitSignature) {
    returnType = flow.returnType || 'void';
  } else {
    outputResource = resources.find(r => r.availableForOutput);
    returnType = outputResource ? (outputResource.dataType || 'int') : 'void';
  }
  const hasReturn = returnType && returnType !== 'void';

  // Resources to declare as local variables inside the function body.
  // Exclude:
  //   - resources mirrored from parameters (when explicit signature)
  //   - legacy availableForInput/availableForOutput resources
  const paramNames = new Set(paramInfo.map(p => p.name));
  const localResources = resources.filter(r => {
    if (paramNames.has(r.name)) return false;
    if (r._fromParameter) return false;
    if (!hasExplicitSignature && (r.availableForInput || r.availableForOutput)) return false;
    return true;
  });
  
  // Generate parameter list based on language
  let params = '';
  if (lang.name === 'C') {
    params = paramInfo.map(p => formatCParam(p.dataType, p.name, mapToCType)).join(', ');
  } else if (lang.name === 'C++') {
    params = paramInfo.map(p => formatCParam(p.dataType, p.name, mapToCppType)).join(', ');
  } else if (lang.name === 'Java') {
    params = paramInfo.map(p => formatJavaParam(p.dataType, p.name)).join(', ');
  } else if (lang.name === 'JavaScript') {
    params = paramInfo.map(p => p.name).join(', ');
  } else if (lang.name === 'Python') {
    params = paramInfo.map(p => p.name).join(', ');
  } else {
    // Pseudocode
    params = paramInfo.map(p => `${p.name}: ${p.dataType}`).join(', ');
  }

  // ADO #449 — a variadic C/C++ function gets `...` after its named params.
  if (lang.name === 'C' || lang.name === 'C++') {
    params = withVariadicEllipsis(params, flow.isVariadic);
  }

  // The function's IPO, once its Process is written, as a comment above it (C, Ram 2026-10-29).
  if (lang.name === 'C') code += ipoComment(flow, true);
  // Function declaration
  const _sigLocalLine = _cl(code) + 1; // 1-based line of the signature within funcCode
  if (lang.name === 'C') {
    const cReturnType = hasReturn ? mapToCType(returnType) : 'void';
    code += `${cReturnType} ${funcName}(${cParamList(params)}) {\n`;
  } else if (lang.name === 'C++') {
    const cppReturnType = hasReturn ? mapToCppType(returnType) : 'void';
    code += `${cppReturnType} ${funcName}(${params}) {\n`;
  } else if (lang.name === 'Java') {
    // ADO #226 — emit the Java method header based on flowType + accessSpecifier.
    // Legacy flows without flowType fall back to the old behaviour (public static).
    const javaReturnType = hasReturn ? mapToJavaType(returnType) : 'void';
    const access = flow.accessSpecifier && flow.accessSpecifier !== 'package'
      ? `${flow.accessSpecifier} `
      : (flow.accessSpecifier === 'package' ? '' : 'public ');
    const flowType = flow.flowType
      || (flow.isStatic === false ? 'instance' : 'static');

    // ADO #313 — `throws` clause. Synced onto the impl flow from the
    // matching method_decl / constructor_decl in useFlowStore. Filter empty
    // strings (defensive — the editor doesn't add them, but legacy data may).
    const throwsList = Array.isArray(flow.throwsTypes)
      ? flow.throwsTypes.map((t) => (t || '').trim()).filter(Boolean)
      : [];
    const throwsClause = throwsList.length > 0 ? ` throws ${throwsList.join(', ')}` : '';

    switch (flowType) {
      case 'constructor':
        // No return type keyword; name must match the class name.
        code += `    ${access}${className}(${params})${throwsClause} {\n`;
        break;
      case 'compactConstructor':
        // Records (deferred to ADO #232 for full support, but the header is
        // straightforward). No params list, no return type.
        // Compact constructors can declare throws too — keep the clause.
        code += `    ${access}${className}${throwsClause} {\n`;
        break;
      case 'instance':
        // No `static` keyword; instance methods operate on object state.
        code += `    ${access}${javaReturnType} ${funcName}(${params})${throwsClause} {\n`;
        break;
      case 'static':
      default:
        code += `    ${access}static ${javaReturnType} ${funcName}(${params})${throwsClause} {\n`;
        break;
    }
  } else if (lang.name === 'JavaScript') {
    code += `function ${funcName}(${params}) {\n`;
  } else if (lang.name === 'Python') {
    // ADO #441 — a method inside a Python class. The constructor becomes
    // `__init__`; instance methods/constructors take `self` as the first
    // parameter; static methods get an `@staticmethod` decorator and no self.
    if (options.pyClass) {
      const ptype = flow.flowType || 'instance';
      if (ptype === 'static') {
        code += `@staticmethod\ndef ${funcName}(${params}):\n`;
      } else {
        const name = ptype === 'constructor' ? '__init__' : funcName;
        const withSelf = params ? `self, ${params}` : 'self';
        code += `def ${name}(${withSelf}):\n`;
      }
    } else {
      code += `def ${funcName}(${params}):\n`;
    }
  } else {
    // Pseudocode
    const returnDecl = hasReturn ? ` RETURNS ${returnType}` : '';
    code += `FUNCTION ${funcName}(${params})${returnDecl}\n`;
  }
  
  // ADO #350 — record the signature line (lights up once the student creates
  // this function flow). Single-line for every language we emit.
  if (lineMap) {
    const sigAbs = lineMap.baseLineNum + _sigLocalLine;
    lineMap.map[sigKey] = { startLine: sigAbs, endLine: sigAbs };
  }

  // ADO #368 — per-function debug scratch buffer for logStep snprintf calls.
  if (isDebugMode && (language === 'c' || language === 'cpp')) {
    code += `${getIndent()}char __ctx[256];\n`;
  }

  // Declare local variables (not input params, not output)
  // ADO #311 — skip inline-declared class_instance resources; the
  // owning instantiate node emits the merged form `ClassName var = new ClassName(args);`.
  localResources
    .filter((r) => !r._inlineDeclaredByNodeId)
    .forEach(resource => {
      const declaration = generateResourceDeclaration(resource, lang, language.toLowerCase(), localResources);
      code += indentDeclaration(declaration, getIndent());
    });

  // Legacy: declare output variable if using availableForOutput mechanism
  if (outputResource) {
    const declaration = generateResourceDeclaration(outputResource, lang, language.toLowerCase(), localResources);
    code += indentDeclaration(declaration, getIndent());
  }

  // For Java non-main functions that contain input nodes, declare a local Scanner
  const nodes = flow.nodes || [];
  const processableNodes = nodes.filter(n => n.type !== 'start' && n.type !== 'end');
  if (lang.name === 'Java' && processableNodes.some(n => n.type === 'input')) {
    code += `${getIndent()}Scanner scanner = new Scanner(System.in);\n`;
  }

  if (localResources.length > 0 || outputResource) {
    code += '\n';
  }

  // Check if the flow contains any explicit Return node anywhere (recursive)
  const containsReturnNode = (function hasReturn(nodeList) {
    for (const n of (nodeList || [])) {
      if (n.type === 'return') return true;
      if (n.outcomes) {
        for (const o of n.outcomes) {
          if (hasReturn(o.nodes)) return true;
        }
      }
      if (n.loopBody && hasReturn(n.loopBody)) return true;
      if (n.afterLast && hasReturn(n.afterLast)) return true;
      if (n.branches) {
        for (const b of n.branches) {
          if (hasReturn(b.nodes)) return true;
        }
      }
    }
    return false;
  })(processableNodes);

  // ADO #308 — stash flow context for the subflow `this.` decision.
  _currentFlowType = flow.flowType || null;
  _currentFlowName = flow.name || null;
  _currentReturnType = flow.returnType || null;
  // ADO #441 — within a Python class instance method / constructor, references
  // to class attributes must be emitted `self.attr`.
  _pyInClassMethod = lang.name === 'Python' && !!(options && options.pyClass)
    && (flow.flowType === 'instance' || flow.flowType === 'constructor');
  _pyMethodParams = _pyInClassMethod ? new Set(paramInfo.map((p) => p.name)) : new Set();

  // ADO #211 — stop after a fully-terminating decision or return node
  for (const node of processableNodes) {
    if (lineMap) {
      // Absolute line where this node's code begins (mirrors the main-flow loop
      // in generateCodeWithLineMap). baseLineNum is the absolute line just before
      // funcCode's first line, so local line L → absolute baseLineNum + L.
      const absStart = lineMap.baseLineNum + _cl(code) + 1;
      code += generateNodeCode(node, lang, getIndent, () => indentLevel++, () => indentLevel--, combinedResources, language, lineMap.map, absStart, isDebugMode);
      const absEnd = lineMap.baseLineNum + _cl(code);
      if (node.id) lineMap.map[node.id] = { startLine: absStart, endLine: Math.max(absEnd, absStart) };
    } else {
      code += generateNodeCode(node, lang, getIndent, () => indentLevel++, () => indentLevel--, combinedResources, language, null, 0, isDebugMode);
    }
    if (node.type === 'return') break;
    if (node.type === 'decision' && _branchTerminates([node])) break;
  }

  // ADO #368 — emit a RETURN event at function fall-through so the memory model
  // pops the callee's frame. (Early returns are covered by the return node's own
  // RETURN event; this one handles void / fall-through functions like push().)
  if (isDebugMode && !containsReturnNode) {
    code += `${getIndent()}${getLogStepCall(language, 'Execution', `Return ${funcName}`, `__ret_${flow.id || funcName}`, `"RETURN:${funcName}:"`)}\n`;
  }

  // Legacy: auto-emit return when using availableForOutput AND no explicit Return node
  if (outputResource && !containsReturnNode) {
    code += '\n';
    // ADO #350 — map this synthesised return line; without it the line is real
    // (not boilerplate, not a node) and would be uncoverable in the progress map.
    const retAbs = lineMap ? lineMap.baseLineNum + _cl(code) + 1 : 0;
    if (lang.name === 'Python') {
      code += `${getIndent()}return ${outputResource.name}\n`;
    } else if (lang.name === 'Pseudocode') {
      code += `${getIndent()}RETURN ${outputResource.name}\n`;
    } else {
      code += `${getIndent()}return ${outputResource.name};\n`;
    }
    if (lineMap) lineMap.map[`ret:${flow.id || flow.name || 'function1'}`] = { startLine: retAbs, endLine: retAbs };
  }
  
  // Close function
  if (lang.name === 'Python') {
    if (processableNodes.length === 0 && !outputResource) {
      code += `${getIndent()}pass\n`;
    }
  } else if (lang.name === 'Pseudocode') {
    code += 'END FUNCTION\n';
  } else if (lang.name === 'Java') {
    code += '    }\n';
  } else {
    code += '}\n';
  }
  
  return code;
}

/**
 * Generate main function from entry flow
 */
function generateMainFunctionCode(flow, lang, allResources = [], language = 'c', isDebugMode = false, globalResources = []) {
  let code = '';
  let indentLevel = 1;

  const getIndent = () => lang.indent.repeat(indentLevel);

  // Main function start
  code += mainStartFor(lang, language, flow);
  // Marker for the body start (Python: to detect an empty body → `pass`).
  const _pyBodyStart = code.length;

  // Local resources
  const resources = flow.resources || [];
  // ADO #400 — argc/argv participate in type resolution (argv[i] → %s).
  const combinedResources = withCommandLineArgs([...resources, ...allResources], flow);  // own names first, as above

  // Debug: declare __ctx buffer at function scope (C/C++ only)
  if (isDebugMode && (language === 'c' || language === 'cpp')) {
    code += getIndent() + 'char __ctx[256];\n';
  }

  // ADO #311 — skip inline-declared class_instance resources; the
  // owning Instantiate node emits the merged form.
  const declarableResources = resources.filter((r) => !r._inlineDeclaredByNodeId);
  declarableResources.forEach(resource => {
    const declaration = generateResourceDeclaration(resource, lang, language.toLowerCase(), [...resources, ...globalResources]);
    code += indentDeclaration(declaration, getIndent());
  });

  if (declarableResources.length > 0) {
    code += '\n';
  }

  // Process nodes
  const nodes = flow.nodes || [];
  const processableNodes = nodes.filter(n => n.type !== 'start' && n.type !== 'end');

  // Debug: logStep for start node
  if (isDebugMode) {
    const startNode = nodes.find(n => n.type === 'start');
    if (startNode) {
      code += getIndent() + getLogStepCall(language, 'Execution', 'Start', startNode.id, '"Program started"') + '\n';
    }
  }

  // Debug: logStep for resources — shows each declared variable with storage size (ADO #210)
  // LOCALS only. File-scope globals are NOT counted here — they have static
  // storage duration (they exist before main, shown at Start in the GLOBALS
  // region), so lumping them into the local Resources count is misleading.
  if (isDebugMode && resources.length > 0) {
    const declarableRes = resources.filter(r => {
      const rt = r.resourceType || r.type;
      return rt !== 'struct_type' && rt !== 'union_type' && rt !== 'typedef';
    });
    if (declarableRes.length > 0) {
      const count = declarableRes.length;
      if (language === 'c' || language === 'cpp') {
        // Emit: "N variables (X bytes total)" using sum of sizeof() at runtime
        const sizeofSum = declarableRes.map(r => `sizeof(${r.name})`).join(' + ');
        code += getIndent() + `snprintf(__ctx, sizeof(__ctx), "${count} variable${count > 1 ? 's' : ''} (%zu bytes total)", (size_t)(${sizeofSum}));\n`;
        code += getIndent() + getLogStepCall(language, 'Setup', 'Resources', '__resources__', '__ctx') + '\n';
      } else if (language === 'java') {
        const jlsSizes = { 'byte': 1, 'boolean': 1, 'short': 2, 'char': 2, 'int': 4, 'float': 4, 'long': 8, 'double': 8 };
        let totalBytes = 0;
        declarableRes.forEach(r => {
          const dt = r.dataType || 'int';
          const bytes = jlsSizes[dt] || 4;
          const rt = r.resourceType || r.type || 'variable';
          if (rt === 'array_1d') totalBytes += bytes * (r.size || r.length || 0);
          else if (rt === 'array_2d') totalBytes += bytes * (r.rows || 0) * (r.cols || 0);
          else totalBytes += bytes;
        });
        code += getIndent() + getLogStepCall(language, 'Setup', 'Resources', '__resources__', `"${count} variable${count > 1 ? 's' : ''} (${totalBytes} bytes total)"`) + '\n';
      } else if (language === 'python') {
        code += getIndent() + getLogStepCall(language, 'Setup', 'Resources', '__resources__', `f"${count} variable${count > 1 ? 's' : ''}"`) + '\n';
      } else if (language === 'javascript') {
        code += getIndent() + getLogStepCall(language, 'Setup', 'Resources', '__resources__', `\`${count} variable${count > 1 ? 's' : ''}\``) + '\n';
      }
    }
  }

  // ADO #308 — stash flow context for the subflow `this.` decision.
  _currentFlowType = flow.flowType || (flow.isEntry ? 'main' : null);
  _currentFlowName = flow.name || null;
  _currentReturnType = flow.returnType || null;
  _pyInClassMethod = false;
  _pyMethodParams = new Set();

  // ADO #211 — stop after a fully-terminating decision or return node
  let mainAlreadyReturned = false;
  for (const node of processableNodes) {
    code += generateNodeCode(node, lang, getIndent, () => indentLevel++, () => indentLevel--, combinedResources, language, null, 0, isDebugMode);
    if (node.type === 'return') { mainAlreadyReturned = true; break; }
    if (node.type === 'decision' && _branchTerminates([node])) { mainAlreadyReturned = true; break; }
  }

  // Debug: logStep for end node
  if (isDebugMode) {
    const endNode = nodes.find(n => n.type === 'end');
    if (endNode) {
      code += getIndent() + getLogStepCall(language, 'Execution', 'End', endNode.id, '"Program finished"') + '\n';
    }
  }

  // An empty Python main body is a SyntaxError — emit `pass` before the
  // module-guard (mainEnd carries the `if __name__ == "__main__":` line).
  if (language.toLowerCase() === 'python' && !code.slice(_pyBodyStart).trim()) {
    code += lang.indent + 'pass\n';
  }

  // Main function end. See the note on mainEndAfterReturn in generateMainCode:
  // a student's own Return in main must not be followed by the automatic one.
  code += (mainAlreadyReturned && lang.mainEndAfterReturn) ? lang.mainEndAfterReturn : lang.mainEnd;

  // ADO #430 — Python: if main() assigns any module-level global, declare them
  // `global …` right after `def main():`, else the assignment creates a local
  // and the global silently stays at its initial value.
  if (language.toLowerCase() === 'python') {
    const decl = pythonGlobalDeclaration(code, globalResources, lang.indent);
    if (decl) {
      const nl = code.indexOf('\n'); // end of `def main():`
      if (nl !== -1) code = code.slice(0, nl + 1) + decl + code.slice(nl + 1);
    }
  }

  return code;
}

/**
 * Generate a deterministic, human-readable comment for a node based on its properties.
 * Used in Code Next mode to label each code block with its originating node.
 *
 * Examples:
 *   Input node  → "Input: Read nos[i]"
 *   Output node → "Output: Display {sum}"
 *   Loop node   → "Loop: for i from 0 to 4"
 *   Decision    → "Decision: if x > 10"
 *   Assignment  → "Assignment: sum += nos[i]"
 */
export function getNodeComment(node, resources = []) {
  if (!node) return null;

  switch (node.type) {
    case 'input': {
      const selected = node.selectedResources || [];
      if (selected.length > 0) {
        const names = selected.map(r => {
          if (r.includes('.')) return r;                       // struct member path
          // Strip array index suffix (e.g. "res-abc123[i]" → "res-abc123") for lookup
          const baseId = r.replace(/\[.*\]$/, '');
          const indexSuffix = r.slice(baseId.length);          // e.g. "[i]" or ""
          const res = resources.find(rs => rs.id === baseId || rs.name === baseId || rs.id === r || rs.name === r);
          return res ? `${res.name}${indexSuffix}` : r;
        });
        return `Input: Read ${names.join(', ')}`;
      }
      return node.label && node.label !== 'Input' ? `Input: ${node.label}` : 'Input: Read data from user';
    }

    case 'output': {
      const tpl = node.outputTemplate || node.description || '';
      if (tpl) {
        const short = tpl.length > 40 ? tpl.slice(0, 37) + '...' : tpl;
        return `Output: Display ${short}`;
      }
      return 'Output: Display data';
    }

    case 'assignment': {
      const assignments = node.assignments || [];
      if (assignments.length > 0) {
        const parts = assignments.filter(a => a.targetVariable).map(a => {
          const res = resources.find(r => r.id === a.targetVariable);
          const target = a.fullPath || res?.name || a.targetVariable;
          const op = a.operator || '=';
          if (a.valueSource === 'direct' && a.directValue !== undefined) return `${target} ${op} ${a.directValue}`;
          if (a.valueSource === 'variable' && (a.sourceFullPath || a.sourceVariable)) {
            const srcRes = resources.find(r => r.id === a.sourceVariable);
            const src = a.sourceFullPath || srcRes?.name || a.sourceVariable;
            return `${target} ${op} ${src}`;
          }
          if (a.valueSource === 'expression' && a.expression) return `${target} ${op} ${a.expression}`;
          return `${target} ${op} ...`;
        });
        const summary = parts.join(', ');
        return `Assignment: ${summary.length > 50 ? summary.slice(0, 47) + '...' : summary}`;
      }
      return node.label && node.label !== 'Assignment' ? `Assignment: ${node.label}` : 'Assignment';
    }

    case 'decision': {
      const outcomes = node.outcomes || [];
      if (outcomes.length > 0 && outcomes[0].conditions?.length > 0) {
        const firstCond = outcomes[0].conditions[0];
        const varName = firstCond.resourceFullPath || firstCond.resource || '?';
        const opMap = { equals: '==', notEquals: '!=', greaterThan: '>', greaterOrEqual: '>=', lessThan: '<', lessOrEqual: '<=' };
        const op = opMap[firstCond.operator] || firstCond.operator || '==';
        return `Decision: if ${varName} ${op} ${firstCond.value || '?'}`;
      }
      return node.label && node.label !== 'Decision' ? `Decision: ${node.label}` : 'Decision';
    }

    case 'loop': {
      const loopType = node.loopType || 'while';
      if (loopType === 'for') {
        const cfg = node.forConfig || {};
        const counter = cfg.counter || 'i';
        const start = cfg.start || '0';
        const end = cfg.end || '?';
        return `Loop: for ${counter} from ${start} to ${parseInt(end) - 1 || end}`;
      }
      if (loopType === 'forEach') {
        const cfg = node.forEachConfig || {};
        const item = cfg.itemVariable || 'item';
        const coll = cfg.collectionFullPath || cfg.collection || 'items';
        return `Loop: for each ${item} in ${coll}`;
      }
      // while loop — describe condition
      if (node.rawExpression) return `Loop: while ${node.rawExpression}`;
      if (node.conditions?.length > 0) {
        const c = node.conditions[0];
        const varName = c.resourceFullPath || c.resource || '?';
        const opMap = { equals: '==', notEquals: '!=', greaterThan: '>', greaterOrEqual: '>=', lessThan: '<', lessOrEqual: '<=' };
        const op = opMap[c.operator] || c.operator || '==';
        return `Loop: while ${varName} ${op} ${c.value || '?'}`;
      }
      return node.label && node.label !== 'Loop' ? `Loop: ${node.label}` : 'Loop';
    }

    case 'action': {
      const funcName = node.functionName || node.actionFunction || '';
      if (funcName) return `Action: ${funcName}(${(node.arguments || []).join(', ')})`;
      return node.label && node.label !== 'Action' ? `Action: ${node.label}` : 'Action';
    }

    case 'subflow': {
      const flowName = node.flowName || node.subflowName || '';
      return flowName ? `Subflow: Call ${flowName}` : 'Subflow';
    }

    case 'return': {
      const expr = (node.returnValue || '').trim();
      return expr ? `Return: ${expr}` : 'Return';
    }

    default:
      return node.label || node.type;
  }
}

/**
 * Generate code with line number mapping for node highlighting
 * Returns both the code and a map of nodeId -> {startLine, endLine}
 * @param {Object} flowData - Program data with flows and globalResources
 * @param {string} language - Target language
 * @param {Object} options - { withNodeComments: boolean } — when true, inserts descriptive comments before each node's code
 */
export function generateCodeWithLineMap(flowData, language = 'c', options = {}) {
  // C: global variables wait until after the prototypes (see below).
  let deferredGlobalLines = [];
  const lang = LANGUAGE_CONFIG[language.toLowerCase()] || LANGUAGE_CONFIG.c;
  const flows = flowData.flows || [];
  const globalResources = flowData.globalResources || [];
  
  let code = '';
  let lineNum = 1;
  const nodeLineMap = {}; // nodeId -> {startLine, endLine}
  
  const countLines = (str) => (str.match(/\n/g) || []).length;
  const addCode = (str) => {
    code += str;
    lineNum += countLines(str);
  };
  
  // For non-C/C++ languages, add header immediately.
  // Java uses options.className (derived from the .java file name) so the
  // class declaration matches the file. Falls back to `Main` when missing.
  // C/C++ headers are prepended AFTER code generation (see bottom of function)
  // ADO #307 — collect Java imports from globalResources + per-flow resources.
  if (lang.name !== 'C' && lang.name !== 'C++') {
    const allFileResources = [
      ...globalResources,
      ...flows.flatMap((f) => f.resources || []),
    ];
    // ADO #313 — pass flows so the import set picks up checked exceptions.
    const extraImports = collectJavaImports(allFileResources, flows);
    const header = resolveFileHeader(lang, options.className, options.fileClassType, options.recordComponents, extraImports);
    if (header) addCode(header);
  }
  
  // ADO #215 Option B — #include directives for the auto-generated headers of a
  // multi-file C/C++ project. THESE MUST COME BEFORE THE GLOBALS. `_globals.h`
  // carries the struct and typedef definitions, so a global declared from one of
  // those types -- `struct Command commands[24];` -- is an incomplete type until
  // the include has been seen. Emitted after the globals, as they were, the entry
  // file failed with "array has incomplete element type" on every struct it
  // owned, while _globals.h itself was perfectly correct.
  if (options.includeDirectives && options.includeDirectives.length > 0) {
    options.includeDirectives.forEach(inc => { addCode(inc + '\n'); });
    addCode('\n');
  }

  // Generate global variables. A multi-file non-entry C file (suppressGlobalDecls)
  // takes them from _globals.h, as the build does, so the Code panel shows the
  // file that is compiled rather than one with every global repeated in it.
  if (globalResources.length > 0 && !options.suppressGlobalDecls) {
    // ADO #306 follow-up — Java class fields live INSIDE `public class X {`,
    // so they must be indented one level and labelled "Class Fields" (the
    // C/Python/JS pool stays "Global Variables" at column 0). Mirrors the
    // Java-specific branch in generateProgramCode (codeGenerator.js:3645-3663)
    // which the right-pane preview path was missing.
    if (lang.name === 'Java') {
      addCode('\n    ' + lang.comment('Class Fields') + '\n');
      globalResources.forEach(resource => {
        const declaration = generateResourceDeclaration(resource, lang, language, globalResources);
        if (declaration.includes('\n')) {
          declaration.split('\n').forEach(line => {
            if (line.trim()) addCode('    ' + line + '\n');
          });
          addCode('    ;\n');
        } else {
          addCode('    ' + declaration + '\n');
        }
      });
      addCode('\n');
    } else {
      // Build the lines first; only emit the "Global Variables" header when
      // there's real module-level content. Python class fields emit inside
      // their class (empty declaration here), so an all-classField pool must
      // not print an empty section. ADO #294 — multi-line struct definitions
      // come back WITHOUT a trailing `;`, so close each with `;`.
      const globalLines = [];
      const typeLines = [];
      // Types before uses — see globalsInTypeOrder. This is the THIRD place
      // globals are emitted and they must all agree, or the Code panel shows
      // something that would not compile.
      globalsInTypeOrder(globalResources).forEach(resource => {
        // ADO #396 — macros are hoisted to the top (after #include); skip here.
        if (isMacroResource(resource)) return;
        if (options.emitOnly && !options.emitOnly.has(resource.name)) return;
        const declaration = generateResourceDeclaration(resource, lang, language, globalResources);
        if (!declaration || !declaration.trim()) return;
        // C: a type before the prototypes, a variable after them (see below).
        const into = ((lang.name === 'C' || lang.name === 'C++') && isTypeDefinition(resource)) ? typeLines : globalLines;
        if (declaration.includes('\n')) {
          declaration.split('\n').forEach(line => { if (line.trim()) into.push(line); });
          into.push(';');
        } else {
          into.push(declaration);
        }
      });
      if (typeLines.length > 0) {
        addCode('\n');
        typeLines.forEach(line => addCode(line + '\n'));
      }
      if (globalLines.length > 0) {
        if (lang.name === 'C' || lang.name === 'C++') {
          deferredGlobalLines = globalLines;
        } else {
          addCode('\n' + lang.comment('Global Variables') + '\n');
          globalLines.forEach(line => addCode(line + '\n'));
          addCode('\n');
        }
      }
    }
  }
  
  const { entryFlow: mainFlow, otherFlows } = partitionFlows(flows, { ...options, language });
  populateFunctionReturnTypes(otherFlows);

  // For C/C++: Generate function prototypes before main()
  // ADO #525 — as generateProgramCode: no prototype its own header has.
  const protoFlows = otherFlows.filter((f) => !options.declaredInHeader?.has?.(f.name));
  if ((lang.name === 'C' || lang.name === 'C++') && protoFlows.length > 0) {
    const typeMapper = lang.name === 'C++' ? mapToCppType : mapToCType;
    addCode(lang.comment('Function Declarations') + '\n');
    protoFlows.forEach(flow => {
      const flowResources = flow.resources || [];
      const hasExplicit = Array.isArray(flow.parameters)
        && (flow.parameters.length > 0 || (flow.returnType && flow.returnType !== ''));
      const paramInfo = hasExplicit
        ? (flow.parameters || []).map(p => ({ name: p.name, dataType: p.type || p.dataType || 'int' }))
        : flowResources.filter(r => r.availableForInput).map(r => ({ name: r.name, dataType: r.dataType || 'int' }));
      const returnTypeStr = hasExplicit
        ? (flow.returnType || 'void')
        : (flowResources.find(r => r.availableForOutput)?.dataType || 'void');
      const returnType = returnTypeStr && returnTypeStr !== 'void' ? typeMapper(returnTypeStr) : 'void';
      const params = paramInfo.map(p => formatCParam(p.dataType, p.name, typeMapper)).join(', ');
      // ADO #350 — map the prototype line so given multi-function code that
      // includes prototypes lights up once the subflow exists.
      const protoLine = lineNum;
      const list = withVariadicEllipsis(params, flow.isVariadic);
      addCode(`${returnType} ${flow.name || 'function1'}(${lang.name === 'C' ? cParamList(list) : list});\n`);
      const protoKey = flow.id ? `proto:${flow.id}` : `proto:${flow.name || 'function1'}`;
      nodeLineMap[protoKey] = { startLine: protoLine, endLine: protoLine };
    });
    addCode('\n');
  }
  // C: global VARIABLES after the prototypes, as generateProgramCode emits
  // them - a command table { {"\\q", doQuit}, ... } names functions, which
  // must be declared first. Emitted through addCode so the line map stays true.
  if (deferredGlobalLines.length > 0) {
    addCode(lang.comment('Global Variables') + '\n');
    deferredGlobalLines.forEach(line => addCode(line + '\n'));
    addCode('\n');
  }

  // ADO #441 — Python OOP (display path): group a class session's method flows
  // into a `class Name:` block (mirrors generateProgramCode). Module functions
  // emit normally. Class-method bodies don't carry a precise per-node line map
  // here yet (the run path's codegen is authoritative); the displayed code is
  // correct, node highlighting inside methods is coarse.
  if (lang.name === 'Python' && pythonClassSessions(options.sessions, mainFlow).length > 0) {
    const classSessions = pythonClassSessions(options.sessions, mainFlow);
    const classSessionIds = new Set(classSessions.map(s => s.id));
    const fnOpts = { className: options.className, isDebugMode: options.isDebugMode };
    classSessions.forEach(s => {
      addCode(emitPythonClassBlock(s, flows, lang, [...globalResources], language, fnOpts) + '\n');
    });
    otherFlows
      .filter(f => !classSessionIds.has(f.sessionId))
      .forEach(flow => {
        const funcCode = generateFunctionCode(flow, lang, [...globalResources, ...(flow.resources || [])], language, { ...fnOpts, lineMap: { map: nodeLineMap, baseLineNum: lineNum - 1 } });
        addCode(funcCode + '\n');
      });
  } else if ((lang.name === 'JavaScript' || lang.name === 'Python') && otherFlows.length > 0) {
    // For JavaScript/Python (no classes): generate function definitions before main
    otherFlows.forEach(flow => {
      const funcCode = generateFunctionCode(flow, lang, [...globalResources, ...(flow.resources || [])], language, { className: options.className, isDebugMode: options.isDebugMode, lineMap: { map: nodeLineMap, baseLineNum: lineNum - 1 } });
      addCode(funcCode + '\n');
    });
  }

  // ADO #227 — cleanup: removed the phantom `flow.flowType === 'method' || 'function'`
  // branch that called the undefined `generateJavaMethod`. Java non-entry flows are
  // now emitted below (after mainEnd, before classEnd) via `generateFunctionCode`
  // so they appear as methods inside the class, matching `generateProgramCode`.

  if (!mainFlow) {
    // ADO #215: For skipMain (non-entry files), still generate C function definitions
    if ((lang.name === 'C' || lang.name === 'C++') && otherFlows.length > 0) {
      otherFlows.forEach(flow => {
        const funcCode = generateFunctionCode(flow, lang, [...globalResources, ...(flow.resources || [])], language, { className: options.className, isDebugMode: options.isDebugMode, lineMap: { map: nodeLineMap, baseLineNum: lineNum - 1 } });
        addCode(funcCode + '\n');
      });
    }
    // ADO #227 / #240 — Java non-entry files: emit methods inside the class
    // wrapper. ADO #240 adds session-aware scoping.
    if (language === 'java' && otherFlows.length > 0) {
      const sessionList = options.sessions || [];
      const primarySession = findPrimarySession(sessionList);
      const isMultiSession = sessionList.length > 1 && primarySession;
      const fnOpts = { className: options.className, isDebugMode: options.isDebugMode };

      let javaOtherFlows = otherFlows;
      if (isMultiSession) {
        javaOtherFlows = otherFlows.filter(f =>
          !f.sessionId || f.sessionId === primarySession.id
        );
      }

      javaOtherFlows.forEach(flow => {
        const funcCode = generateFunctionCode(flow, lang, [...globalResources, ...(flow.resources || [])], language, { ...fnOpts, lineMap: { map: nodeLineMap, baseLineNum: lineNum - 1 } });
        addCode(funcCode + '\n');
      });

      if (isMultiSession) {
        const nested = sessionList.filter(s => s.parentSessionId === primarySession.id);
        nested.forEach(ns => {
          addCode(emitJavaInnerSessionBlock(ns, otherFlows, lang, [...globalResources], language, fnOpts));
        });
      }

      addCode(lang.classEnd);

      if (isMultiSession) {
        const siblings = sessionList.filter(s => s.kind === 'siblingClass' && !s.parentSessionId);
        siblings.forEach(s => {
          addCode(emitJavaSiblingSessionBlock(s, otherFlows, lang, [...globalResources], language, fnOpts));
        });
      }
    }
    // For C, prepend headers + macro block (ADO #396). Both shift node lines.
    if (lang.name === 'C') {
      // A multi-file non-entry body (suppressGlobalDecls) gets its macros from
      // _globals.h, like every other global; repeating them here is noise.
      const prefix = generateCHeader(code)
        + generateMacroBlock(macrosToEmit(globalResources, options), lang, language);
      if (prefix) {
        const prefixLineCount = (prefix.match(/\n/g) || []).length;
        Object.keys(nodeLineMap).forEach(key => {
          nodeLineMap[key].startLine += prefixLineCount;
          nodeLineMap[key].endLine += prefixLineCount;
        });
        code = prefix + code;
      }
    } else if (lang.name === 'Python') {
      // ADO #430 — import lines are inserted after the header comment, above the
      // body, so shift the node line map by the number of import lines added.
      const importBlock = generatePythonImports(code);
      if (importBlock) {
        const addedLines = (importBlock.match(/\n/g) || []).length;
        Object.keys(nodeLineMap).forEach(key => {
          nodeLineMap[key].startLine += addedLines;
          nodeLineMap[key].endLine += addedLines;
        });
        code = insertPythonImports(code, lang.fileHeader);
      }
    }
    return { code, nodeLineMap };
  }

  const nodes = mainFlow.nodes || [];
  // ADO #400 — argc/argv participate in type resolution (argv[i] → %s).
  const resources = withCommandLineArgs([...globalResources, ...(mainFlow.resources || [])], mainFlow);
  let indentLevel = 1;
  const getIndent = () => lang.indent.repeat(indentLevel);

  // Main function start
  addCode(mainStartFor(lang, language, mainFlow));

  // ADO #430 — remember where a Python `global …` declaration would go (right
  // after `def main():`), filled in once the body is emitted (see below).
  const _pyGlobalPos = code.length;
  const _pyGlobalLine = lineNum;

  // Generate local variable declarations inside main()
  // ADO #311 — skip inline-declared class_instance resources (the
  // owning instantiate node emits the merged form).
  const localResources = mainFlow.resources || [];
  const declarableLocals = localResources.filter((r) => !r._inlineDeclaredByNodeId);
  declarableLocals.forEach(resource => {
    const declaration = generateResourceDeclaration(resource, lang, language, [...localResources, ...globalResources]);
    if (declaration && declaration.trim()) {
      addCode(indentDeclaration(declaration, getIndent()));
    }
  });
  if (declarableLocals.length > 0) addCode('\n');

  // Process nodes with line tracking
  const processableNodes = nodes.filter(n => n.type !== 'start' && n.type !== 'end');
  const withNodeComments = options.withNodeComments === true;

  // ADO #308 — stash flow context for the subflow `this.` decision.
  _currentFlowType = flowData.flowType || (flowData.isEntry ? 'main' : null);
  _currentFlowName = flowData.name || null;
  _currentReturnType = flowData.returnType || null;
  _pyInClassMethod = false;
  _pyMethodParams = new Set();

  // ADO #211 — stop after a fully-terminating decision or return node
  for (const node of processableNodes) {
    // Optionally prepend a descriptive comment before the node's code
    let commentAdded = false;
    if (withNodeComments) {
      const comment = getNodeComment(node, resources);
      if (comment) {
        addCode(`${getIndent()}${lang.comment(comment)}\n`);
        commentAdded = true;
      }
    }

    const startLine = lineNum;
    const nodeCode = generateNodeCode(
      node, lang, getIndent,
      () => indentLevel++,
      () => indentLevel--,
      resources, language,
      nodeLineMap, startLine,
      false, withNodeComments
    );
    addCode(nodeCode);
    const endLine = lineNum - 1;

    // Record line mapping for this node
    if (node.id) {
      // If we added a comment, include it in the node's line range so highlight covers the comment too
      const effectiveStart = commentAdded ? startLine - 1 : startLine;
      nodeLineMap[node.id] = { startLine: effectiveStart, endLine: Math.max(endLine, effectiveStart) };
    }

    if (node.type === 'return') break;
    if (node.type === 'decision' && _branchTerminates([node])) break;
  }
  
  // An empty Python main body is a SyntaxError — emit `pass` before mainEnd
  // (which carries the `if __name__ == "__main__":` guard).
  if (language.toLowerCase() === 'python' && !code.slice(_pyGlobalPos).trim()) {
    addCode(lang.indent + 'pass\n');
  }

  // Main function end
  addCode(lang.mainEnd);

  // ADO #430 — Python: if main() assigns any module-level global, insert the
  // `global …` line right after `def main():` and shift the affected node line
  // numbers (the run path does this inside generateMainFunctionCode; this is the
  // line-map / Code-panel path so the displayed code matches what runs).
  if (language.toLowerCase() === 'python') {
    const decl = pythonGlobalDeclaration(code.slice(_pyGlobalPos), globalResources, lang.indent);
    if (decl) {
      code = code.slice(0, _pyGlobalPos) + decl + code.slice(_pyGlobalPos);
      const added = countLines(decl);
      lineNum += added;
      for (const k of Object.keys(nodeLineMap)) {
        if (nodeLineMap[k].startLine >= _pyGlobalLine) {
          nodeLineMap[k].startLine += added;
          nodeLineMap[k].endLine += added;
        }
      }
    }
  }

  // For C/C++: Generate function definitions after main()
  if ((lang.name === 'C' || lang.name === 'C++') && otherFlows.length > 0) {
    addCode('\n');
    otherFlows.forEach(flow => {
      const funcCode = generateFunctionCode(flow, lang, [...globalResources, ...(flow.resources || [])], language, { className: options.className, isDebugMode: options.isDebugMode, lineMap: { map: nodeLineMap, baseLineNum: lineNum - 1 } });
      addCode(funcCode + '\n');
    });
  }

  // ADO #227 / #240 — Java: emit non-entry flows as methods inside the class,
  // between main() and the class closing brace. ADO #240 adds multi-session
  // support: scope flows to the primary session, then emit nested sessions
  // (staticNested / innerClass) inside the primary, then siblings after.
  if (language === 'java') {
    const sessionList = options.sessions || [];
    const primarySession = findPrimarySession(sessionList);
    const isMultiSession = sessionList.length > 1 && primarySession;
    const fnOpts = { className: options.className, isDebugMode: options.isDebugMode };

    let javaOtherFlows = otherFlows;
    if (isMultiSession) {
      javaOtherFlows = otherFlows.filter(f =>
        !f.sessionId || f.sessionId === primarySession.id
      );
    }

    if (javaOtherFlows.length > 0) {
      javaOtherFlows.forEach(flow => {
        // '\n' is prepended below, so funcCode's first line is lineNum (not lineNum-1).
        const funcCode = generateFunctionCode(flow, lang, [...globalResources, ...(flow.resources || [])], language, { ...fnOpts, lineMap: { map: nodeLineMap, baseLineNum: lineNum } });
        addCode('\n' + funcCode);
      });
    }

    // ADO #240 — nested sessions inside primary's braces
    if (isMultiSession) {
      const nested = sessionList.filter(s => s.parentSessionId === primarySession.id);
      nested.forEach(ns => {
        addCode(emitJavaInnerSessionBlock(ns, otherFlows, lang, [...globalResources], language, fnOpts));
      });
    }

    // Close primary class
    addCode(lang.classEnd);

    // ADO #240 — sibling classes after primary
    if (isMultiSession) {
      const siblings = sessionList.filter(s => s.kind === 'siblingClass' && !s.parentSessionId);
      siblings.forEach(s => {
        addCode(emitJavaSiblingSessionBlock(s, otherFlows, lang, [...globalResources], language, fnOpts));
      });
    }
  }

  // For C/C++, scan the generated code and prepend required headers, then the
  // macro block (ADO #396 — #defines between includes and globals).
  // Shift all line numbers in nodeLineMap by the number of prefix lines added.
  if (lang.name === 'C') {
    // Non-entry bodies take their macros from _globals.h (see generateProgramCode).
    const prefix = generateCHeader(code)
      + generateMacroBlock(macrosToEmit(globalResources, options), lang, language);
    if (prefix) {
      const prefixLineCount = countLines(prefix);
      code = prefix + code;
      for (const nodeId of Object.keys(nodeLineMap)) {
        nodeLineMap[nodeId].startLine += prefixLineCount;
        nodeLineMap[nodeId].endLine += prefixLineCount;
      }
    }
  } else if (lang.name === 'C++') {
    const prefix = generateCppHeader(flows, globalResources) + generateMacroBlock(globalResources, lang, language);
    if (prefix) {
      const prefixLineCount = countLines(prefix);
      code = prefix + code;
      for (const nodeId of Object.keys(nodeLineMap)) {
        nodeLineMap[nodeId].startLine += prefixLineCount;
        nodeLineMap[nodeId].endLine += prefixLineCount;
      }
    }
  } else if (lang.name === 'Python') {
    // ADO #430 — insert stdlib imports above the body; shift the line map.
    const importBlock = generatePythonImports(code);
    if (importBlock) {
      const addedLines = countLines(importBlock);
      code = insertPythonImports(code, lang.fileHeader);
      for (const nodeId of Object.keys(nodeLineMap)) {
        nodeLineMap[nodeId].startLine += addedLines;
        nodeLineMap[nodeId].endLine += addedLines;
      }
    }
  }

  return { code, nodeLineMap };
}

/**
 * Generate code with line mapping for a single non-main function (flow).
 * Used by Code Next when stepping through a user-defined function.
 * Generates only the function definition (no #include, no main()).
 *
 * @param {Object} flow - The function flow object { name, resources, nodes }
 * @param {string} language - Target language
 * @param {Object} options - { withNodeComments: boolean }
 * @returns {{ code: string, nodeLineMap: Object }}
 */
export function generateFunctionCodeWithLineMap(flow, language = 'c', options = {}) {
  const lang = LANGUAGE_CONFIG[language.toLowerCase()] || LANGUAGE_CONFIG.c;
  const resources = flow.resources || [];
  const nodes = flow.nodes || [];

  let code = '';
  let lineNum = 1;
  const nodeLineMap = {};

  const countLines = (str) => (str.match(/\n/g) || []).length;
  const addCode = (str) => {
    code += str;
    lineNum += countLines(str);
  };

  // Generate the function definition using the existing helper
  const funcCode = generateFunctionCode(flow, lang, resources, language, { className: options.className });
  addCode(funcCode);

  // Now re-generate node code with line tracking for nodeLineMap
  // We need to find where the nodes start inside the function
  // Parse the function code to find the body start line
  const funcLines = funcCode.split('\n');
  // Find the first node's code by regenerating with line tracking
  let indentLevel = 1;
  const getIndent = () => lang.indent.repeat(indentLevel);
  const withNodeComments = options.withNodeComments === true;

  // Re-scan: count lines in function header + declarations to find body start
  const processableNodes = nodes.filter(n => n.type !== 'start' && n.type !== 'end');
  // Estimate where nodes start: after function signature + local var declarations
  const inputResources = resources.filter(r => r.availableForInput);
  const outputResource = resources.find(r => r.availableForOutput);
  const localResources = resources.filter(r => !r.availableForInput && !r.availableForOutput);
  // Lines: signature (1) + local vars + blank line
  let bodyStartLine = 1 + localResources.length + (localResources.length > 0 ? 1 : 0);

  // Map nodes to their lines by scanning the generated code for comments/statements
  // ADO #308 — stash flow context for the subflow `this.` decision.
  _currentFlowType = flow.flowType || null;
  _currentFlowName = flow.name || null;
  _currentReturnType = flow.returnType || null;
  _pyInClassMethod = false;
  _pyMethodParams = new Set(); // line-map (Code panel) path: self.attr handled by the run path

  // Simple approach: regenerate just the node code portion with tracking
  let nodeLineNum = bodyStartLine + 1; // +1 for the blank line after declarations
  // ADO #211 — stop after a fully-terminating decision or return node
  for (const node of processableNodes) {
    let commentAdded = false;
    if (withNodeComments) {
      const comment = getNodeComment(node, resources);
      if (comment) {
        nodeLineNum++; // comment line
        commentAdded = true;
      }
    }

    const startLine = nodeLineNum;
    const nodeCode = generateNodeCode(
      node, lang, getIndent,
      () => indentLevel++,
      () => indentLevel--,
      resources, language,
      nodeLineMap, startLine,
      false, withNodeComments
    );
    const nodeLines = countLines(nodeCode);
    nodeLineNum += nodeLines;
    const endLine = nodeLineNum - 1;

    if (node.id) {
      const effectiveStart = commentAdded ? startLine - 1 : startLine;
      nodeLineMap[node.id] = { startLine: effectiveStart, endLine: Math.max(endLine, effectiveStart) };
    }

    if (node.type === 'return') break;
    if (node.type === 'decision' && _branchTerminates([node])) break;
  }

  return { code, nodeLineMap };
}

// Export language options for UI
export const SUPPORTED_LANGUAGES = Object.keys(LANGUAGE_CONFIG).map(key => ({
  value: key,
  label: LANGUAGE_CONFIG[key].name,
  extension: LANGUAGE_CONFIG[key].extension
}));

// ============================================================================
// ADO #248 — Unit-Emission Contract (Phase 1 stubs)
// ----------------------------------------------------------------------------
// These are intentionally non-functional. Phase 2-5 will implement them in
// sequence, each phase asserting byte-parity against generateProgramCode
// via codeGenerator.assembler.test.js goldens.
// ============================================================================

const _ADO_248_NOT_IMPLEMENTED =
  'ADO #248: not implemented — see phases 2-5 in the Unit-Emission Contract plan';

/**
 * Derive the unit kind from a flow when the caller hasn't set one explicitly.
 * Phase 2 only implements main / function / method. Later phases add contract
 * (Phase 3), constructor/destructor/operator/static-init (Phase 3).
 *
 * @param {Object} unit
 * @returns {string}
 */
function _deriveUnitKind(unit) {
  if (unit.unitKind) return unit.unitKind;
  if (unit.flowType === 'contract') return 'contract';
  if (unit.isEntry) return 'main';
  return unit.flowType || 'function';
}

/**
 * Emit a single unit (Contract, method, function, constructor, destructor,
 * operator, static-init, compact-constructor) as a pure function of its
 * inputs. No sibling-unit access — that coupling is exactly what #248 removes.
 *
 * Phase 2 (this file) — main, function, method, instance, static, constructor,
 * compactConstructor. Dispatches to the existing helpers and wraps the
 * emitted source in a UnitFragment. Byte-identical to the corresponding
 * call into generateFunctionCode / generateMainFunctionCode.
 *
 * NOTE: Phase 2 still threads `allResources` through ctx because
 * `generateNodeCode` (the leaf emitter) consults sibling resources for type
 * inference. Phase 4 replaces that pathway with `ctx.contractSnapshot`, which
 * is the real enforcement of "no sibling-unit access". The marker property
 * is deliberately named `_allResources` to flag it as transitional.
 *
 * @param {Object}          unit
 * @param {UnitEmitContext} ctx
 * @returns {UnitFragment}
 */
export function emitUnit(unit, ctx) {
  const ctxObj = ctx || {};
  const languageKey = (ctxObj.language || 'c').toLowerCase();
  const lang = LANGUAGE_CONFIG[languageKey] || LANGUAGE_CONFIG.c;
  const allResources = ctxObj._allResources || [];
  const globalResources = ctxObj._globalResources || [];
  const options = ctxObj.options || {};
  const isDebugMode = ctxObj.isDebugMode || false;

  const kind = _deriveUnitKind(unit);

  switch (kind) {
    case 'main':
      return { source: generateMainFunctionCode(unit, lang, allResources, languageKey, isDebugMode, globalResources) };
    case 'function':
    case 'method':
    case 'instance':
    case 'static':
    case 'constructor':
    case 'compactConstructor':
      return { source: generateFunctionCode(unit, lang, allResources, languageKey, options) };
    case 'contract':
      // Phase 3 — for byte-parity with today's generateProgramCode, contract
      // flows route through generateFunctionCode just as they do today (they
      // end up in `otherFlows` and get emitted as functions with empty
      // bodies). The real Contract emitter — configuration → class/struct
      // head, resources → field decls, function_decl → prototypes — moves
      // into assembleFile in Phase 4. Keeping parity here ensures Phase 4's
      // assembler can replace this without touching emitUnit.
      return { source: generateFunctionCode(unit, lang, allResources, languageKey, options) };
    case 'destructor':
    case 'operator':
    case 'static-init':
    case 'staticInitializer':
      // Phase 3 — these unit kinds have no corresponding emitter in today's
      // generateProgramCode (no node types emit them). Return an empty
      // fragment so the dispatcher table is exhaustive. When the node types
      // ship (separate ADOs), these cases will implement real emitters.
      return { source: '' };
    default:
      throw new Error(`ADO #248: unknown unit kind '${kind}'`);
  }
}

/**
 * ADO #262 Phase 4a — Read a Contract Flow's function_decl nodes into the
 * same shape `_signatureForProto` produces, plus a resolved scopeMode.
 *
 * Returned entries are filtered to those whose `_implFlowId` resolves to a
 * non-empty impl flow in `fileFlows`, mirroring today's prototype gating
 * (only emit prototypes for functions that actually exist as bodies). This
 * keeps assembleFile's prototype set the same size as before for any
 * project where contract decls and impl flows are paired correctly.
 *
 * scopeMode resolution:
 *   - `function_decl.scopeMode` → 'this-file' | 'beyond-this-file' | 'inherit'
 *   - 'inherit' resolves to the Configuration node's `scopeMode`
 *   - Configuration default is 'this-file' (createContractFlow at
 *     useFlowStore.js:215). For C single-file emission the scopeMode is
 *     informational — assembleFile does not emit static/extern keywords
 *     in single-file context. The multi-file driver consumes scopeMode to
 *     control static keyword + .h propagation.
 *
 * @param {Object|null} contractFlow  The session's Contract Flow (flowType='contract'), or null.
 * @param {Object[]}    fileFlows     All flows in the file (used to validate _implFlowId).
 * @returns {{ funcName: string, paramInfo: Array<{name:string, dataType:string}>, returnTypeStr: string, scopeMode: string, _implFlowId: string }[]}
 */
export function _emitContractFunctionDeclsC(contractFlow, fileFlows) {
  if (!contractFlow || !Array.isArray(contractFlow.nodes)) return [];

  const configNode = contractFlow.nodes.find((n) => n.type === 'configuration');
  const configScope = (configNode && configNode.scopeMode) || 'this-file';

  const _hasContent = (f) =>
    f && Array.isArray(f.nodes) && f.nodes.some((n) => n.type !== 'start' && n.type !== 'end');
  const flowsById = {};
  (fileFlows || []).forEach((f) => { if (f && f.id) flowsById[f.id] = f; });

  const out = [];
  for (const node of contractFlow.nodes) {
    if (node.type !== 'function_decl') continue;
    const implFlow = node._implFlowId ? flowsById[node._implFlowId] : null;
    if (!implFlow) continue;
    let scopeMode = node.scopeMode || 'inherit';
    if (scopeMode === 'inherit') scopeMode = configScope;
    // A SHARED function is in its file's header from the moment it is
    // declared - that is what the student said, and a prototype with no body
    // yet is legal C (ADO #523: rectangle.h stayed empty while test() had no
    // steps). A PRIVATE one with no body is left out: `static` and never
    // defined is a warning, and the build treats warnings as errors.
    if (!_hasContent(implFlow) && scopeMode !== 'beyond-this-file') continue;

    const funcName = node.functionName || implFlow.name || 'function';
    const paramInfo = (node.parameters || []).map((p) => ({
      name: p.name,
      dataType: p.type || p.dataType || 'int',
    }));
    const returnTypeStr = node.returnType || 'void';

    out.push({ funcName, paramInfo, returnTypeStr, scopeMode, _implFlowId: node._implFlowId, isVariadic: !!node.isVariadic });
  }

  return out;
}

/**
 * Extract (name, params, returnType) from a flow for C prototype emission.
 * Mirrors the `getFunctionSignature` logic inside generateProgramCode so
 * assembleFile's forward-decl output matches byte-for-byte on the flows
 * generateProgramCode also emits prototypes for.
 *
 * @param {Object} flow
 * @returns {{ funcName: string, paramInfo: Array<{name: string, dataType: string}>, returnTypeStr: string }}
 */
function _signatureForProto(flow) {
  const funcName = flow.name || 'function1';
  const hasExplicit = Array.isArray(flow.parameters)
    && (flow.parameters.length > 0 || (flow.returnType && flow.returnType !== ''));

  if (hasExplicit) {
    const paramInfo = (flow.parameters || []).map((p) => ({
      name: p.name,
      dataType: p.type || p.dataType || 'int',
    }));
    return { funcName, paramInfo, returnTypeStr: flow.returnType || 'void', isVariadic: !!flow.isVariadic };
  }

  const flowResources = flow.resources || [];
  const inputResources = flowResources.filter((r) => r.availableForInput);
  const outputResource = flowResources.find((r) => r.availableForOutput);
  return {
    funcName,
    paramInfo: inputResources.map((r) => ({ name: r.name, dataType: r.dataType || 'int' })),
    returnTypeStr: outputResource ? (outputResource.dataType || 'int') : 'void',
  };
}

/**
 * Assemble a single file from its emitted unit fragments. Owns:
 *   - Union of headerIncludes / imports
 *   - Canonical ordering: imports → Contract header → unit definitions
 *   - Java class open/close bracing, session wrapping
 *   - Forward-decl emission (Phase 5: excludes contract flows — the
 *     fix for the single-file C duplicate-main bug).
 *
 * Phase 5 scope — C only. Supports:
 *   - Any number of flows.
 *   - Forward declarations emitted for non-contract, non-entry flows.
 *   - Contract flows are filtered out entirely: no body emission and
 *     no prototype. This is the fix for the 2026-04-21 bug where
 *     a hello-world-C project with a synthesised Contract Flow
 *     produced `void Contract();` + `int main(){}` (conflicting
 *     declarations in gcc).
 *
 * Parity: byte-identical to generateProgramCode for any C project
 * whose `flows` contain no contract entries. For projects that DO
 * contain a contract, the output intentionally diverges — missing
 * the bogus prototype the old walker emitted. Verified by Phase 5
 * tests.
 *
 * Expected input:
 *   file = {
 *     filename: string,
 *     language: 'c',
 *     flows:    [{ id, name, isEntry, flowType?, ... }],   // parallels emittedUnits
 *     globalResources?: []
 *   }
 *
 * @param {Object}         file
 * @param {UnitFragment[]} emittedUnits
 * @returns {FileSource}
 */
export function assembleFile(file, emittedUnits) {
  const language = (file && file.language ? file.language : 'c').toLowerCase();

  // Phase 5 scope guard — Java / Python / JS wrapping lands in follow-up phases.
  if (language !== 'c') {
    throw new Error(
      `ADO #248 Phase 5: assembleFile currently supports language='c' only; got '${language}'`
    );
  }

  const flows = file.flows || [];
  const units = emittedUnits || [];
  const globalResources = file.globalResources || [];
  const lang = LANGUAGE_CONFIG[language] || LANGUAGE_CONFIG.c;
  const isDebugMode = !!file.isDebugMode;

  // Partition: entry / contract / other. Contract flows are excluded from
  // both body emission AND forward-decl emission — this is the duplicate-
  // main bug fix. Any Contract-derived declarations will land via
  // function_decl-node-driven emission in a later phase.
  const entryIdx = flows.findIndex((f) => f && f.isEntry);
  const entryFragment = entryIdx >= 0 ? units[entryIdx] : null;

  // Match generateProgramCode's otherFlows filter: skip flows with no user
  // content (only start/end nodes). Mirrors the `_hasContent` predicate at
  // codeGenerator.js:3085 so parity holds for flows that today get filtered.
  const _hasContent = (f) =>
    (f.nodes || []).some((n) => n.type !== 'start' && n.type !== 'end');

  // ADO #267 follow-up: a non-entry flow sharing the entry's name (classic
  // "two flows both named main" data anomaly) would otherwise emit as another
  // `int main() {}` body and produce a gcc "conflicting types for 'main'".
  // Skip those in body, prototype, and fallback-prototype paths below.
  const entryName = entryIdx >= 0 && flows[entryIdx] ? flows[entryIdx].name : null;
  const _shadowsEntry = (f) => entryName && f.name === entryName;

  const otherFragments = [];
  const implFlowsById = {};
  flows.forEach((f, i) => {
    if (i === entryIdx) return;
    if (!f) return;
    if (f.flowType === 'contract') return; // skip contracts — the bug fix
    if (!_hasContent(f)) return;            // match generateProgramCode
    if (_shadowsEntry(f)) return;           // skip duplicate-main data anomaly
    const frag = units[i];
    if (!frag || !frag.source) return;
    otherFragments.push(frag);
    implFlowsById[f.id] = f;
  });

  // ADO #262 Phase 4a — when a Contract Flow with function_decl nodes is
  // present, drive prototypes from the contract (the architectural source
  // of truth). Falls back to per-impl-flow signatures for legacy projects
  // without a contract — preserves byte-parity for fixtures that have no
  // contract entries (hello-world C, Box.java with volume, etc.).
  const contractFlow = flows.find((f) => f && f.flowType === 'contract');
  const contractDecls = _emitContractFunctionDeclsC(contractFlow, flows);

  let prototypeEntries;
  if (contractDecls.length > 0) {
    // Contract-driven path — one prototype per contract function_decl whose
    // impl flow is present and non-empty. Order: declaration order in the
    // contract flow (stable; matches the tab order users see).
    prototypeEntries = contractDecls.filter((d) =>
      implFlowsById[d._implFlowId] && d.funcName !== entryName
    );
    // Cover impl flows not linked to a contract decl (mixed legacy + contract
    // projects). Append in the impl-flow order so output is deterministic.
    const coveredImplIds = new Set(prototypeEntries.map((d) => d._implFlowId));
    flows.forEach((f, i) => {
      if (i === entryIdx) return;
      if (!f) return;
      if (f.flowType === 'contract') return;
      if (!_hasContent(f)) return;
      if (_shadowsEntry(f)) return;
      if (coveredImplIds.has(f.id)) return;
      const sig = _signatureForProto(f);
      prototypeEntries.push({ ...sig, scopeMode: 'beyond-this-file' });
    });
  } else {
    // Legacy fallback — every non-contract, non-entry impl flow with content.
    prototypeEntries = [];
    flows.forEach((f, i) => {
      if (i === entryIdx) return;
      if (!f) return;
      if (f.flowType === 'contract') return;
      if (!_hasContent(f)) return;
      if (_shadowsEntry(f)) return;
      prototypeEntries.push({ ..._signatureForProto(f), scopeMode: 'beyond-this-file' });
    });
  }

  let source = '';

  // Debug: emit the logStep helper definition before anything that might
  // reference it. Parallels generateProgramCode:3074. Without this, the
  // Debug button path (useDebugStore → assembleProject → assembleFile)
  // compiled main.c with implicit-declaration warnings and failed linking
  // on `undefined reference to 'logStep'`.
  if (isDebugMode) {
    source += getLogStepDefinition(language);
  }

  // Global variable / struct declarations. ADO #248 Phase 4 left this out
  // because the hello-world fixture had no globals; ADO #267 restores parity
  // with generateProgramCode (codeGenerator.js:3099-3118) so single-file C
  // projects that declare variables at file scope compile. Emission matches
  // the non-Java branch there — struct_type resources produce multi-line
  // declarations closed with `;`, everything else is a single-line decl.
  // Types, then prototypes, then variables: a variable's initialiser may name a
  // function (a command table), which C needs declared first.
  let variableSection = '';
  if (globalResources.length > 0) {
    let typeSection = '';
    // Types before uses — see globalsInTypeOrder. This is the path a
    // single-file C project actually COMPILES through, so an unsorted pool
    // here is a compile error the learner sees, not a cosmetic difference.
    globalsInTypeOrder(globalResources).forEach((resource) => {
      // ADO #396 — macros are hoisted to the top (after #include); skip here.
      if (isMacroResource(resource)) return;
      const declaration = generateResourceDeclaration(resource, lang, language, globalResources);
      let text = '';
      if (declaration.includes('\n')) {
        declaration.split('\n').forEach((line) => {
          if (line.trim()) text += line + '\n';
        });
        text += ';\n';
      } else {
        text += declaration + '\n';
      }
      if (isTypeDefinition(resource)) typeSection += text; else variableSection += text;
    });
    if (typeSection) source += typeSection + '\n';
    if (variableSection) variableSection = lang.comment('Global Variables') + '\n' + variableSection + '\n';
  }

  // Forward declarations for non-contract, non-entry flows. Emits nothing
  // when the only non-entry flows are contracts (the bug-fix case).
  // scopeMode is informational here — single-file C ignores it (no static
  // keyword). The multi-file driver consumes scopeMode separately.
  if (prototypeEntries.length > 0) {
    source += '// Function Declarations\n';
    prototypeEntries.forEach(({ funcName, paramInfo, returnTypeStr, isVariadic }) => {
      const returnType = returnTypeStr && returnTypeStr !== 'void'
        ? mapToCType(returnTypeStr)
        : 'void';
      const params = paramInfo.map((p) => formatCParam(p.dataType, p.name, mapToCType)).join(', ');
      // ADO #449 — the prototype must carry `...` to match a variadic definition.
      source += `${returnType} ${funcName}(${cParamList(withVariadicEllipsis(params, isVariadic))});\n`;
    });
    source += '\n';
  }
  source += variableSection;

  // Main body first, then other function bodies (C convention today:
  // prototypes → main → definitions).
  if (entryFragment) {
    source += entryFragment.source;
    source += '\n';
  }
  otherFragments.forEach((frag) => {
    source += frag.source;
    source += '\n';
  });

  // C header prepend — scan the assembled source for printf/scanf/etc., then
  // the macro block (ADO #396 — #defines between includes and globals).
  source = generateCHeader(source) + generateMacroBlock(globalResources, lang, language) + source;

  return { filename: file.filename, source };
}

/**
 * Assemble a whole project using the emitUnit + assembleFile pipeline.
 *
 * Phase 6 scope: drop-in bridge for generateProgramCode.
 *   - Returns a STRING (not a {filename: source} map) so callers can swap
 *     `generateProgramCode(...)` → `assembleProject(...)` with a one-word
 *     diff. The ADO's originally-proposed map shape lands in the
 *     multi-file phase when TerminalPanel stops iterating files itself.
 *   - For C **without** options.skipMain: runs the new pipeline — emitUnit
 *     per flow → assembleFile. This is where the single-file C
 *     duplicate-main bug is fixed in production.
 *   - For C **with** options.skipMain OR any non-C language: delegates to
 *     generateProgramCode. Those paths get migrated in later phases when
 *     assembleFile gains skipMain + Java/Python/JS support.
 *
 * @param {Object}  programData        { projectName, flows, globalResources }
 * @param {string}  language           'c' | 'cpp' | 'java' | 'python' | 'javascript'
 * @param {Object=} options            { className, skipMain, isDebugMode, ... }
 * @returns {string}
 */
export function assembleProject(programData, language, options = {}) {
  const lang = (language || 'c').toLowerCase();

  // Bridge: anything the new assembleFile can't handle yet falls back to
  // the old walker. This makes the migration safe at every call site.
  if (lang !== 'c' || options.skipMain) {
    return generateProgramCode(programData, language, options);
  }

  const rawFlows = programData.flows || [];
  const globalResources = programData.globalResources || [];

  // ADO #580 — the same entry rule as every other C path (resolveEntryFlow).
  // This used to fall back to "the first non-contract flow with content" when
  // nothing was flagged entry, which built a helper (indexOf) as main while
  // the real main, an unflagged function, became a second `void main`. The
  // Code view (old walker) already used the named-main rule, so it looked
  // right while Run failed.
  const entryFlow = resolveEntryFlow(rawFlows, { language: 'c' });

  // Normalise flags so exactly one flow ends up the entry:
  //   - chosen entryFlow → isEntry=true, flowType='main'
  //   - any OTHER flow still flagged entry (isEntry or flowType 'main') is a
  //     plain function here. Left flagged, assembleFile's findIndex could land
  //     on it, and emitUnit derives kind 'main' from flowType — a second main.
  const flows = rawFlows.map((f) => {
    if (!f) return f;
    if (f === entryFlow) {
      return f.isEntry && f.flowType === 'main' ? f : { ...f, isEntry: true, flowType: 'main' };
    }
    if (f.isEntry || f.flowType === 'main') {
      return { ...f, isEntry: false, flowType: f.flowType === 'main' ? 'function' : f.flowType };
    }
    return f;
  });
  _allFlowsForCodegen = flows; // ADO #392 — for subflow arg ordering

  // Transitional allResources pool — parallels generateProgramCode's pre-pass
  // at lines 3100-3108. Emitters consult it for type inference until
  // Phase 7 replaces the pathway with contractSnapshot.
  const allResources = [...globalResources];
  flows.forEach((f) => {
    if (f.resources) allResources.push(...f.resources);
  });

  // Pre-populate _functionReturnTypes (module-level map consulted by
  // generateNodeCode for format-specifier inference on function-call
  // expressions). Mirrors generateProgramCode lines 3113-3124.
  _functionReturnTypes = {};
  const otherFlows = flows.filter((f) => !f.isEntry);
  for (const flow of otherFlows) {
    const fname = flow.name || 'function1';
    const hasExplicit = Array.isArray(flow.parameters)
      && (flow.parameters.length > 0 || (flow.returnType && flow.returnType !== ''));
    if (hasExplicit) {
      _functionReturnTypes[fname] = flow.returnType || 'void';
    } else {
      const outRes = (flow.resources || []).find((r) => r.availableForOutput);
      _functionReturnTypes[fname] = outRes ? (outRes.dataType || 'int') : 'void';
    }
  }

  // Emit each flow as a UnitFragment.
  const ctx = {
    language: lang,
    _allResources: allResources,
    _globalResources: globalResources,
    options,
    isDebugMode: options.isDebugMode || false,
  };
  const units = flows.map((f) => emitUnit(f, ctx));

  // Assemble the single C file.
  const file = {
    filename: (programData.projectName || 'main') + '.c',
    language: lang,
    flows,
    globalResources,
    isDebugMode: !!options.isDebugMode,
  };
  const assembled = assembleFile(file, units);

  return assembled.source;
}

export default {
  generateCode,
  generateProgramCode,
  generateCodeWithLineMap,
  SUPPORTED_LANGUAGES,
  // ADO #248 stubs — wired into default export so migrated callers in
  // phases 2-5 can swap `.generateProgramCode` for `.assembleProject` with
  // a single character diff.
  emitUnit,
  assembleFile,
  assembleProject
};
